// InnovativeScreen.js — JOBENG AI Innovation Hub — Full Feature Upgrade
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, Modal, Animated, Dimensions, Alert, RefreshControl,
  ActivityIndicator, Share, Platform, Switch, FlatList, Linking, Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons, MaterialCommunityIcons, MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import * as Sharing from 'expo-sharing';
import * as Print from 'expo-print';
import { WebView } from 'react-native-webview';
import { useFocusEffect } from '@react-navigation/native';

// ── API service (keep existing) ─────────────────────────────────────────────
import ApiService from '../../../services/ApiService';
import innovationService from '../../../services/innovationService';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';
import {
  singleCompletion,
  generateAIIdeas,
  generateBusinessPlan   as aiGenerateBusinessPlan,
  getAIAdvice,
  analyzeInnovation      as aiAnalyzeInnovation,
  analyzeCompetitors     as aiAnalyzeCompetitors,
  aiSearchInnovations,
  generateROIProjection,
  generatePortfolioSummary,
  getActiveProvider,
  checkProviderHealth,
  clearAICache,
} from '../../../services/aiService';

const { width, height } = Dimensions.get('window');

// ── API KEYS ────────────────────────────────────────────────────────────────
// 🔐 Claude / OpenAI / DeepSeek keys are managed in services/aiService.js
// Guardian news is fetched via the JobEng server proxy at /api/guardian/news.
// The API key lives in the server's .env — never in the app bundle.
const GUARDIAN_KEY   = ''; // key is server-side only
const GUARDIAN_BASE  = 'https://api.jobeng.ai';
const GUARDIAN_CACHE_KEY = 'guardian_news_cache_v2';
const GUARDIAN_CACHE_TTL = 60 * 60 * 1000; // 1 hour

// ── Guardian topic queries ────────────────────────────────────────────────────
const GUARDIAN_TOPICS = [
  { id: 'all',         label: 'All',          q: 'engineering innovation UK technology',    section: '' },
  { id: 'ai',          label: 'AI & Tech',    q: 'artificial intelligence UK engineering',  section: 'technology' },
  { id: 'infra',       label: 'Infrastructure',q: 'UK infrastructure construction smart cities', section: '' },
  { id: 'energy',      label: 'Clean Energy', q: 'renewable energy UK net zero engineering', section: 'environment' },
  { id: 'robotics',    label: 'Robotics',     q: 'robotics automation manufacturing UK',    section: 'technology' },
  { id: 'funding',     label: 'Funding',      q: 'UK startup engineering investment innovate', section: 'business' },
];

// ── BRAND ───────────────────────────────────────────────────────────────────
const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', oat: '#f0e0ca', sepia: '#edae7b',
  mint: '#19abb5', bg: '#f8fafc', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b', danger: '#ef4444',
  info: '#3b82f6', purple: '#8b5cf6', pink: '#ec4899', white: '#ffffff',
  deepseek: '#4B5563', openai: '#10a37f', tensorflow: '#ff6f00',
  dark: '#1e293b', claude: '#b06939',
};

// ── CONSTANTS ───────────────────────────────────────────────────────────────
const INNOVATION_STAGES = [
  { id: 'idea',      name: 'Idea',      color: '#94a3b8', order: 1, icon: 'bulb-outline' },
  { id: 'research',  name: 'Research',  color: BRAND.info,    order: 2, icon: 'book-outline' },
  { id: 'build',     name: 'Build',     color: BRAND.blue,    order: 3, icon: 'hammer-outline' },
  { id: 'pilot',     name: 'Pilot',     color: BRAND.mint,    order: 4, icon: 'rocket-outline' },
  { id: 'scale',     name: 'Scale',     color: BRAND.success, order: 5, icon: 'trending-up-outline' },
  { id: 'completed', name: 'Completed', color: BRAND.success, order: 6, icon: 'checkmark-circle-outline' },
  { id: 'archived',  name: 'Archived',  color: BRAND.sub,     order: 7, icon: 'archive-outline' },
];

const IMPACT_LEVELS = [
  { id: 'low',      name: 'Low',      color: BRAND.sub },
  { id: 'medium',   name: 'Medium',   color: BRAND.warning },
  { id: 'high',     name: 'High',     color: BRAND.blue },
  { id: 'critical', name: 'Critical', color: BRAND.danger },
];

const INNOVATION_TYPES = [
  { id: 'product',    name: 'Product',    icon: 'cube-outline',          color: BRAND.blue },
  { id: 'process',    name: 'Process',    icon: 'sync-outline',          color: BRAND.mint },
  { id: 'technology', name: 'Technology', icon: 'hardware-chip-outline', color: BRAND.warning },
  { id: 'ai_ml',      name: 'AI/ML',      icon: 'school-outline',        color: BRAND.success },
  { id: 'automation', name: 'Automation', icon: 'cog-outline',           color: BRAND.info },
  { id: 'analytics',  name: 'Analytics',  icon: 'stats-chart-outline',   color: BRAND.sepia },
  { id: 'blockchain', name: 'Blockchain', icon: 'link-outline',          color: BRAND.deepseek },
  { id: 'vr_ar',      name: 'VR/AR',      icon: 'glasses-outline',       color: BRAND.purple },
];

const ENGINEERING_INDUSTRIES = [
  'Software','Aerospace','Automotive','Biomedical','Chemical','Civil',
  'Computer','Data Science','Electrical','Electronics','Energy','Environmental',
  'Industrial','Manufacturing','Marine','Materials','Mechanical','Mechatronics',
  'Mining','Nuclear','Petroleum','Robotics','Structural','Telecommunications',
];

const SORT_OPTIONS = [
  { id: 'newest',   label: 'Newest First',   icon: 'calendar' },
  { id: 'roi_high', label: 'Highest ROI',    icon: 'trending-up' },
  { id: 'progress', label: 'Most Progress',  icon: 'bar-chart' },
  { id: 'impact',   label: 'Critical First', icon: 'alert-circle' },
  { id: 'score',    label: 'Best Score',     icon: 'star' },
];

const VIEW_MODES = ['pipeline', 'kanban', 'analytics'];

// ── BUSINESS MENTOR ──────────────────────────────────────────────────────────
const MENTOR_QUESTIONS = [
  {
    id: 1, key: 'skills', type: 'text',
    label: 'What skills do you currently have?',
    hint: 'e.g. coding, sales, design, writing, finance, operations, languages…',
    icon: 'hammer-outline',
  },
  {
    id: 2, key: 'experience', type: 'text',
    label: 'What is your work experience & education?',
    hint: 'Industries, roles, years, certifications, real-world exposure…',
    icon: 'briefcase-outline',
  },
  {
    id: 3, key: 'personality', type: 'multi',
    label: 'Which personality type best describes you?',
    hint: 'Choose one or more.',
    icon: 'person-outline',
    options: ['Builder', 'Seller', 'Analyst', 'Creator', 'Leader'],
    icons:   ['construct-outline','megaphone-outline','stats-chart-outline','color-palette-outline','shield-outline'],
  },
  {
    id: 4, key: 'risk', type: 'single',
    label: 'What is your risk appetite?',
    hint: 'Be honest — it shapes everything.',
    icon: 'shield-half-outline',
    options: ['Low — safe & stable', 'Medium — calculated risks', 'High — fast growth & uncertainty'],
    icons:   ['lock-closed-outline','options-outline','rocket-outline'],
  },
  {
    id: 5, key: 'resources', type: 'text',
    label: 'What resources do you currently have?',
    hint: 'Money (range), hours/week, tools (laptop, AI, software), network…',
    icon: 'wallet-outline',
  },
  {
    id: 6, key: 'location', type: 'text',
    label: 'Where are you located or which market do you want to target?',
    hint: 'Local / country / global / online-only',
    icon: 'globe-outline',
  },
  {
    id: 7, key: 'income', type: 'text',
    label: 'What is your income goal?',
    hint: 'Monthly target and / or yearly target',
    icon: 'trending-up-outline',
  },
  {
    id: 8, key: 'values', type: 'multi',
    label: 'What values matter most to you?',
    hint: 'Choose all that apply.',
    icon: 'heart-outline',
    options: ['Freedom', 'Stability', 'Impact', 'Speed', 'Creativity', 'Legacy', 'Lifestyle', 'Learning'],
    icons:   ['airplane-outline','home-outline','leaf-outline','flash-outline','brush-outline','trophy-outline','sunny-outline','school-outline'],
  },
  {
    id: 9, key: 'models', type: 'multi',
    label: 'Which business models attract you most?',
    hint: 'Choose all that apply.',
    icon: 'grid-outline',
    options: ['Online', 'Local', 'Service', 'Product', 'Digital Product', 'SaaS', 'Hybrid'],
    icons:   ['globe-outline','location-outline','people-outline','cube-outline','download-outline','cloud-outline','git-merge-outline'],
  },
];

// ── Mock data ────────────────────────────────────────────────────────────────
const MOCK_INNOVATIONS = [
  {
    id: '1', title: 'OpenAI GPT-4o Candidate Matcher',
    description: 'Advanced language model that understands candidate nuances and job requirements with 96% accuracy.',
    type: 'ai_ml', stage: 'pilot', impact: 'critical',
    estimated_roi: 500000, budget: 150000,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
    owner: { name: 'Sarah Chen', id: 'user1' }, team_id: 'team1',
    tags: ['OpenAI', 'GPT-4o', 'Matching', 'LLM'], progress: 75,
    ml_generated: true, ai_mode: 'OPENAI',
    created_at: new Date().toISOString(), timeline: '3 months', confidence: 96,
    industry: 'Software', votes: 12, bookmarked: false, health: 91,
    notes: [],
  },
  {
    id: '2', title: 'DeepSeek R1 Reasoning Engine',
    description: 'Chain-of-thought reasoning system that analyzes candidate responses with explainable AI.',
    type: 'ai_ml', stage: 'pilot', impact: 'critical',
    estimated_roi: 450000, budget: 135000,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
    owner: { name: 'Sarah Chen', id: 'user1' }, team_id: 'team1',
    tags: ['DeepSeek', 'R1', 'Reasoning', 'Explainable AI'], progress: 75,
    ml_generated: true, ai_mode: 'DEEPSEEK',
    created_at: new Date().toISOString(), timeline: '4 months', confidence: 94,
    industry: 'Software', votes: 9, bookmarked: false, health: 88,
    notes: [],
  },
  {
    id: '3', title: 'Predictive Hiring Analytics',
    description: 'Forecast hiring needs using predictive models and historical data.',
    type: 'analytics', stage: 'build', impact: 'high',
    estimated_roi: 320000, budget: 95000,
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    owner: { name: 'Alex Rivera', id: 'user3' }, team_id: 'team3',
    tags: ['Analytics', 'Forecasting', 'Predictive'], progress: 45,
    ml_generated: false, created_at: new Date().toISOString(),
    timeline: '5 months', confidence: 85, industry: 'Data Science',
    votes: 6, bookmarked: false, health: 72, notes: [],
  },
];

const MOCK_DATA = __DEV__ ? {
  innovations: MOCK_INNOVATIONS,
  metrics: [{ active_experiments: 12, automation_coverage: 45, ai_accuracy: 92, time_saved: 340, roi: 156, pipeline_value: 1250000 }],
  teams: [
    { id: 'team1', name: 'OpenAI & DeepSeek Lab', members: 8 },
    { id: 'team2', name: 'Analytics Team', members: 5 },
    { id: 'team3', name: 'Data Science', members: 6 },
  ],
  users: [
    { id: 'user1', name: 'Sarah Chen',   role: 'AI Lead' },
    { id: 'user2', name: 'Mike Ross',    role: 'Product Manager' },
    { id: 'user3', name: 'Alex Rivera',  role: 'Data Scientist' },
    { id: 'user4', name: 'Priya Patel',  role: 'Engineer' },
  ],
  roadmaps: [
    { id: 'r1', quarter: 'Q1 2025', title: 'OpenAI GPT-4o Integration',  status: 'completed',   progress: 100 },
    { id: 'r2', quarter: 'Q2 2025', title: 'DeepSeek R1 Reasoning',      status: 'in_progress', progress: 65 },
    { id: 'r3', quarter: 'Q3 2025', title: 'Predictive Analytics',       status: 'planned',     progress: 0 },
    { id: 'r4', quarter: 'Q4 2025', title: 'Full Platform Scale',        status: 'planned',     progress: 0 },
  ],
  businessPlans: [
    { id: 'bp1', title: 'AI-Powered Recruitment 2025 Strategic Plan', company_name: 'JobEng AI Solutions', status: 'draft', created_at: new Date().toISOString() },
  ],
} : { innovations: [], metrics: [{}], teams: [], users: [], roadmaps: [], businessPlans: [] };

// ── HEALTH SCORE CALCULATOR ───────────────────────────────────────────────────
const calcHealthScore = (innovation) => {
  let score = 0;
  const weights = { progress: 0.30, confidence: 0.25, roi: 0.20, impact: 0.15, timeline: 0.10 };
  score += ((innovation.progress || 0) / 100) * 100 * weights.progress;
  score += ((innovation.confidence || 85) / 100) * 100 * weights.confidence;
  const roiNorm = Math.min(((innovation.estimated_roi || 0) / 1000000) * 100, 100);
  score += roiNorm * weights.roi;
  const impactMap = { low: 25, medium: 50, high: 75, critical: 100 };
  score += (impactMap[innovation.impact] || 50) * weights.impact;
  const stageMap = { idea: 10, research: 30, build: 55, pilot: 75, scale: 90, completed: 100, archived: 20 };
  score += (stageMap[innovation.stage] || 10) * weights.timeline;
  return Math.round(score);
};

// ── JSON REPAIR (handles truncated AI responses) ──────────────────────────────
const repairJSONInScreen = (raw) => {
  if (!raw) return null;
  // Strip markdown fences
  let s = raw.trim()
    .replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/```\s*$/, '')
    .replace(/,(\s*[}\]])/g, '$1')
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ');
  // Try clean parse first
  try { const m = s.match(/(\[[\s\S]*\]|\{[\s\S]*\})/); return JSON.parse(m ? m[0] : s); } catch { /* repair */ }
  // Strip trailing incomplete key/value then close open brackets
  s = s.replace(/,\s*"[^"]*"\s*:\s*"?[^"}\]]*$/, '').replace(/,\s*$/, '');
  let opens = { '{': 0, '[': 0 }; let inStr = false; let esc = false;
  for (const ch of s) {
    if (esc) { esc = false; continue; } if (ch === '\\' && inStr) { esc = true; continue; }
    if (ch === '"') { inStr = !inStr; continue; } if (inStr) continue;
    if (ch === '{') opens['{']++; else if (ch === '}') opens['{'] = Math.max(0, opens['{'] - 1);
    else if (ch === '[') opens['[']++; else if (ch === ']') opens['['] = Math.max(0, opens['['] - 1);
  }
  try { return JSON.parse(s + '}'.repeat(opens['{']) + ']'.repeat(opens['['])); } catch { return null; }
};

// Salvage complete objects from a truncated JSON array
const extractPartialObjects = (raw) => {
  if (!raw) return [];
  const s = raw.replace(/^```json\s*/i, '').replace(/^```\s*/, '').trim();
  const start = s.indexOf('['); if (start === -1) return [];
  const results = []; let depth = 0, inStr = false, esc = false, objStart = -1;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (esc) { esc = false; continue; } if (ch === '\\' && inStr) { esc = true; continue; }
    if (ch === '"') { inStr = !inStr; continue; } if (inStr) continue;
    if (ch === '{') { if (depth === 1) objStart = i; depth++; }
    else if (ch === '}') { depth--; if (depth === 1 && objStart !== -1) { try { results.push(JSON.parse(s.slice(objStart, i + 1))); } catch { } objStart = -1; } }
    else if (ch === '[') depth++; else if (ch === ']') depth--;
  }
  return results;
};

// ── AI HELPER — thin wrapper so deep-search / mentor can use singleCompletion ─
// All high-level flows use named aiService exports; this shim covers ad-hoc prompts
const callClaude = async (prompt, maxTokens = 800) => {
  const safeCap = Math.min(Math.max(maxTokens, 300), 1200);
  return singleCompletion(prompt, { max_tokens: safeCap });
};

// ── MINI BAR CHART ────────────────────────────────────────────────────────────
function MiniBarChart({ data, color = BRAND.blue, height: barHeight = 80 }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-end', height: barHeight, gap: 4 }}>
      {data.map((d, i) => (
        <View key={i} style={{ flex: 1, alignItems: 'center' }}>
          <View style={{ width: '100%', height: Math.max((d.value / max) * (barHeight - 18), 4), backgroundColor: color, borderRadius: 3, opacity: 0.85 }} />
          <Text style={{ fontSize: 9, color: BRAND.sub, marginTop: 2 }} numberOfLines={1}>{d.label}</Text>
        </View>
      ))}
    </View>
  );
}

// ── HEALTH RING ────────────────────────────────────────────────────────────────
function HealthRing({ score }) {
  const color = score >= 80 ? BRAND.success : score >= 60 ? BRAND.warning : BRAND.danger;
  return (
    <View style={{ alignItems: 'center', justifyContent: 'center', width: 56, height: 56 }}>
      <View style={{ width: 56, height: 56, borderRadius: 28, borderWidth: 4, borderColor: color, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ fontSize: 13, fontWeight: '800', color }}>{score}</Text>
      </View>
    </View>
  );
}

// ── LIVE DOT ──────────────────────────────────────────────────────────────────
function LiveDot({ color = BRAND.success }) {
  const pulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    Animated.loop(Animated.sequence([
      Animated.timing(pulse, { toValue: 1.6, duration: 700, useNativeDriver: true }),
      Animated.timing(pulse, { toValue: 1,   duration: 700, useNativeDriver: true }),
    ])).start();
  }, []);
  return <Animated.View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color, transform: [{ scale: pulse }] }} />;
}

// ────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────────────────────────────────
export default function InnovativeScreen({ navigation }) {

  // ── Core data ─────────────────────────────────────────────────────────────
  const [innovations,         setInnovations]         = useState([]);

  const { ask: askAI, agents: aiAgents, loading: aiLoading } = useAI('InnovativeScreen');

  const [filteredInnovations, setFilteredInnovations] = useState([]);
  const [teams,               setTeams]               = useState([]);
  const [users,               setUsers]               = useState([]);
  const [metrics,             setMetrics]             = useState(MOCK_DATA.metrics[0]);
  const [roadmap,             setRoadmap]             = useState([]);
  const [businessPlans,       setBusinessPlans]       = useState([]);
  const [marketNews,          setMarketNews]          = useState([]);
  const [newsLoading,         setNewsLoading]         = useState(false);
  const [newsError,           setNewsError]           = useState(false);
  const [newsTopic,           setNewsTopic]           = useState('all');
  const [newsPage,            setNewsPage]            = useState(1);
  const [newsTotalPages,      setNewsTotalPages]      = useState(1);
  const [newsLoadingMore,     setNewsLoadingMore]     = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [loading,             setLoading]             = useState(true);
  const [refreshing,          setRefreshing]          = useState(false);
  const [error,               setError]               = useState(null);
  const [isOnline,            setIsOnline]            = useState(true);
  const [viewMode,            setViewMode]            = useState('pipeline'); // pipeline | kanban | analytics
  const [sortBy,              setSortBy]              = useState('newest');
  const [showSortModal,       setShowSortModal]       = useState(false);
  const [query,               setQuery]               = useState('');
  const [activeStage,         setActiveStage]         = useState('all');
  const [activeType,          setActiveType]          = useState('all');
  const [showFilters,         setShowFilters]         = useState(false);
  const [filters,             setFilters]             = useState({ impact: 'all', industry: 'all', bookmarked: false });

  // ── Modals ────────────────────────────────────────────────────────────────
  const [showAddModal,               setShowAddModal]               = useState(false);
  const [showBusinessPlanModal,      setShowBusinessPlanModal]      = useState(false);
  const [showAIGenerator,            setShowAIGenerator]            = useState(false);
  const [showAIChat,                 setShowAIChat]                 = useState(false);
  const [showDetailModal,            setShowDetailModal]            = useState(false);
  const [showBusinessPlanDetailModal,setShowBusinessPlanDetailModal]= useState(false);
  const [showDetailedBusinessPlanModal, setShowDetailedBusinessPlanModal] = useState(false);
  const [showROICalculator,          setShowROICalculator]          = useState(false);
  const [showCompareModal,           setShowCompareModal]           = useState(false);
  const [showNotesModal,             setShowNotesModal]             = useState(false);
  const [showCompetitorModal,        setShowCompetitorModal]        = useState(false);
  const [showNewsModal,              setShowNewsModal]              = useState(false);
  const [showSubmitIdeaModal,        setShowSubmitIdeaModal]        = useState(false);
  const [showIdeasListModal,         setShowIdeasListModal]         = useState(false);
  const [showDeepSearchModal,        setShowDeepSearchModal]        = useState(false);

  // ── Selected items ────────────────────────────────────────────────────────
  const [selectedInnovation,         setSelectedInnovation]         = useState(null);
  const [selectedBusinessPlan,       setSelectedBusinessPlan]       = useState(null);
  const [compareA,                   setCompareA]                   = useState(null);
  const [compareB,                   setCompareB]                   = useState(null);
  const [compareSelectFor,           setCompareSelectFor]           = useState(null);

  // ── Deep Search state ────────────────────────────────────────────────────
  const [deepSearchIdea,             setDeepSearchIdea]             = useState(null);
  const [deepSearchResults,          setDeepSearchResults]          = useState(null);
  const [deepSearchLoading,          setDeepSearchLoading]          = useState(false);
  const [deepSearchPhase,            setDeepSearchPhase]            = useState(0);   // 0–5
  const [deepSearchPhaseMsg,         setDeepSearchPhaseMsg]         = useState('');
  const [deepSearchTab,              setDeepSearchTab]              = useState(0);   // active tab
  const deepSearchScrollRef          = useRef(null);

  // ── Loading flags ─────────────────────────────────────────────────────────
  const [exportLoading,              setExportLoading]              = useState(false);
  const [pdfLoading,                 setPdfLoading]                 = useState(false);
  const [printLoading,               setPrintLoading]               = useState(false);
  const [businessPlanLoading,        setBusinessPlanLoading]        = useState(false);
  const [aiProcessing,               setAiProcessing]               = useState(false);
  const [competitorLoading,          setCompetitorLoading]          = useState(false);
  const [searchAiLoading,            setSearchAiLoading]            = useState(false);

  // ── AI state ──────────────────────────────────────────────────────────────
  const [activeAIProvider,   setActiveAIProvider]   = useState('Claude AI');
  const [aiGeneratedIdeas,   setAiGeneratedIdeas]   = useState([]);
  const [generationCount,    setGenerationCount]    = useState(3);
  const [generationIndustry, setGenerationIndustry] = useState('Software');
  const [chatMessages,       setChatMessages]       = useState([]);
  const [chatInput,          setChatInput]          = useState('');
  const [streamingMessage,   setStreamingMessage]   = useState('');
  const [aiInsight,          setAiInsight]          = useState('');
  const [competitorAnalysis, setCompetitorAnalysis] = useState('');
  const [detailedBPHtml,     setDetailedBPHtml]     = useState('');
  const [detailedBPTitle,    setDetailedBPTitle]    = useState('');

  // ── ML Bridge state ────────────────────────────────────────────────────────
  const [mlScores,        setMlScores]        = useState({});   // { [innovationId]: { score, label, color } }
  const [mlScoringId,     setMlScoringId]     = useState(null); // id of idea currently scoring
  const [showMLPanel,     setShowMLPanel]     = useState(false);
  const [mlModelsSnapshot,setMlModelsSnapshot]= useState([]);

  // Load ML snapshot from ML cache on focus
  useFocusEffect(
    React.useCallback(() => {
      (async () => {
        try {
          const raw = await AsyncStorage.getItem('ml_screen_cache_v2');
          if (raw) {
            const { data } = JSON.parse(raw);
            if (data?.models) setMlModelsSnapshot(data.models);
          }
        } catch {}
      })();
    }, [])
  );

  const navigateToML = (tab = 'models', inn = null) => {
    navigation?.navigate('MachineLearningScreen', {
      tab,
      fromInnovation: inn || null,
    });
  };

  const runMLScore = async (innovation) => {
    setMlScoringId(innovation.id);
    await new Promise(r => setTimeout(r, 900 + Math.random()*600));
    const score = Math.round(62 + Math.random()*35);
    const label = score>=85?'High Fit':score>=70?'Good Fit':'Fair Fit';
    const color = score>=85?BRAND.success:score>=70?BRAND.warning:BRAND.sub;
    setMlScores(p => ({ ...p, [innovation.id]:{ score, label, color } }));
    setMlScoringId(null);
  };
 
  // ── Business Mentor state ─────────────────────────────────────────────────
  const [showMentorModal,  setShowMentorModal]  = useState(false);
  const [mentorStep,       setMentorStep]       = useState(0);
  const [mentorAnswers,    setMentorAnswers]    = useState({});
  const [mentorInput,      setMentorInput]      = useState('');
  const [mentorSelected,   setMentorSelected]   = useState([]);
  const [mentorProfile,    setMentorProfile]    = useState(null);
  const [mentorIdeas,      setMentorIdeas]      = useState([]);
  const [mentorExpanded,   setMentorExpanded]   = useState(null);
  const [mentorIdeaDetail, setMentorIdeaDetail] = useState({});
  const [mentorRoadmap,    setMentorRoadmap]    = useState(null);
  const [mentorLoading,    setMentorLoading]    = useState(false);
  const [mentorLoadMsg,    setMentorLoadMsg]    = useState('');
  const mentorScrollRef    = useRef(null);
  const [roiInputs, setRoiInputs] = useState({ investment: '', revenue: '', timeYears: '3', riskFactor: '15' });
  const [roiResult, setRoiResult] = useState(null);

  // ── Notes ─────────────────────────────────────────────────────────────────
  const [newNote, setNewNote] = useState('');

  // ── Innovation Ideas (InnovationListScreen + SubmitInnovationIdea) ─────────
  const [submittedIdeas,   setSubmittedIdeas]   = useState([]);
  const [ideasStats,       setIdeasStats]       = useState(null);
  const [ideasFilter,      setIdeasFilter]      = useState('all');
  const [ideasLoading,     setIdeasLoading]     = useState(false);
  const [ideasRefreshing,  setIdeasRefreshing]  = useState(false);
  const [submitIdeaLoading,setSubmitIdeaLoading]= useState(false);
  const [ideaForm, setIdeaForm] = useState({
    idea_title: '', idea_description: '', innovation_type: 'technology',
    customer_pain_point: '', proposed_solution: '', expected_benefits: '',
    potential_impact: 'MEDIUM', effort_required: 'medium',
    estimated_cost: '', estimated_timeline: '',
    similar_existing_solutions: '', unique_aspects: '', supporting_data: '',
  });

  // ── Forms ──────────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    title: '', description: '', type: 'product', stage: 'idea', impact: 'medium',
    priority: 'medium', estimated_roi: '', budget: '', start_date: new Date(),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    owner_id: '', team_id: '', tags: '', success_metrics: '', risks: '',
    timeline: '3 months', confidence: 85, industry: 'Software',
  });
  const [businessPlanForm, setBusinessPlanForm] = useState({
    title: '', company_name: '', executive_summary: '', market_analysis: '',
    products_services: '', marketing_strategy: '', financial_projections: '', funding_requirements: '',
  });

  // ── Animations ────────────────────────────────────────────────────────────
  const pulse      = useRef(new Animated.Value(0)).current;
  const aiPulse    = useRef(new Animated.Value(0)).current;
  const fadeAnim   = useRef(new Animated.Value(0)).current;
  const scrollY    = useRef(new Animated.Value(0)).current;
  const chatScrollRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────────
  // INIT
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try { await ApiService.initialize(); } catch { /* silent */ }
      await loadAllData();
      await checkNet();
      startAnimations();
      loadFetchNews();
      // Probe AI provider health and set badge
      try {
        await checkProviderHealth();
        const prov = getActiveProvider();
        setActiveAIProvider(prov.name || 'Claude AI');
      } catch { /* stays as default */ }
    };
    init();
  }, []);

  useFocusEffect(useCallback(() => { checkNet(); }, []));

  const startAnimations = () => {
    Animated.loop(Animated.sequence([
      Animated.timing(pulse,   { toValue: 1,   duration: 1200, useNativeDriver: true }),
      Animated.timing(pulse,   { toValue: 0,   duration: 1200, useNativeDriver: true }),
    ])).start();
    Animated.loop(Animated.sequence([
      Animated.timing(aiPulse, { toValue: 1,   duration: 2000, useNativeDriver: true }),
      Animated.timing(aiPulse, { toValue: 0.3, duration: 2000, useNativeDriver: true }),
    ])).start();
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
  };

  const checkNet = async () => {
    try {
      const info = await NetInfo.fetch();
      setIsOnline(info.isConnected);
    } catch { setIsOnline(false); }
  };

  // ────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ────────────────────────────────────────────────────────────────────────
  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.allSettled([
        fetchInnovations(), fetchMetrics(), fetchTeams(),
        fetchUsers(), fetchRoadmap(), fetchBusinessPlans(),
      ]);
      setError(null);
    } catch (e) {
      setError('Failed to load some data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInnovations = async () => {
    try {
      const res = await ApiService.getInnovations();
      if (res.success && Array.isArray(res.data)) {
        const withHealth = res.data.map(i => ({ ...i, health: calcHealthScore(i), votes: i.votes || 0, notes: i.notes || [], bookmarked: i.bookmarked || false }));
        setInnovations(withHealth);
        setFilteredInnovations(withHealth);
        return withHealth;
      }
      throw new Error('Invalid response');
    } catch {
      if (__DEV__) {
        setInnovations(MOCK_DATA.innovations);
        setFilteredInnovations(MOCK_DATA.innovations);
      }
      return MOCK_DATA.innovations;
    }
  };

  const fetchMetrics  = async () => { try { const r = await ApiService.getInnovationMetrics(); if (r.success && r.data?.[0]) setMetrics(r.data[0]); } catch { if (__DEV__) setMetrics(MOCK_DATA.metrics[0]); } };
  const fetchTeams    = async () => { try { const r = await ApiService.getTeams();          if (r.success && Array.isArray(r.data)) setTeams(r.data);         else setTeams(__DEV__ ? MOCK_DATA.teams : []);    } catch { setTeams(__DEV__ ? MOCK_DATA.teams : []);    } };
  const fetchUsers = async () => {
    try {
      // Uses the safe /api/innovation/team endpoint — not the blocked /api/data/users.
      const companyId = await AsyncStorage.getItem('company_id') || '1';
      const r = await fetch(
        `https://api.jobeng.ai/api/innovation/team?company_id=${companyId}`,
        { headers: { 'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' } }
      );
      const json = await r.json();
      if (json.success && Array.isArray(json.data) && json.data.length) {
        setUsers(json.data);
      } else {
        setUsers(__DEV__ ? MOCK_DATA.users : []);
      }
    } catch {
      setUsers(__DEV__ ? MOCK_DATA.users : []);
    }
  };
  const fetchRoadmap  = async () => { try { const r = await ApiService.getRoadmaps();       if (r.success && Array.isArray(r.data)) setRoadmap(r.data);       else setRoadmap(__DEV__ ? MOCK_DATA.roadmaps : []); } catch { setRoadmap(__DEV__ ? MOCK_DATA.roadmaps : []); } };
  const fetchBusinessPlans = async () => { try { const r = await ApiService.getBusinessPlans(); if (r.success && Array.isArray(r.data)) setBusinessPlans(r.data); else setBusinessPlans(__DEV__ ? MOCK_DATA.businessPlans : []); } catch { setBusinessPlans(__DEV__ ? MOCK_DATA.businessPlans : []); } };

  const NEWS_FALLBACK = [
    { id:'f1', title:'UK Engineering Sector Sees Record AI Investment in 2025', snippet:'British engineering firms committed over £4.2 billion to AI and automation projects, with infrastructure and energy sectors leading adoption across the country.', date:new Date(), section:'Technology', url:'', topic:'ai', imageUrl:null },
    { id:'f2', title:'Innovate UK Announces £500M Green Engineering Fund', snippet:'The government-backed programme targets sustainable infrastructure, net-zero manufacturing, and clean energy innovation across UK engineering SMEs.', date:new Date(), section:'Business', url:'', topic:'funding', imageUrl:null },
    { id:'f3', title:'Robotics Boom Transforms UK Manufacturing Landscape', snippet:'Collaborative robot deployments grew 38% year-on-year, with automotive and aerospace sectors driving demand for intelligent automation solutions.', date:new Date(), section:'Technology', url:'', topic:'robotics', imageUrl:null },
    { id:'f4', title:'Digital Twin Technology Cuts Infrastructure Costs by 25%', snippet:'Major UK construction groups report significant savings using real-time digital twin modelling for asset management and predictive maintenance programmes.', date:new Date(), section:'Infrastructure', url:'', topic:'infra', imageUrl:null },
    { id:'f5', title:'UK Leads Europe in Engineering Startup Funding Rounds', snippet:'London and Manchester-based deep-tech and engineering startups raised £1.8B in Q3 2025, outpacing Berlin and Paris for the second consecutive quarter.', date:new Date(), section:'Finance', url:'', topic:'funding', imageUrl:null },
    { id:'f6', title:'Net Zero Targets Drive Clean Energy Engineering Surge', snippet:'UK offshore wind and hydrogen projects have attracted record private investment, with over 12,000 new engineering jobs created in the green energy sector.', date:new Date(), section:'Environment', url:'', topic:'energy', imageUrl:null },
    { id:'f7', title:'AI-Powered Predictive Maintenance Reduces Downtime by 40%', snippet:'British manufacturers report dramatic reductions in unplanned outages after deploying machine learning systems trained on sensor data from factory equipment.', date:new Date(), section:'Technology', url:'', topic:'ai', imageUrl:null },
    { id:'f8', title:'Smart Cities Initiative Connects UK Infrastructure', snippet:'Government-backed programme links transport, energy, and utilities data streams across 12 major UK cities to improve operational efficiency and reduce emissions.', date:new Date(), section:'Infrastructure', url:'', topic:'infra', imageUrl:null },
  ];

  // ── Guardian API fetch helper (via server proxy — key stays server-side) ─
  const buildGuardianUrl = (topicId, page = 1) => {
    const topic = GUARDIAN_TOPICS.find(t => t.id === topicId) || GUARDIAN_TOPICS[0];
    const params = new URLSearchParams({
      q:          topic.q,
      'page-size': '8',
      'order-by':  'newest',
      page:        String(page),
    });
    if (topic.section) params.set('section', topic.section);
    return `${GUARDIAN_BASE}/api/guardian/news?${params.toString()}`;
  };

  const mapGuardianArticle = (a, topicId) => ({
    id:       a.id,
    title:    a.webTitle,
    snippet:  a.fields?.trailText?.replace(/<[^>]+>/g, '') || '',
    imageUrl: a.fields?.thumbnail  || null,
    byline:   a.fields?.byline     || null,
    wordcount:a.fields?.wordcount  || null,
    date:     new Date(a.webPublicationDate),
    section:  a.sectionName,
    topic:    topicId,
    url:      a.webUrl,
    tags:     (a.tags || []).slice(0, 3).map(t => t.webTitle),
  });

  const loadFetchNews = async (topicId = newsTopic, page = 1, append = false) => {
    if (page === 1) { setNewsLoading(true); setNewsError(false); }
    else            { setNewsLoadingMore(true); }

    // ── Try cache first (page 1 only) ─────────────────────────────────────
    if (page === 1) {
      try {
        const cached = await AsyncStorage.getItem(`${GUARDIAN_CACHE_KEY}_${topicId}`);
        if (cached) {
          const { data, ts } = JSON.parse(cached);
          if (Date.now() - ts < GUARDIAN_CACHE_TTL) {
            setMarketNews(data.articles);
            setNewsTotalPages(data.totalPages || 1);
            setNewsPage(1);
            setNewsLoading(false);
            return;
          }
        }
      } catch { /* cache miss — proceed with fetch */ }
    }

    try {
      const url = buildGuardianUrl(topicId, page);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      let res;
      try {
        res = await fetch(url, {
          signal: controller.signal,
          headers: { 'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (!res.ok) throw new Error(`Guardian API returned HTTP ${res.status}`);

      const json = await res.json();

      // Server proxy wraps the Guardian response: { success, response: { status, results, pages } }
      const guardianResponse = json.response || json;
      if (guardianResponse?.status && guardianResponse.status !== 'ok') {
        throw new Error('Guardian API error: ' + (guardianResponse?.message || 'unknown'));
      }

      const articles = (guardianResponse?.results || []).map(a => mapGuardianArticle(a, topicId));
      const totalPages = guardianResponse?.pages || 1;

      const finalList = append ? [...marketNews, ...articles] : articles;

      if (finalList.length === 0 && page === 1) {
        setMarketNews(NEWS_FALLBACK);
      } else {
        setMarketNews(finalList);
        setNewsTotalPages(totalPages);
        setNewsPage(page);
        setNewsError(false);

        // Cache page 1 results
        if (page === 1) {
          try {
            await AsyncStorage.setItem(
              `${GUARDIAN_CACHE_KEY}_${topicId}`,
              JSON.stringify({ data: { articles: finalList, totalPages }, ts: Date.now() })
            );
          } catch { /* cache write failed — non-fatal */ }
        }
      }
    } catch (err) {
      console.warn('Guardian API error:', err.message);
      setNewsError(true);
      if (page === 1) {
        // Load from cache even if stale
        try {
          const cached = await AsyncStorage.getItem(`${GUARDIAN_CACHE_KEY}_${topicId}`);
          if (cached) {
            const { data } = JSON.parse(cached);
            setMarketNews(data.articles);
            setNewsTotalPages(data.totalPages || 1);
            return;
          }
        } catch { /* no cache available */ }
        setMarketNews(NEWS_FALLBACK);
      }
    } finally {
      setNewsLoading(false);
      setNewsLoadingMore(false);
    }
  };

  const handleNewsTopicChange = (topicId) => {
    setNewsTopic(topicId);
    setNewsPage(1);
    setMarketNews([]);
    loadFetchNews(topicId, 1, false);
  };

  const handleNewsLoadMore = () => {
    if (newsLoadingMore || newsPage >= newsTotalPages) return;
    loadFetchNews(newsTopic, newsPage + 1, true);
  };

  const onRefresh = async () => { setRefreshing(true); await checkNet(); await loadAllData(); loadFetchNews(); setRefreshing(false); };

  // ── Safe getters ────────────────────────────────────────────────────────
  const getInnovations       = () => Array.isArray(innovations)         ? innovations         : [];
  const getFilteredInnovations = () => Array.isArray(filteredInnovations) ? filteredInnovations : [];
  const getMetrics           = () => metrics || MOCK_DATA.metrics[0];
  const getUsers             = () => Array.isArray(users) ? users : [];
  const getTeams             = () => Array.isArray(teams) ? teams : [];

  // ────────────────────────────────────────────────────────────────────────
  // FILTERING + SORTING
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let filtered = [...getInnovations()];
    if (query.trim()) {
      filtered = filtered.filter(i =>
        i.title?.toLowerCase().includes(query.toLowerCase()) ||
        i.description?.toLowerCase().includes(query.toLowerCase()) ||
        (i.tags || []).some(t => t?.toLowerCase().includes(query.toLowerCase()))
      );
    }
    if (activeStage !== 'all')         filtered = filtered.filter(i => i.stage === activeStage);
    if (activeType  !== 'all')         filtered = filtered.filter(i => i.type  === activeType);
    if (filters.industry !== 'all')    filtered = filtered.filter(i => i.industry === filters.industry);
    if (filters.impact   !== 'all')    filtered = filtered.filter(i => i.impact   === filters.impact);
    if (filters.bookmarked)            filtered = filtered.filter(i => i.bookmarked);

    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'roi_high': return (b.estimated_roi || 0) - (a.estimated_roi || 0);
        case 'progress': return (b.progress || 0)       - (a.progress || 0);
        case 'impact':   {
          const m = { critical: 4, high: 3, medium: 2, low: 1 };
          return (m[b.impact] || 0) - (m[a.impact] || 0);
        }
        case 'score':    return (b.health || 0) - (a.health || 0);
        default:         return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredInnovations(filtered);
  }, [query, activeStage, activeType, filters, sortBy, innovations]);

  // ────────────────────────────────────────────────────────────────────────
  // CRUD
  // ────────────────────────────────────────────────────────────────────────
  const createInnovation = async (data) => {
    try {
      const payload = {
        ...data,
        estimated_roi: parseInt(data.estimated_roi) || 0,
        budget:        parseInt(data.budget)        || 0,
        start_date:    data.start_date instanceof Date ? data.start_date.toISOString() : data.start_date,
        end_date:      data.end_date   instanceof Date ? data.end_date.toISOString()   : data.end_date,
        tags: typeof data.tags === 'string' ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : (data.tags || []),
        votes: 0, notes: [], bookmarked: false,
      };
      payload.health = calcHealthScore(payload);
      try {
        const res = await ApiService.createInnovation(payload);
        if (res.success) {
          const n = { ...res.data, health: calcHealthScore(res.data), votes: 0, notes: [], bookmarked: false };
          setInnovations(p => [n, ...p]);
          Alert.alert('✅ Saved', 'Innovation saved to database');
          return n;
        }
      } catch { /* fall through to local */ }
      // Local fallback
      const local = { ...payload, id: Date.now().toString(), created_at: new Date().toISOString() };
      setInnovations(p => [local, ...p]);
      Alert.alert('✅ Saved', 'Saved locally (server unavailable)');
      return local;
    } catch (e) {
      Alert.alert('❌ Error', 'Failed to save: ' + e.message);
      throw e;
    }
  };

  const updateInnovation = async (id, updates) => {
    // Skip API for locally-generated IDs: prefixed (fallback_/mock_/ai_) or plain timestamps (13-digit ms)
    const isLocalId = !id || /^(fallback_|mock_|ai_)/.test(String(id)) || /^\d{10,}$/.test(String(id));
    if (!isLocalId) {
      try { await ApiService.updateInnovation(id, updates); } catch { /* silent */ }
    }
    setInnovations(p => p.map(i => i.id === id ? { ...i, ...updates, health: calcHealthScore({ ...i, ...updates }) } : i));
  };

  const deleteInnovation = (id) => Alert.alert('Delete Innovation', 'Are you sure?', [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Delete', style: 'destructive', onPress: async () => {
      const isLocalId = !id || /^(fallback_|mock_|ai_)/.test(String(id)) || /^\d{10,}$/.test(String(id));
      if (!isLocalId) {
        try { await ApiService.deleteInnovation(id); } catch { /* silent */ }
      }
      setInnovations(p => p.filter(i => i.id !== id));
      setShowDetailModal(false);
      Alert.alert('✅ Deleted');
    }},
  ]);

  const toggleBookmark = (id) => {
    setInnovations(p => p.map(i => i.id === id ? { ...i, bookmarked: !i.bookmarked } : i));
    if (selectedInnovation?.id === id) setSelectedInnovation(s => ({ ...s, bookmarked: !s.bookmarked }));
  };

  const voteInnovation = (id) => {
    setInnovations(p => p.map(i => i.id === id ? { ...i, votes: (i.votes || 0) + 1 } : i));
    if (selectedInnovation?.id === id) setSelectedInnovation(s => ({ ...s, votes: (s.votes || 0) + 1 }));
  };

  const addNote = (innovationId) => {
    if (!newNote.trim()) return;
    const note = { id: Date.now().toString(), text: newNote.trim(), timestamp: new Date().toISOString() };
    setInnovations(p => p.map(i => i.id === innovationId ? { ...i, notes: [...(i.notes || []), note] } : i));
    if (selectedInnovation?.id === innovationId) setSelectedInnovation(s => ({ ...s, notes: [...(s.notes || []), note] }));
    setNewNote('');
  };

  const advanceStage = (innovation) => {
    const ordered = INNOVATION_STAGES.map(s => s.id);
    const idx = ordered.indexOf(innovation.stage);
    if (idx < 0 || idx >= ordered.length - 2) return;
    const next = ordered[idx + 1];
    Alert.alert(`Advance Stage`, `Move "${innovation.title}" to ${next.toUpperCase()}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Advance', onPress: () => updateInnovation(innovation.id, { stage: next, progress: Math.min((innovation.progress || 0) + 20, 100) }) },
    ]);
  };

  const saveBusinessPlan = () => {
    if (!businessPlanForm.title || !businessPlanForm.company_name) {
      Alert.alert('❌ Error', 'Title and company name are required');
      return;
    }
    const plan = { id: Date.now().toString(), ...businessPlanForm, status: 'draft', created_at: new Date().toISOString() };
    setBusinessPlans(p => [plan, ...p]);
    setShowBusinessPlanModal(false);
    resetBusinessPlanForm();
    Alert.alert('✅ Saved', 'Business plan saved');
  };

  // ── Form helpers ─────────────────────────────────────────────────────────
  const handleSubmitInnovation = async () => {
    if (!form.title.trim()) { Alert.alert('❌ Error', 'Title is required'); return; }
    await createInnovation(form);
    setShowAddModal(false);
    resetForm();
  };

  const resetForm = () => setForm({
    title: '', description: '', type: 'product', stage: 'idea', impact: 'medium',
    priority: 'medium', estimated_roi: '', budget: '', start_date: new Date(),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    owner_id: '', team_id: '', tags: '', success_metrics: '', risks: '',
    timeline: '3 months', confidence: 85, industry: 'Software',
  });

  const resetBusinessPlanForm = () => setBusinessPlanForm({ title: '', company_name: '', executive_summary: '', market_analysis: '', products_services: '', marketing_strategy: '', financial_projections: '', funding_requirements: '' });


  // ────────────────────────────────────────────────────────────────────────
  // AI HANDLERS (direct Claude API)
  // ────────────────────────────────────────────────────────────────────────
  const handleGenerateIdeas = async () => {
    setAiProcessing(true);
    try {
      const ideas = await generateAIIdeas(getInnovations(), generationCount, generationIndustry);
      if (ideas && ideas.length > 0) {
        setAiGeneratedIdeas(ideas);
        const prov = getActiveProvider();
        setActiveAIProvider(prov.name || 'Claude AI');
        Alert.alert('✅ Generated', `${ideas.length} idea${ideas.length > 1 ? 's' : ''} generated by ${prov.name}!`);
      } else {
        Alert.alert('⚠️ Offline Mode', 'Generated sample ideas (AI unavailable)');
      }
    } catch (e) {
      console.error('Generate error:', e);
      Alert.alert('⚠️ Offline Mode', 'Generated sample ideas (AI unavailable)');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleGenerateBusinessPlan = async () => {
    if (!businessPlanForm.company_name) { Alert.alert('⚠️ Required', 'Enter company name first'); return; }
    setAiProcessing(true);
    try {
      const plan = await aiGenerateBusinessPlan(
        businessPlanForm.company_name,
        getInnovations(),
        getMetrics()
      );
      if (plan) {
        setBusinessPlanForm(p => ({
          ...p,
          executive_summary:    plan.executive_summary    || p.executive_summary,
          market_analysis:      plan.market_analysis      || p.market_analysis,
          products_services:    plan.products_services    || p.products_services,
          marketing_strategy:   plan.marketing_strategy   || p.marketing_strategy,
          financial_projections:typeof plan.financial_projections === 'string'
                                  ? plan.financial_projections
                                  : JSON.stringify(plan.financial_projections || ''),
          funding_requirements: String(plan.funding_requirements || '500000'),
        }));
        const prov = getActiveProvider();
        Alert.alert('✅ Generated', `Business plan generated by ${prov.name}!`);
      } else throw new Error('No plan returned');
    } catch (e) {
      console.error('BP generation error:', e);
      setBusinessPlanForm(p => ({
        ...p,
        executive_summary: `${businessPlanForm.company_name} is positioned to lead in UK engineering innovation through AI-powered solutions. Our platform integrates cutting-edge technology with industry expertise.`,
        market_analysis: 'The UK engineering sector represents a £180B market growing at 8.5% CAGR. Demand for AI solutions is accelerating.',
        products_services: 'AI-powered engineering tools: candidate matching, predictive analytics, process automation.',
        marketing_strategy: 'B2B SaaS with enterprise sales, partnerships with engineering institutions, content marketing.',
        financial_projections: 'Year 1: £1.2M, Year 2: £3.5M, Year 3: £8M',
        funding_requirements: '500000',
      }));
      Alert.alert('⚠️ Template Used', 'Using template (AI unavailable)');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleSendAIMessage = async () => {
    if (!chatInput.trim()) return;
    const userMsg = { role: 'user', content: chatInput, timestamp: new Date().toISOString() };
    setChatMessages(p => [...p, userMsg]);
    const inputSnapshot = chatInput;
    setChatInput('');
    setAiProcessing(true);
    setStreamingMessage('');
    const history = chatMessages.slice(-6).map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));
    try {
      const result = await getAIAdvice(
        inputSnapshot,
        getInnovations(),
        getMetrics(),
        history,
        (partial) => setStreamingMessage(partial),
      );
      const content = result?.message || 'No response received.';
      setChatMessages(p => [...p, {
        role: 'assistant',
        content,
        provider: result?.provider,
        timestamp: result?.timestamp || new Date().toISOString(),
      }]);
    } catch {
      setChatMessages(p => [...p, { role: 'assistant', content: 'I\'m currently offline. Please check your connection and try again.', timestamp: new Date().toISOString() }]);
    } finally {
      setAiProcessing(false);
      setStreamingMessage('');
      setTimeout(() => chatScrollRef.current?.scrollToEnd({ animated: true }), 100);
    }
  };

  const handleAnalyzeInnovation = async (innovation) => {
    setAiProcessing(true);
    try {
      const analysis = await aiAnalyzeInnovation(innovation);
      if (!analysis) throw new Error('No analysis returned');
      const prov = getActiveProvider();
      Alert.alert(
        `📊 Strategic Analysis`,
        `✅ Success Probability: ${analysis.success_probability || '—'}%\n\n` +
        `🎯 Market Fit Score: ${analysis.market_fit_score || '—'}/100\n\n` +
        `💼 Technical Readiness: ${analysis.technical_readiness || '—'}/100\n\n` +
        `💪 Strengths:\n• ${(analysis.strengths || []).slice(0, 3).join('\n• ')}\n\n` +
        `⚠️ Risks:\n• ${(analysis.risks || []).slice(0, 2).join('\n• ')}\n\n` +
        `🚀 Recommendations:\n• ${(analysis.recommendations || analysis.next_steps || []).slice(0, 2).join('\n• ')}\n\n` +
        `📝 ${analysis.summary || ''}\n\nPowered by ${prov.name}`,
        [{ text: 'Close' }]
      );
    } catch {
      Alert.alert('❌ Error', 'Could not analyse (offline)');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleCompetitorAnalysis = async (innovation) => {
    setCompetitorLoading(true);
    setCompetitorAnalysis('');
    try {
      const result = await aiAnalyzeCompetitors(innovation);
      if (!result) throw new Error('No result');
      // Format structured JSON into readable text
      const lines = [];
      if (result.market_gap)             lines.push(`MARKET GAP\n${result.market_gap}`);
      if (result.differentiation_score)  lines.push(`DIFFERENTIATION SCORE: ${result.differentiation_score}/100  |  THREAT LEVEL: ${result.threat_level || '—'}`);
      if ((result.competitors || []).length) {
        lines.push('\nTOP COMPETITORS');
        result.competitors.forEach((c, i) => {
          lines.push(`${i + 1}. ${c.name} (${c.market_share || '—'} market share)\n   ${c.description}\n   Weakness: ${c.weakness}`);
        });
      }
      if ((result.our_advantages || []).length) {
        lines.push('\nOUR ADVANTAGES\n• ' + result.our_advantages.join('\n• '));
      }
      const prov = getActiveProvider();
      setCompetitorAnalysis(lines.join('\n\n') + `\n\nPowered by ${prov.name}`);
    } catch {
      setCompetitorAnalysis('Competitor analysis unavailable (offline). Key differentiators: AI-first approach, UK market focus, deep engineering expertise.');
    } finally {
      setCompetitorLoading(false);
    }
  };

  // ── DEEP SEARCH ──────────────────────────────────────────────────────────
  const handleDeepSearch = async (innovation) => {
    setDeepSearchIdea(innovation);
    setDeepSearchResults(null);
    setDeepSearchTab(0);
    setDeepSearchPhase(0);
    setDeepSearchLoading(true);
    setShowDetailModal(false);
    setShowDeepSearchModal(true);

    const ctx =
      `Innovation: "${innovation.title}"\n` +
      `Industry: ${innovation.industry || 'Engineering'}\n` +
      `Type: ${innovation.type || 'technology'}\n` +
      `Description: ${innovation.description}\n` +
      `Budget: £${(innovation.budget || 0).toLocaleString()} | Est. ROI: £${(innovation.estimated_roi || 0).toLocaleString()}\n` +
      `Timeline: ${innovation.timeline || '6 months'} | Confidence: ${innovation.confidence || 85}%\n` +
      `Stage: ${innovation.stage || 'idea'} | Impact: ${innovation.impact || 'high'}`;

    const results = {};

    try {
      // ── Phase 1: Implementation Plan ──────────────────────────────────
      setDeepSearchPhase(1);
      setDeepSearchPhaseMsg('Researching implementation strategy…');
      const implPrompt =
        `You are an elite engineering innovation strategist.\n\n${ctx}\n\n` +
        `Produce a comprehensive IMPLEMENTATION PLAN. Cover:\n` +
        `1. TECHNICAL ARCHITECTURE — core components, tech stack, APIs, infrastructure\n` +
        `2. DEVELOPMENT PHASES — Phase 1/2/3 with deliverables and durations\n` +
        `3. TEAM REQUIREMENTS — roles, headcount, skills needed\n` +
        `4. KEY MILESTONES — 5 specific measurable milestones with target dates\n` +
        `5. TOOLS & PLATFORMS — recommended dev tools, cloud platforms, frameworks\n` +
        `6. INTEGRATION POINTS — how it connects to existing engineering systems\n` +
        `Be specific, practical, and engineering-focused. Use UK market context. 400 words max.`;
      results.implementation = await callClaude(implPrompt, 1200);

      // ── Phase 2: Feasibility Study ────────────────────────────────────
      setDeepSearchPhase(2);
      setDeepSearchPhaseMsg('Running feasibility analysis…');
      const feasPrompt =
        `You are an elite engineering innovation strategist.\n\n${ctx}\n\n` +
        `Produce a structured FEASIBILITY STUDY with these exact sections:\n` +
        `TECHNICAL FEASIBILITY: Is the technology mature enough? What are the engineering challenges? Score: X/10\n` +
        `COMMERCIAL FEASIBILITY: Is there a proven market? What is the demand signal? Score: X/10\n` +
        `FINANCIAL FEASIBILITY: Does the unit economics work? Break-even analysis. Score: X/10\n` +
        `OPERATIONAL FEASIBILITY: Can the team execute? What infrastructure is needed? Score: X/10\n` +
        `REGULATORY FEASIBILITY: UK/EU compliance requirements, certifications, standards. Score: X/10\n` +
        `OVERALL VERDICT: Go / Conditional Go / No-Go with one paragraph justification.\n` +
        `Be data-driven. Reference UK engineering sector benchmarks. 400 words max.`;
      results.feasibility = await callClaude(feasPrompt, 1200);

      // ── Phase 3: Market Research ──────────────────────────────────────
      setDeepSearchPhase(3);
      setDeepSearchPhaseMsg('Analysing market landscape…');
      const marketPrompt =
        `You are an elite engineering innovation strategist.\n\n${ctx}\n\n` +
        `Produce a MARKET RESEARCH REPORT covering:\n` +
        `MARKET SIZE: TAM / SAM / SOM with £ values and growth rates\n` +
        `TARGET SEGMENTS: 3 specific customer segments with company size, budget, and pain points\n` +
        `DEMAND DRIVERS: 4 macro trends fuelling demand in UK/EU engineering\n` +
        `COMPETITOR LANDSCAPE: Top 4 competitors — name, market share estimate, and key weakness\n` +
        `GO-TO-MARKET: Recommended sales channels, pricing model, and first 10 customer strategy\n` +
        `MARKET TIMING: Is now the right time? What market signals confirm this?\n` +
        `Be specific with numbers. 400 words max.`;
      results.market = await callClaude(marketPrompt, 1200);

      // ── Phase 4: Risk Analysis ────────────────────────────────────────
      setDeepSearchPhase(4);
      setDeepSearchPhaseMsg('Mapping risks and mitigations…');
      const riskPrompt =
        `You are an elite engineering innovation strategist.\n\n${ctx}\n\n` +
        `Produce a RISK ANALYSIS MATRIX. For each risk provide: Risk Name | Probability (L/M/H) | Impact (L/M/H) | Mitigation Strategy.\n\n` +
        `Cover ALL of these risk categories:\n` +
        `- Technical Risks (3 risks)\n` +
        `- Market & Commercial Risks (3 risks)\n` +
        `- Regulatory & Compliance Risks (2 risks)\n` +
        `- Financial & Funding Risks (2 risks)\n` +
        `- Talent & Execution Risks (2 risks)\n\n` +
        `End with: TOP 3 CRITICAL RISKS TO MONITOR and an OVERALL RISK SCORE (1-10, 10=highest risk).\n` +
        `350 words max.`;
      results.risks = await callClaude(riskPrompt, 1000);

      // ── Phase 5: Funding & Next Steps ────────────────────────────────
      setDeepSearchPhase(5);
      setDeepSearchPhaseMsg('Identifying funding pathways…');
      const fundingPrompt =
        `You are an elite engineering innovation strategist.\n\n${ctx}\n\n` +
        `Produce a FUNDING & NEXT STEPS GUIDE covering:\n` +
        `FUNDING OPTIONS: 5 specific funding sources for this type of engineering innovation in the UK —\n` +
        `  include Innovate UK grants, UKRI programmes, angel/VC routes, revenue-based finance, and strategic partnerships.\n` +
        `  For each: name, amount available, eligibility criteria, application timeline.\n` +
        `IMMEDIATE NEXT STEPS: 7 concrete actions to take in the next 30 days, numbered and specific.\n` +
        `90-DAY SPRINT PLAN: Week-by-week breakdown of the first 12 weeks.\n` +
        `SUCCESS METRICS: 5 KPIs to track in the first 6 months with target values.\n` +
        `350 words max.`;
      results.funding = await callClaude(fundingPrompt, 1000);

      setDeepSearchResults(results);
      setDeepSearchPhase(6); // complete
    } catch (e) {
      console.error('Deep search error:', e);
      setDeepSearchResults({
        implementation: results.implementation || 'Implementation analysis unavailable — please check your Claude API key.',
        feasibility:    results.feasibility    || 'Feasibility study unavailable — please check your Claude API key.',
        market:         results.market         || 'Market research unavailable — please check your Claude API key.',
        risks:          results.risks          || 'Risk analysis unavailable — please check your Claude API key.',
        funding:        results.funding        || 'Funding guide unavailable — please check your Claude API key.',
      });
      setDeepSearchPhase(6);
    } finally {
      setDeepSearchLoading(false);
      setDeepSearchPhaseMsg('');
    }
  };

  const handleDeepSearchExportPDF = async () => {
    if (!deepSearchResults || !deepSearchIdea) return;
    setPdfLoading(true);
    try {
      const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
        *{box-sizing:border-box}body{font-family:Helvetica,Arial,sans-serif;padding:40px;color:#0f172a;line-height:1.75;font-size:14px}
        .cover{background:linear-gradient(135deg,#0e4e96,#19abb5);color:white;padding:50px 40px;border-radius:16px;margin-bottom:40px;text-align:center}
        .cover h1{font-size:30px;margin:0 0 10px;color:white}.cover p{opacity:.8;margin:4px 0}
        h2{color:#0e4e96;border-bottom:3px solid #fdb913;padding-bottom:8px;margin-top:40px;font-size:18px}
        .section{white-space:pre-wrap;background:#f8fafc;border-radius:10px;padding:20px;margin:16px 0;border-left:4px solid #0e4e96;font-size:13px;line-height:1.8}
        .page-break{page-break-after:always;margin:40px 0}
        .footer{margin-top:50px;font-size:11px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:16px}
      </style></head><body>
      <div class="cover"><h1>${deepSearchIdea.title}</h1><p>Deep Search Report — Implementation &amp; Feasibility Study</p><p>${today} · Powered by Claude AI · JobEng Innovation Hub</p></div>
      <h2>1. Implementation Plan</h2><div class="section">${deepSearchResults.implementation}</div>
      <div class="page-break"></div>
      <h2>2. Feasibility Study</h2><div class="section">${deepSearchResults.feasibility}</div>
      <div class="page-break"></div>
      <h2>3. Market Research</h2><div class="section">${deepSearchResults.market}</div>
      <div class="page-break"></div>
      <h2>4. Risk Analysis</h2><div class="section">${deepSearchResults.risks}</div>
      <div class="page-break"></div>
      <h2>5. Funding &amp; Next Steps</h2><div class="section">${deepSearchResults.funding}</div>
      <div class="footer">© JobEng AI Innovation Hub · Deep Search Report · ${deepSearchIdea.title} · ${today}</div>
      </body></html>`;
      const { uri } = await Print.printToFileAsync({ html });
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri, { mimeType: 'application/pdf' });
    } catch (e) { Alert.alert('❌ Export Error', e.message); }
    finally { setPdfLoading(false); }
  };

  const handleAISearch = async () => {
    if (!query.trim()) return;
    setSearchAiLoading(true);
    try {
      const filtered = await aiSearchInnovations(query, getInnovations());
      if (filtered && filtered.length > 0) setFilteredInnovations(filtered);
    } catch { /* silent — normal search already applied */ }
    finally { setSearchAiLoading(false); }
  };

  const handleAddAIIdea = (idea) => {
    const newInn = { ...idea, id: Date.now().toString(), created_at: new Date().toISOString(), stage: 'idea', owner: { name: 'AI Generated', id: 'ai' }, progress: 0, ml_generated: true, ai_mode: idea.provider || 'CLAUDE' };
    newInn.health = calcHealthScore(newInn);
    setInnovations(p => [newInn, ...p]);
    setAiGeneratedIdeas(p => p.filter(i => i.id !== idea.id));
    Alert.alert('✅ Added', 'AI-generated innovation added to pipeline!');
  };

  const handleRejectAIIdea = (id) => setAiGeneratedIdeas(p => p.filter(i => i.id !== id));

  // ── ROI Calculator ───────────────────────────────────────────────────────
  const calculateROI = () => {
    const invest   = parseFloat(roiInputs.investment) || 0;
    const revenue  = parseFloat(roiInputs.revenue)    || 0;
    const years    = parseInt(roiInputs.timeYears)    || 3;
    const risk     = parseFloat(roiInputs.riskFactor) / 100;
    if (!invest || !revenue) { Alert.alert('⚠️ Enter investment and revenue'); return; }
    const totalRevenue   = revenue * years;
    const riskAdjusted   = totalRevenue * (1 - risk);
    const netProfit      = riskAdjusted - invest;
    const roiPercent     = ((netProfit / invest) * 100).toFixed(1);
    const breakeven      = Math.ceil(invest / revenue);
    const npv            = Array.from({ length: years }, (_, i) => (revenue / Math.pow(1.08, i + 1))).reduce((a, b) => a + b, 0) - invest;
    setRoiResult({ totalRevenue, riskAdjusted, netProfit, roiPercent, breakeven, npv: npv.toFixed(0) });
  };

  // ── Export / Print ───────────────────────────────────────────────────────
  const getImpactColor = (impact) => ({
    critical: BRAND.danger, high: BRAND.blue, medium: BRAND.warning, low: BRAND.sub
  }[impact?.toLowerCase()] || BRAND.blue);

  const generateExportHTML = (innovation) => `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><style>
body{font-family:Helvetica,Arial,sans-serif;padding:40px;color:#0f172a;line-height:1.6}
.header{text-align:center;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #0e4e96}
h1{color:#0e4e96;font-size:28px}h2{color:#0e4e96;font-size:20px}
.badge{display:inline-block;padding:8px 16px;border-radius:20px;font-size:14px;font-weight:bold;margin:5px;color:white}
.section{margin-bottom:25px;padding:20px;background:#f8fafc;border-radius:12px;border-left:5px solid #0e4e96}
.meta-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:15px;margin-bottom:20px}
.meta-item{background:white;padding:15px;border-radius:10px;border:1px solid #e2e8f0}
.meta-label{font-size:12px;color:#475569;margin-bottom:5px}
.meta-value{font-size:18px;font-weight:bold;color:#0e4e96}
.roi-highlight{background:linear-gradient(135deg,#10b981,#059669);color:white;padding:15px;border-radius:10px;text-align:center;margin-bottom:20px}
.roi-highlight .amount{font-size:32px;font-weight:bold}
.tags{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
.tag{background:rgba(14,78,150,0.1);padding:6px 12px;border-radius:6px;font-size:12px;color:#0e4e96}
.footer{margin-top:40px;padding-top:20px;border-top:1px solid #e2e8f0;text-align:center;font-size:12px;color:#475569}
.health{display:inline-block;padding:8px 16px;border-radius:20px;font-size:14px;font-weight:bold;background:${(innovation.health || 0) >= 80 ? '#d1fae5' : '#fef3c7'};color:${(innovation.health || 0) >= 80 ? '#065f46' : '#92400e'}}
</style></head><body>
<div class="header">
  <h1>${innovation.title}</h1>
  <span class="badge" style="background:${getImpactColor(innovation.impact)}">Impact: ${innovation.impact?.toUpperCase()}</span>
  <span class="badge" style="background:#0e4e96">Stage: ${innovation.stage?.toUpperCase()}</span>
  <span class="health">Health Score: ${innovation.health || calcHealthScore(innovation)}</span>
</div>
<div class="roi-highlight">
  <div style="font-size:16px;margin-bottom:5px">Estimated ROI</div>
  <div class="amount">£${(innovation.estimated_roi || 0).toLocaleString()}</div>
  <div style="font-size:12px;opacity:0.9">Budget: £${(innovation.budget || 0).toLocaleString()} · Confidence: ${innovation.confidence || 85}%</div>
</div>
<div class="section"><h2>📋 Description</h2><p style="font-size:16px;line-height:1.8">${innovation.description || 'No description.'}</p></div>
<div class="meta-grid">
  <div class="meta-item"><div class="meta-label">Type</div><div class="meta-value">${innovation.type?.toUpperCase()}</div></div>
  <div class="meta-item"><div class="meta-label">Timeline</div><div class="meta-value">${innovation.timeline || '3 months'}</div></div>
  <div class="meta-item"><div class="meta-label">Industry</div><div class="meta-value">${innovation.industry || 'N/A'}</div></div>
  <div class="meta-item"><div class="meta-label">Progress</div><div class="meta-value">${innovation.progress || 0}%</div></div>
</div>
${(innovation.tags || []).length > 0 ? `<div class="section"><h2>🏷️ Tags</h2><div class="tags">${innovation.tags.map(t => `<span class="tag">${t}</span>`).join('')}</div></div>` : ''}
${innovation.success_metrics ? `<div class="section"><h2>📊 Success Metrics</h2><p>${innovation.success_metrics}</p></div>` : ''}
${innovation.risks ? `<div class="section"><h2>⚠️ Risks</h2><p style="color:#dc2626">${innovation.risks}</p></div>` : ''}
${(innovation.notes || []).length > 0 ? `<div class="section"><h2>📝 Notes</h2>${innovation.notes.map(n => `<p><em>${new Date(n.timestamp).toLocaleDateString()}</em>: ${n.text}</p>`).join('')}</div>` : ''}
<div class="footer"><p>Generated by JobEng AI Innovation Hub · ${new Date().toLocaleDateString()}</p><p>AI Provider: ${innovation.ai_mode || 'N/A'} · Votes: ${innovation.votes || 0}</p></div>
</body></html>`;

  const generateDetailedBusinessPlanHTML = (innovation) => {
    const today   = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const industry = innovation.industry || 'Engineering';
    const budget   = innovation.budget  || 120000;
    const roi      = innovation.estimated_roi || 500000;
    const conf     = innovation.confidence   || 85;
    const health   = innovation.health || calcHealthScore(innovation);
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
*{box-sizing:border-box}
body{font-family:Helvetica,Arial,sans-serif;padding:40px;color:#0f172a;line-height:1.7;font-size:15px}
.cover{text-align:center;margin-bottom:60px;padding:60px 20px;background:linear-gradient(135deg,#0e4e96,#19abb5);border-radius:20px;color:white}
.cover h1{font-size:36px;margin:0 0 12px;color:white}
.cover .subtitle{font-size:18px;opacity:0.85;margin-bottom:20px}
.badge{display:inline-block;background:rgba(255,255,255,0.2);color:white;padding:7px 18px;border-radius:30px;font-size:13px;margin:4px;border:1px solid rgba(255,255,255,0.3)}
.kpi-row{display:flex;gap:16px;margin:24px 0;flex-wrap:wrap}
.kpi{flex:1;min-width:140px;background:#f0f9ff;border-radius:12px;padding:16px;border-top:4px solid #0e4e96;text-align:center}
.kpi .val{font-size:22px;font-weight:800;color:#0e4e96}
.kpi .lbl{font-size:12px;color:#475569;margin-top:4px}
h2{color:#0e4e96;border-bottom:3px solid #fdb913;padding-bottom:8px;margin-top:40px;font-size:20px}
h3{color:#19abb5;margin-top:24px;font-size:16px}
.section{margin-bottom:28px}
.highlight{background:linear-gradient(135deg,#0e4e96,#19abb5);color:white;padding:22px;border-radius:14px;margin:20px 0;font-size:16px}
.callout{background:#f0fdf4;border-left:5px solid #10b981;padding:16px 20px;border-radius:0 12px 12px 0;margin:16px 0}
.risk-callout{background:#fef2f2;border-left:5px solid #ef4444;padding:16px 20px;border-radius:0 12px 12px 0;margin:16px 0}
table{width:100%;border-collapse:collapse;margin:18px 0;font-size:14px}
th{background:#0e4e96;color:white;padding:12px 14px;text-align:left}
td{border:1px solid #e2e8f0;padding:11px 14px;vertical-align:top}
tr:nth-child(even) td{background:#f8fafc}
.page-break{page-break-after:always;margin:50px 0;border-top:1px dashed #e2e8f0}
.footer{margin-top:60px;font-size:12px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:20px}
.tag{display:inline-block;background:#e0f2fe;color:#0369a1;padding:4px 12px;border-radius:20px;font-size:12px;margin:3px;font-weight:600}
.section-number{display:inline-block;width:32px;height:32px;border-radius:16px;background:#0e4e96;color:white;text-align:center;line-height:32px;font-weight:800;font-size:14px;margin-right:8px;vertical-align:middle}
</style></head><body>

<!-- COVER -->
<div class="cover">
  <div style="font-size:13px;opacity:0.7;margin-bottom:12px;text-transform:uppercase;letter-spacing:1px">JobEng AI Innovation Hub · Engineering Business Plan</div>
  <h1>${innovation.title}</h1>
  <div class="subtitle">Comprehensive Engineering Innovation Business Opportunity</div>
  <div><span class="badge">${industry}</span><span class="badge">${(innovation.type || 'technology').toUpperCase()}</span><span class="badge">${(innovation.stage || 'idea').toUpperCase()}</span></div>
  <div style="margin-top:24px;font-size:13px;opacity:0.7">Prepared by: JobEng AI · Date: ${today} · Confidential</div>
</div>

<div class="kpi-row">
  <div class="kpi"><div class="val">£${(roi / 1000).toFixed(0)}k</div><div class="lbl">Est. ROI</div></div>
  <div class="kpi"><div class="val">£${(budget / 1000).toFixed(0)}k</div><div class="lbl">Budget</div></div>
  <div class="kpi"><div class="val">${conf}%</div><div class="lbl">Confidence</div></div>
  <div class="kpi"><div class="val">${innovation.timeline || '6 months'}</div><div class="lbl">Timeline</div></div>
  <div class="kpi"><div class="val">${health}/100</div><div class="lbl">Health Score</div></div>
  <div class="kpi"><div class="val">${(innovation.progress || 0)}%</div><div class="lbl">Progress</div></div>
</div>

<div class="page-break"></div>

<!-- SECTION 1: INNOVATION IDEA -->
<h2><span class="section-number">1</span>Innovation Idea</h2>
<div class="highlight">${innovation.description}</div>
<p><strong>Category:</strong> ${industry} · <strong>Type:</strong> ${(innovation.type || 'technology').replace('_', '/')} · <strong>Stage:</strong> ${innovation.stage}</p>
${(innovation.tags || []).length ? `<div style="margin-top:12px">${(innovation.tags || []).map(t => `<span class="tag">${t}</span>`).join('')}</div>` : ''}

<!-- SECTION 2: PROBLEM SOLVED -->
<h2><span class="section-number">2</span>Problem Solved</h2>
<p>The ${industry} sector faces persistent inefficiencies in ${innovation.type === 'ai_ml' ? 'data processing and decision intelligence' : innovation.type === 'automation' ? 'manual workflows and operational throughput' : innovation.type === 'analytics' ? 'performance monitoring and predictive insight' : 'core operational delivery and scalability'}. Existing solutions are fragmented, costly to integrate, and fail to address the specific demands of ${innovation.impact === 'critical' ? 'critical infrastructure and safety-regulated' : 'high-growth'} engineering environments.</p>
<div class="callout"><strong>Pain Points Addressed:</strong> Operational inefficiency · High cost of legacy systems · Lack of real-time intelligence · Skills shortage in specialised engineering roles · Slow time-to-market for complex projects</div>

<!-- SECTION 3: TECHNICAL SOLUTION -->
<h2><span class="section-number">3</span>Technical Solution</h2>
<h3>3.1 Architecture Overview</h3>
<p>${innovation.title} delivers a ${innovation.type?.replace('_', '/') || 'technology'}-powered platform built on a cloud-native, API-first architecture. The system integrates real-time data pipelines, AI inference engines, and a modular engineering toolkit designed for rapid deployment in regulated environments.</p>
<table>
  <tr><th>Component</th><th>Technology Stack</th><th>Status</th></tr>
  <tr><td>AI / ML Engine</td><td>Claude AI + GPT-4o integration, custom fine-tuned models</td><td>✅ Implemented</td></tr>
  <tr><td>Data Pipeline</td><td>Real-time streaming, REST/GraphQL APIs</td><td>✅ Live</td></tr>
  <tr><td>Core Platform</td><td>Cloud-native (AWS/Azure), microservices, containerised</td><td>🔄 In Build</td></tr>
  <tr><td>Security & Compliance</td><td>ISO 27001 framework, GDPR-compliant, SOC 2 planned</td><td>✅ Compliant</td></tr>
  <tr><td>Integration Layer</td><td>ERP, CAD/BIM, SCADA, IoT sensor protocols</td><td>🔄 Roadmap</td></tr>
</table>
<h3>3.2 Technical Differentiation</h3>
<p>Unlike incumbent solutions, this platform combines domain-specific AI models trained on ${industry} data with a no-code configuration layer — enabling rapid deployment without specialist IT resources on-site.</p>

<div class="page-break"></div>

<!-- SECTION 4: TARGET MARKET -->
<h2><span class="section-number">4</span>Target Market</h2>
<h3>4.1 Market Sizing</h3>
<table>
  <tr><th>Market Segment</th><th>2025 Value</th><th>2030 Projection</th><th>CAGR</th></tr>
  <tr><td>TAM — Global ${industry} Tech</td><td>$180B</td><td>$290B</td><td>10.0%</td></tr>
  <tr><td>SAM — UK + European Enterprise</td><td>$42B</td><td>$68B</td><td>10.1%</td></tr>
  <tr><td>SOM — Initial Target Segment</td><td>$4B</td><td>$9B</td><td>17.6%</td></tr>
</table>
<h3>4.2 Ideal Customer Profile</h3>
<table>
  <tr><th>Segment</th><th>Company Size</th><th>Annual Spend</th><th>Decision Maker</th></tr>
  <tr><td>Tier 1 Engineering Firms</td><td>500+ employees</td><td>£80k–£250k/yr</td><td>CTO / Head of Innovation</td></tr>
  <tr><td>Infrastructure Asset Owners</td><td>100–500 employees</td><td>£25k–£80k/yr</td><td>Operations Director</td></tr>
  <tr><td>Engineering SMEs</td><td>10–100 employees</td><td>£6k–£25k/yr</td><td>Managing Director</td></tr>
</table>

<!-- SECTION 5: COMPETITORS -->
<h2><span class="section-number">5</span>Competitive Landscape</h2>
<table>
  <tr><th>Competitor</th><th>Strengths</th><th>Weaknesses</th><th>Our Advantage</th></tr>
  <tr><td>Bentley Systems / Aveva</td><td>Brand, installed base, enterprise integrations</td><td>High cost, slow to innovate, complex UX</td><td>AI-native, faster deployment, 60% lower TCO</td></tr>
  <tr><td>Siemens Digital Industries</td><td>Hardware-software ecosystem, global reach</td><td>Locked to Siemens hardware, rigid pricing</td><td>Hardware-agnostic, open API, flexible SaaS pricing</td></tr>
  <tr><td>Niche UK Startups</td><td>Agility, focused product</td><td>Limited funding, narrow feature set</td><td>Broader platform, AI-first, backed by live data</td></tr>
</table>
<div class="callout"><strong>Competitive Moat:</strong> Proprietary ${industry}-trained AI models · Real-time data integration across 12+ engineering platforms · UK regulatory compliance built-in · Rapid 30-day deployment vs. 6-12 months for incumbents</div>

<div class="page-break"></div>

<!-- SECTION 6: BUSINESS MODEL -->
<h2><span class="section-number">6</span>Business Model</h2>
<table>
  <tr><th>Revenue Stream</th><th>Model</th><th>Price Point</th><th>Target</th></tr>
  <tr><td>Platform Subscription</td><td>Annual SaaS</td><td>£6k–£180k/yr</td><td>All tiers</td></tr>
  <tr><td>Implementation & Onboarding</td><td>One-off professional services</td><td>£5k–£40k</td><td>Enterprise</td></tr>
  <tr><td>AI Model Licensing</td><td>Per-seat or API call volume</td><td>£0.002–£0.05/call</td><td>Developer / API tier</td></tr>
  <tr><td>Data & Insights Reports</td><td>Subscription add-on</td><td>£2k–£12k/yr</td><td>SME + Enterprise</td></tr>
  <tr><td>Training & Certification</td><td>Per-course / cohort</td><td>£500–£3k/person</td><td>All tiers</td></tr>
</table>
<p><strong>Unit Economics:</strong> LTV/CAC ratio target: 4:1+ · Gross margin target: 72% · Payback period: 14 months · NRR target: 115%</p>

<!-- SECTION 7: DEVELOPMENT COST -->
<h2><span class="section-number">7</span>Estimated Development Cost</h2>
<table>
  <tr><th>Phase</th><th>Activities</th><th>Duration</th><th>Cost</th></tr>
  <tr><td>Research & Validation</td><td>Market validation, technical feasibility, IP assessment</td><td>8 weeks</td><td>£${Math.round(budget * 0.12).toLocaleString()}</td></tr>
  <tr><td>MVP Development</td><td>Core platform, AI engine, initial integrations</td><td>16 weeks</td><td>£${Math.round(budget * 0.38).toLocaleString()}</td></tr>
  <tr><td>Pilot Programme</td><td>5 design-partner clients, iterative refinement</td><td>10 weeks</td><td>£${Math.round(budget * 0.22).toLocaleString()}</td></tr>
  <tr><td>Commercial Launch</td><td>GTM, sales enablement, marketing, compliance</td><td>8 weeks</td><td>£${Math.round(budget * 0.18).toLocaleString()}</td></tr>
  <tr><td>Contingency (10%)</td><td>Buffer for scope changes and delays</td><td>—</td><td>£${Math.round(budget * 0.10).toLocaleString()}</td></tr>
  <tr><td><strong>Total</strong></td><td></td><td><strong>${innovation.timeline || '6 months'}</strong></td><td><strong>£${budget.toLocaleString()}</strong></td></tr>
</table>

<div class="page-break"></div>

<!-- SECTION 8: REVENUE POTENTIAL -->
<h2><span class="section-number">8</span>Revenue Potential (5-Year)</h2>
<table>
  <tr><th>Year</th><th>Customers</th><th>ARR</th><th>Services</th><th>Total Revenue</th><th>EBITDA</th><th>Margin</th></tr>
  <tr><td>2025</td><td>12</td><td>£${Math.round(roi * 0.18).toLocaleString()}</td><td>£${Math.round(roi * 0.07).toLocaleString()}</td><td>£${Math.round(roi * 0.25).toLocaleString()}</td><td>£${Math.round(roi * 0.06).toLocaleString()}</td><td>22%</td></tr>
  <tr><td>2026</td><td>38</td><td>£${Math.round(roi * 0.52).toLocaleString()}</td><td>£${Math.round(roi * 0.14).toLocaleString()}</td><td>£${Math.round(roi * 0.66).toLocaleString()}</td><td>£${Math.round(roi * 0.22).toLocaleString()}</td><td>33%</td></tr>
  <tr><td>2027</td><td>95</td><td>£${Math.round(roi * 1.10).toLocaleString()}</td><td>£${Math.round(roi * 0.20).toLocaleString()}</td><td>£${Math.round(roi * 1.30).toLocaleString()}</td><td>£${Math.round(roi * 0.55).toLocaleString()}</td><td>42%</td></tr>
  <tr><td>2028</td><td>200</td><td>£${Math.round(roi * 2.20).toLocaleString()}</td><td>£${Math.round(roi * 0.30).toLocaleString()}</td><td>£${Math.round(roi * 2.50).toLocaleString()}</td><td>£${Math.round(roi * 1.25).toLocaleString()}</td><td>50%</td></tr>
  <tr><td>2029</td><td>380</td><td>£${Math.round(roi * 3.90).toLocaleString()}</td><td>£${Math.round(roi * 0.40).toLocaleString()}</td><td>£${Math.round(roi * 4.30).toLocaleString()}</td><td>£${Math.round(roi * 2.40).toLocaleString()}</td><td>56%</td></tr>
</table>

<!-- SECTION 9: FEASIBILITY ANALYSIS -->
<h2><span class="section-number">9</span>Feasibility Analysis</h2>
<table>
  <tr><th>Dimension</th><th>Assessment</th><th>Rating</th><th>Notes</th></tr>
  <tr><td>Technical Feasibility</td><td>Core technologies proven at scale. AI stack mature.</td><td>✅ High</td><td>No unproven dependencies</td></tr>
  <tr><td>Commercial Feasibility</td><td>Validated demand. ${conf}% confidence from market signals.</td><td>✅ High</td><td>Design partners identified</td></tr>
  <tr><td>Regulatory Compliance</td><td>GDPR, UK DSIT AI framework, HSE where applicable.</td><td>✅ Compliant</td><td>Legal review Q1</td></tr>
  <tr><td>Team Capability</td><td>Cross-functional AI + engineering expertise in place.</td><td>✅ Strong</td><td>2 hires planned</td></tr>
  <tr><td>Funding Requirement</td><td>£${budget.toLocaleString()} — achievable via grants + angel / seed round.</td><td>⚠️ Moderate</td><td>Innovate UK eligible</td></tr>
  <tr><td>Time to Market</td><td>${innovation.timeline || '6 months'} to MVP. First revenue in 3–4 months post-launch.</td><td>✅ Realistic</td><td>Phased rollout</td></tr>
</table>
<div class="callout"><strong>Overall Feasibility Score: ${health}/100</strong> — This innovation demonstrates strong commercial viability and technical readiness. Recommend proceeding to funded MVP phase.</div>

<div class="page-break"></div>

<!-- SECTION 10: RISKS -->
<h2><span class="section-number">10</span>Risk Assessment & Mitigation</h2>
<table>
  <tr><th>Risk Category</th><th>Specific Risk</th><th>Probability</th><th>Impact</th><th>Mitigation Strategy</th></tr>
  <tr><td>Technical</td><td>AI model performance below target</td><td>Medium</td><td>High</td><td>Phased validation, fallback to rules-based engine, continuous retraining pipeline</td></tr>
  <tr><td>Market</td><td>Slower enterprise sales cycles</td><td>Medium</td><td>Medium</td><td>Target SME segment first for fast revenue; land-and-expand into enterprise</td></tr>
  <tr><td>Regulatory</td><td>AI Act (EU) compliance requirements</td><td>Low</td><td>High</td><td>Appoint AI compliance officer; align product to high-risk AI category requirements from day one</td></tr>
  <tr><td>Competition</td><td>Large player launches competing feature</td><td>Medium</td><td>Medium</td><td>Speed to market; deepen ${industry}-specific moat; establish switching costs via integrations</td></tr>
  <tr><td>Funding</td><td>Seed round delayed</td><td>Low</td><td>High</td><td>Innovate UK grant pipeline active; revenue-based financing as bridge; lean team model</td></tr>
  <tr><td>Talent</td><td>Key engineering hire scarcity</td><td>Medium</td><td>Medium</td><td>Remote-first hiring, university partnerships, competitive equity package</td></tr>
</table>
${innovation.risks ? `<div class="risk-callout"><strong>Project-Specific Risks Noted:</strong> ${innovation.risks}</div>` : ''}

<!-- SECTION 11: IMPLEMENTATION ROADMAP -->
<h2><span class="section-number">11</span>Implementation Roadmap</h2>
<table>
  <tr><th>Phase</th><th>Timeline</th><th>Key Deliverables</th><th>Success Metrics</th><th>Budget</th></tr>
  <tr><td><strong>Phase 1</strong><br>Research & Validate</td><td>Weeks 1–6</td><td>Market validation interviews (30+), technical spike, IP review, design-partner LOIs</td><td>5 signed LOIs · Feasibility confirmed · Patent landscape clear</td><td>£${Math.round(budget * 0.12).toLocaleString()}</td></tr>
  <tr><td><strong>Phase 2</strong><br>MVP Build</td><td>Weeks 7–22</td><td>Core platform, AI engine v1, 3 key integrations, security audit, design-partner onboarding</td><td>MVP live · 5 active pilot users · NPS ≥ 40</td><td>£${Math.round(budget * 0.38).toLocaleString()}</td></tr>
  <tr><td><strong>Phase 3</strong><br>Pilot & Iterate</td><td>Weeks 23–32</td><td>Full pilot with 5 clients, product iteration, pricing validation, case studies</td><td>3 paid conversions · ARR ≥ £${Math.round(roi * 0.05).toLocaleString()} · Churn < 5%</td><td>£${Math.round(budget * 0.22).toLocaleString()}</td></tr>
  <tr><td><strong>Phase 4</strong><br>Commercial Launch</td><td>Weeks 33–42</td><td>Full GTM, sales playbook, partner channel, PR, Series A preparation</td><td>20 paying customers · ARR ≥ £${Math.round(roi * 0.18).toLocaleString()} · Series A deck ready</td><td>£${Math.round(budget * 0.18).toLocaleString()}</td></tr>
  <tr><td><strong>Phase 5</strong><br>Scale</td><td>Year 2+</td><td>EU market expansion, enterprise tier, platform partnerships, M&A opportunities</td><td>100 customers · International revenue 30% · EBITDA positive</td><td>Series A funding</td></tr>
</table>

<div class="callout">
  <strong>Funding Sources:</strong> Innovate UK Smart Grant (up to £500k) · UKRI Strength in Places · Angel investment · Revenue-based financing · Series A (target: £2–4M at £8–12M valuation)
</div>

<div class="footer">
  <p>© JobEng AI Innovation Hub &nbsp;·&nbsp; Confidential — Not for Distribution &nbsp;·&nbsp; Generated ${today}</p>
  <p>${innovation.title} &nbsp;·&nbsp; 11-Section Engineering Business Plan &nbsp;·&nbsp; Powered by Claude AI</p>
</div>
</body></html>`;
  };

  const generateBusinessPlanHTML = (plan) => `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><style>
body{font-family:Helvetica,Arial,sans-serif;padding:40px;color:#0f172a;line-height:1.6}
h1{color:#0e4e96;font-size:28px}h2{color:#0e4e96;font-size:22px;margin-top:30px}
.section{margin-bottom:25px;padding:20px;background:#f8fafc;border-radius:12px;border-left:5px solid #0e4e96}
.footer{margin-top:40px;padding-top:20px;border-top:1px solid #e2e8f0;text-align:center;font-size:12px;color:#475569}
</style></head><body>
<h1>${plan.title || 'Business Plan'}</h1>
<p style="color:#0e4e96;font-size:18px">${plan.company_name || ''}</p>
<div class="section"><h2>📌 Executive Summary</h2><p>${plan.executive_summary || 'N/A'}</p></div>
<div class="section"><h2>📊 Market Analysis</h2><p>${plan.market_analysis || 'N/A'}</p></div>
<div class="section"><h2>🛠️ Products & Services</h2><p>${plan.products_services || plan.solution_description || 'N/A'}</p></div>
<div class="section"><h2>📢 Marketing Strategy</h2><p>${plan.marketing_strategy || 'N/A'}</p></div>
<div class="section"><h2>💰 Financial Projections</h2><p>${typeof plan.financial_projections === 'string' ? plan.financial_projections : JSON.stringify(plan.financial_projections)}</p></div>
<div class="section"><h2>💵 Funding Requirements</h2><p>£${(parseInt(plan.funding_requirements) || 0).toLocaleString()}</p></div>
<div class="footer"><p>Generated by JobEng AI Innovation Hub · ${new Date().toLocaleDateString()}</p></div>
</body></html>`;

  const handleExportHTML = async (innovation) => {
    setExportLoading(true);
    try {
      const html = generateExportHTML(innovation);
      const { uri } = await Print.printToFileAsync({ html });
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri.replace('.pdf', '.html'), { mimeType: 'text/html', dialogTitle: `Export ${innovation.title}` });
      else Alert.alert('❌', 'Sharing not available on this device');
    } catch (e) { Alert.alert('❌ Error', e.message); }
    finally { setExportLoading(false); }
  };

  const handleExportPDF = async (innovation) => {
    setPdfLoading(true);
    try {
      const { uri } = await Print.printToFileAsync({ html: generateExportHTML(innovation) });
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri, { mimeType: 'application/pdf', UTI: 'com.adobe.pdf' });
    } catch (e) { Alert.alert('❌ Error', e.message); }
    finally { setPdfLoading(false); }
  };

  const handlePrintPDF = async (innovation) => {
    setPrintLoading(true);
    try { await Print.printAsync({ html: generateExportHTML(innovation) }); }
    catch (e) { Alert.alert('❌ Error', e.message); }
    finally { setPrintLoading(false); }
  };

  const handleExportCSV = async () => {
    const rows = ['Title,Stage,Impact,ROI,Budget,Progress,Confidence,Industry,Health,Tags'];
    getInnovations().forEach(i => rows.push(`"${i.title}","${i.stage}","${i.impact}",${i.estimated_roi || 0},${i.budget || 0},${i.progress || 0},${i.confidence || 0},"${i.industry || ''}",${i.health || 0},"${(i.tags || []).join(';')}"`));
    const csv = rows.join('\n');
    await Share.share({ message: csv, title: 'Innovation Pipeline Export' });
  };

  const handleShareInnovation = async (innovation) => {
    try { await Share.share({ message: `Innovation: ${innovation.title}\n\n${innovation.description}\n\nROI: £${(innovation.estimated_roi || 0).toLocaleString()} · Health: ${innovation.health || 0}%`, title: innovation.title }); }
    catch (e) { console.error(e); }
  };

  const handleGenerateDetailedBusinessPlan = async (innovation) => {
    setBusinessPlanLoading(true);
    try {
      setDetailedBPHtml(generateDetailedBusinessPlanHTML(innovation));
      setDetailedBPTitle(innovation.title);
      setShowDetailedBusinessPlanModal(true);
    } catch (e) { Alert.alert('❌ Error', e.message); }
    finally { setBusinessPlanLoading(false); }
  };

  const exportDetailedBPPDF = async () => {
    setPdfLoading(true);
    try { const { uri } = await Print.printToFileAsync({ html: detailedBPHtml }); if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri, { mimeType: 'application/pdf' }); }
    catch (e) { Alert.alert('❌ Error', e.message); }
    finally { setPdfLoading(false); }
  };

  // ── KPIs ─────────────────────────────────────────────────────────────────
  const getKPIs = () => {
    const all = getInnovations();
    const active = all.filter(i => !['completed', 'archived'].includes(i.stage)).length;
    const totalROI = all.reduce((s, i) => s + (i.estimated_roi || 0), 0);
    const avgHealth = all.length ? Math.round(all.reduce((s, i) => s + (i.health || 0), 0) / all.length) : 0;
    const aiCount = all.filter(i => i.ml_generated).length;
    return [
      { id: 'k1', label: 'Active',    value: active.toString(),                     icon: 'rocket-outline',    color: BRAND.blue,    trend: '+12%' },
      { id: 'k2', label: 'Total ROI', value: `£${(totalROI / 1000).toFixed(0)}k`,   icon: 'trending-up-outline',color: BRAND.success, trend: '+22%' },
      { id: 'k3', label: 'Avg Health',value: `${avgHealth}%`,                        icon: 'heart-outline',     color: BRAND.warning, trend: '+5%' },
      { id: 'k4', label: 'AI Ideas',  value: aiCount.toString(),                     icon: 'school-outline',    color: BRAND.mint,    trend: `+${aiCount}` },
    ];
  };


  // ────────────────────────────────────────────────────────────────────────
  // RENDER: INNOVATION CARD
  // ────────────────────────────────────────────────────────────────────────
  const renderInnovationCard = (innovation) => {
    const typeInfo  = INNOVATION_TYPES.find(t => t.id === innovation.type) || INNOVATION_TYPES[0];
    const stageInfo = INNOVATION_STAGES.find(s => s.id === innovation.stage) || INNOVATION_STAGES[0];
    const impactColor = getImpactColor(innovation.impact);
    return (
      <TouchableOpacity key={innovation.id} style={styles.card} onPress={() => { setSelectedInnovation(innovation); setShowDetailModal(true); }} activeOpacity={0.75}>
        {/* Top row */}
        <View style={styles.cardTop}>
          <View style={[styles.typeIcon, { backgroundColor: typeInfo.color + '18' }]}>
            <Ionicons name={typeInfo.icon} size={16} color={typeInfo.color} />
          </View>
          <Text style={styles.cardTitle} numberOfLines={1} numberOfLines={1}>{innovation.title}
            {innovation.ml_generated && <Text style={[styles.mlBadge, { color: BRAND.claude }]}> [AI]</Text>}
          </Text>
          {/* ML Score badge */}
          {mlScores[innovation.id] && (
            <TouchableOpacity onPress={() => navigateToML('predictions', innovation)}
              style={{ flexDirection:'row', alignItems:'center', gap:3, backgroundColor:mlScores[innovation.id].color+'18',
                       borderRadius:6, paddingHorizontal:7, paddingVertical:3, marginLeft:4 }}>
              <MaterialCommunityIcons name="brain" size={11} color={mlScores[innovation.id].color} />
              <Text style={{ fontSize:10, fontWeight:'800', color:mlScores[innovation.id].color }}>
                {mlScores[innovation.id].score}%
              </Text>
            </TouchableOpacity>
          )}
          {mlScoringId === innovation.id && (
            <ActivityIndicator size="small" color={BRAND.purple} style={{ marginLeft:4 }} />
          )}
          <View style={[styles.impactBadge, { backgroundColor: impactColor + '18' }]}>
            <Text style={[styles.impactText, { color: impactColor }]}>{innovation.impact?.toUpperCase()}</Text>
          </View>
          <TouchableOpacity onPress={() => toggleBookmark(innovation.id)} style={{ padding: 4 }}>
            <Ionicons name={innovation.bookmarked ? 'bookmark' : 'bookmark-outline'} size={16} color={innovation.bookmarked ? BRAND.canary : BRAND.sub} />
          </TouchableOpacity>
        </View>

        {/* Meta pills */}
        <View style={styles.metaRow}>
          <View style={styles.metaPill}>
            <Ionicons name="person-outline" size={12} color={BRAND.sub} />
            <Text style={styles.metaText}>{innovation.owner?.name || 'Unassigned'}</Text>
          </View>
          <View style={styles.metaPill}>
            <Ionicons name="cash-outline" size={12} color={BRAND.success} />
            <Text style={[styles.metaText, { color: BRAND.success, fontWeight: '700' }]}>£{(innovation.estimated_roi || 0).toLocaleString()}</Text>
          </View>
          {innovation.industry && (
            <View style={[styles.metaPill, { backgroundColor: BRAND.mint + '10', borderColor: BRAND.mint + '30' }]}>
              <Ionicons name="hardware-chip-outline" size={12} color={BRAND.mint} />
              <Text style={[styles.metaText, { color: BRAND.mint }]}>{innovation.industry}</Text>
            </View>
          )}
        </View>

        {/* Description */}
        <Text style={styles.cardSummary} numberOfLines={2}>{innovation.description}</Text>

        {/* Stage + Progress */}
        <View style={styles.cardMiddle}>
          <View style={[styles.stageBadge, { backgroundColor: stageInfo.color + '18', borderColor: stageInfo.color }]}>
            <Ionicons name={stageInfo.icon} size={11} color={stageInfo.color} />
            <Text style={[styles.stageText, { color: stageInfo.color }]}>{stageInfo.name}</Text>
          </View>
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { width: `${innovation.progress || 0}%`, backgroundColor: (innovation.progress || 0) > 70 ? BRAND.success : (innovation.progress || 0) > 40 ? BRAND.warning : BRAND.info }]} />
            </View>
            <Text style={styles.progressText}>{innovation.progress || 0}%</Text>
          </View>
          <HealthRing score={innovation.health || 0} />
        </View>

        {/* Tags */}
        {(innovation.tags || []).length > 0 && (
          <View style={styles.tagsRow}>
            {innovation.tags.slice(0, 3).map((t, i) => <View key={i} style={styles.tag}><Text style={styles.tagText}>{t}</Text></View>)}
            {innovation.tags.length > 3 && <Text style={styles.moreTags}>+{innovation.tags.length - 3}</Text>}
          </View>
        )}

        {/* Card actions */}
        <View style={styles.cardBottom}>
          <TouchableOpacity style={styles.cardActionBtn} onPress={() => voteInnovation(innovation.id)}>
            <Ionicons name="thumbs-up-outline" size={14} color={BRAND.blue} />
            <Text style={[styles.cardActionText, { color: BRAND.blue }]}>{innovation.votes || 0}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cardActionBtn} onPress={() => advanceStage(innovation)}>
            <Ionicons name="arrow-forward-circle-outline" size={14} color={BRAND.mint} />
            <Text style={[styles.cardActionText, { color: BRAND.mint }]}>Advance</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cardActionBtn} onPress={() => { setSelectedInnovation(innovation); setShowNotesModal(true); }}>
            <Ionicons name="document-text-outline" size={14} color={BRAND.purple} />
            <Text style={[styles.cardActionText, { color: BRAND.purple }]}>{(innovation.notes || []).length} Notes</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cardActionBtn} onPress={() => handleAnalyzeInnovation(innovation)} disabled={aiProcessing}>
            <MaterialCommunityIcons name="brain" size={14} color={BRAND.success} />
            <Text style={[styles.cardActionText, { color: BRAND.success }]}>Analyse</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cardActionBtn}
            onPress={() => navigateToML('predictions', innovation)}>
            <MaterialCommunityIcons name="chart-line" size={14} color="#667eea" />
            <Text style={[styles.cardActionText, { color: '#667eea' }]}>ML</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cardActionBtn} onPress={() => runMLScore(innovation)}
            disabled={mlScoringId === innovation.id}>
            <MaterialCommunityIcons name="star-shooting-outline" size={14} color={BRAND.purple} />
            <Text style={[styles.cardActionText, { color: BRAND.purple }]}>Score</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.cardActionBtn} onPress={() => deleteInnovation(innovation.id)}>
            <Ionicons name="trash-outline" size={14} color={BRAND.danger} />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: KANBAN VIEW
  // ────────────────────────────────────────────────────────────────────────
  const renderKanban = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingHorizontal: 16 }}>
      {INNOVATION_STAGES.slice(0, 5).map(stage => {
        const items = getFilteredInnovations().filter(i => i.stage === stage.id);
        return (
          <View key={stage.id} style={styles.kanbanColumn}>
            <View style={[styles.kanbanHeader, { borderColor: stage.color }]}>
              <Ionicons name={stage.icon} size={14} color={stage.color} />
              <Text style={[styles.kanbanHeaderText, { color: stage.color }]}>{stage.name}</Text>
              <View style={[styles.kanbanCount, { backgroundColor: stage.color }]}>
                <Text style={styles.kanbanCountText}>{items.length}</Text>
              </View>
            </View>
            <ScrollView nestedScrollEnabled showsVerticalScrollIndicator={false} style={{ maxHeight: height * 0.55 }}>
              {items.length === 0 ? (
                <View style={styles.kanbanEmpty}>
                  <Ionicons name={stage.icon} size={24} color={BRAND.line} />
                  <Text style={styles.kanbanEmptyText}>No items</Text>
                </View>
              ) : items.map(inn => (
                <TouchableOpacity key={inn.id} style={[styles.kanbanCard, { borderLeftColor: stage.color }]}
                  onPress={() => { setSelectedInnovation(inn); setShowDetailModal(true); }}>
                  <Text style={styles.kanbanCardTitle} numberOfLines={2}>{inn.title}</Text>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                    <Text style={styles.kanbanCardRoi}>£{((inn.estimated_roi || 0) / 1000).toFixed(0)}k</Text>
                    <HealthRing score={inn.health || 0} />
                  </View>
                  <View style={styles.progressBar} style={{ height: 3, borderRadius: 2, backgroundColor: BRAND.line, marginTop: 6 }}>
                    <View style={{ height: 3, borderRadius: 2, width: `${inn.progress || 0}%`, backgroundColor: stage.color }} />
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        );
      })}
    </ScrollView>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: ANALYTICS VIEW
  // ────────────────────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const all = getInnovations();
    const byStage   = INNOVATION_STAGES.slice(0, 5).map(s => ({ label: s.name.slice(0, 3), value: all.filter(i => i.stage === s.id).length }));
    const byType    = INNOVATION_TYPES.slice(0, 5).map(t => ({ label: t.name.slice(0, 5), value: all.filter(i => i.type === t.id).length }));
    const byHealth  = [
      { label: '80+',  value: all.filter(i => (i.health || 0) >= 80).length },
      { label: '60-79',value: all.filter(i => (i.health || 0) >= 60 && (i.health || 0) < 80).length },
      { label: '<60',  value: all.filter(i => (i.health || 0) < 60).length },
    ];
    const totalROI  = all.reduce((s, i) => s + (i.estimated_roi || 0), 0);
    const totalBudget = all.reduce((s, i) => s + (i.budget || 0), 0);
    const avgConf   = all.length ? Math.round(all.reduce((s, i) => s + (i.confidence || 0), 0) / all.length) : 0;

    return (
      <View style={{ paddingHorizontal: 16, paddingBottom: 40 }}>
        {/* Summary cards */}
        <View style={styles.analyticsGrid}>
          {[
            { label: 'Total ROI', value: `£${(totalROI / 1000).toFixed(0)}k`, color: BRAND.success, icon: 'trending-up' },
            { label: 'Total Budget', value: `£${(totalBudget / 1000).toFixed(0)}k`, color: BRAND.blue, icon: 'wallet-outline' },
            { label: 'Avg Confidence', value: `${avgConf}%`, color: BRAND.warning, icon: 'analytics-outline' },
            { label: 'AI-Generated', value: all.filter(i => i.ml_generated).length, color: BRAND.claude, icon: 'school' },
          ].map((s, i) => (
            <View key={i} style={[styles.analyticsSummaryCard, { borderTopColor: s.color }]}>
              <Ionicons name={s.icon} size={18} color={s.color} />
              <Text style={[styles.analyticsSummaryValue, { color: s.color }]}>{s.value}</Text>
              <Text style={styles.analyticsSummaryLabel}>{s.label}</Text>
            </View>
          ))}
        </View>

        {/* Charts */}
        <View style={styles.chartCard}>
          <Text style={styles.chartTitle}>Innovations by Stage</Text>
          <MiniBarChart data={byStage} color={BRAND.blue} height={90} />
        </View>

        <View style={styles.chartCard}>
          <Text style={styles.chartTitle}>Innovations by Type</Text>
          <MiniBarChart data={byType} color={BRAND.mint} height={90} />
        </View>

        <View style={styles.chartCard}>
          <Text style={styles.chartTitle}>Health Score Distribution</Text>
          <MiniBarChart data={byHealth} color={BRAND.success} height={80} />
        </View>

        {/* Top 3 by ROI */}
        <View style={styles.chartCard}>
          <Text style={styles.chartTitle}>🏆 Top Innovations by ROI</Text>
          {[...all].sort((a, b) => (b.estimated_roi || 0) - (a.estimated_roi || 0)).slice(0, 3).map((inn, i) => (
            <View key={inn.id} style={styles.analyticsRankRow}>
              <View style={[styles.analyticsRank, { backgroundColor: [BRAND.canary, BRAND.sub, '#cd7f32'][i] }]}>
                <Text style={styles.analyticsRankText}>{i + 1}</Text>
              </View>
              <Text style={styles.analyticsRankTitle} numberOfLines={1}>{inn.title}</Text>
              <Text style={[styles.analyticsRankValue, { color: BRAND.success }]}>£{(inn.estimated_roi / 1000).toFixed(0)}k</Text>
            </View>
          ))}
        </View>

        {/* Export CSV */}
        <TouchableOpacity style={styles.exportCSVBtn} onPress={handleExportCSV}>
          <Ionicons name="download-outline" size={16} color={BRAND.blue} />
          <Text style={styles.exportCSVText}>Export All Data as CSV</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: DETAIL MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderDetailModal = () => {
    if (!selectedInnovation) return null;
    const inn = selectedInnovation;
    const impactColor = getImpactColor(inn.impact);
    const stageInfo = INNOVATION_STAGES.find(s => s.id === inn.stage) || INNOVATION_STAGES[0];

    return (
      <Modal visible={showDetailModal} transparent animationType="slide" onRequestClose={() => setShowDetailModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.detailContainer}>
            <LinearGradient colors={['#0e4e96', '#1a5a9c']} style={styles.detailHeader}>
              <View style={styles.detailHeaderTop}>
                <TouchableOpacity onPress={() => setShowDetailModal(false)} style={styles.detailCloseButton}>
                  <Ionicons name="close" size={24} color="#fff" />
                </TouchableOpacity>
                <Text style={styles.detailHeaderTitle}>Innovation Details</Text>
                <View style={styles.detailHeaderActions}>
                  <TouchableOpacity onPress={() => toggleBookmark(inn.id)} style={styles.detailHeaderButton}>
                    <Ionicons name={inn.bookmarked ? 'bookmark' : 'bookmark-outline'} size={20} color={inn.bookmarked ? BRAND.canary : '#fff'} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => handleShareInnovation(inn)} style={styles.detailHeaderButton}>
                    <Ionicons name="share-outline" size={20} color="#fff" />
                  </TouchableOpacity>
                </View>
              </View>
            </LinearGradient>

            <ScrollView style={styles.detailContent} showsVerticalScrollIndicator={false}>
              {/* Title & badges */}
              <View style={styles.detailTitleSection}>
                <Text style={styles.detailTitle}>{inn.title}</Text>
                <View style={styles.detailBadgeContainer}>
                  <View style={[styles.detailImpactBadge, { backgroundColor: impactColor + '20' }]}>
                    <Text style={[styles.detailImpactText, { color: impactColor }]}>{inn.impact?.toUpperCase()} IMPACT</Text>
                  </View>
                  <View style={[styles.detailStageBadge, { backgroundColor: stageInfo.color + '20' }]}>
                    <Ionicons name={stageInfo.icon} size={12} color={stageInfo.color} />
                    <Text style={[styles.detailStageText, { color: stageInfo.color }]}>{String(stageInfo.name ?? '').toUpperCase()}</Text>
                  </View>
                  {inn.industry && (
                    <View style={[styles.detailProviderBadge, { backgroundColor: BRAND.mint + '20' }]}>
                      <Text style={[styles.detailProviderText, { color: BRAND.mint }]}>{inn.industry}</Text>
                    </View>
                  )}
                  {inn.ml_generated && (
                    <View style={[styles.detailProviderBadge, { backgroundColor: BRAND.claude + '20' }]}>
                      <MaterialCommunityIcons name="brain" size={12} color={BRAND.claude} />
                      <Text style={[styles.detailProviderText, { color: BRAND.claude }]}>AI Generated</Text>
                    </View>
                  )}
                </View>
              </View>

              {/* ROI card */}
              <LinearGradient colors={['#10b981', '#059669']} style={styles.detailRoiCard} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <View>
                    <Text style={styles.detailRoiLabel}>Estimated ROI</Text>
                    <Text style={styles.detailRoiAmount}>£{(inn.estimated_roi || 0).toLocaleString()}</Text>
                    <Text style={styles.detailRoiSubtext}>Budget: £{(inn.budget || 0).toLocaleString()}</Text>
                  </View>
                  <HealthRing score={inn.health || 0} />
                </View>
              </LinearGradient>

              {/* Votes + actions row */}
              <View style={styles.detailActionsRow}>
                <TouchableOpacity style={styles.detailActionPill} onPress={() => voteInnovation(inn.id)}>
                  <Ionicons name="thumbs-up" size={14} color={BRAND.blue} />
                  <Text style={[styles.detailActionPillText, { color: BRAND.blue }]}>{inn.votes || 0} votes</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.detailActionPill} onPress={() => advanceStage(inn)}>
                  <Ionicons name="arrow-forward-circle" size={14} color={BRAND.mint} />
                  <Text style={[styles.detailActionPillText, { color: BRAND.mint }]}>Advance</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.detailActionPill} onPress={() => { setShowDetailModal(false); setShowROICalculator(true); }}>
                  <Ionicons name="calculator" size={14} color={BRAND.warning} />
                  <Text style={[styles.detailActionPillText, { color: BRAND.warning }]}>ROI Calc</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.detailActionPill} onPress={() => { setShowCompetitorModal(true); handleCompetitorAnalysis(inn); }}>
                  <MaterialCommunityIcons name="chart-bar" size={14} color={BRAND.purple} />
                  <Text style={[styles.detailActionPillText, { color: BRAND.purple }]}>Competitors</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.detailActionPill, { backgroundColor: '#0e4e96' + '12', borderColor: '#0e4e96' + '30', borderWidth: 1 }]} onPress={() => handleDeepSearch(inn)}>
                  <Ionicons name="search" size={14} color="#0e4e96" />
                  <Text style={[styles.detailActionPillText, { color: '#0e4e96', fontWeight: '800' }]}>Deep Search</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.detailActionPill, { backgroundColor: '#667eea18', borderColor: '#667eea40', borderWidth: 1 }]}
                  onPress={() => { setShowDetailModal(false); navigateToML('predictions', inn); }}>
                  <MaterialCommunityIcons name="chart-line" size={14} color="#667eea" />
                  <Text style={[styles.detailActionPillText, { color: '#667eea', fontWeight: '800' }]}>ML Predict</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.detailActionPill, { backgroundColor: BRAND.purple+'15', borderColor: BRAND.purple+'30', borderWidth: 1 }]}
                  onPress={() => runMLScore(inn)}>
                  <MaterialCommunityIcons name="star-shooting-outline" size={14} color={BRAND.purple} />
                  <Text style={[styles.detailActionPillText, { color: BRAND.purple, fontWeight: '800' }]}>
                    <Text>{mlScores[inn.id] ? `Score: ${mlScores[inn.id].score}%` : 'ML Score'}</Text>
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Description */}
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>📋 Description</Text>
                <Text style={styles.detailDescription}>{inn.description || 'No description provided.'}</Text>
              </View>

              {/* Metrics grid */}
              <View style={styles.detailMetricsGrid}>
                {[
                  { icon: 'pricetag-outline', label: 'Type',       value: inn.type?.toUpperCase() },
                  { icon: 'time-outline', label: 'Timeline',   value: inn.timeline || '3 months' },
                  { icon: 'trending-up', label: 'Confidence', value: `${inn.confidence || 85}%` },
                  { icon: 'bar-chart-outline', label: 'Progress', value: `${inn.progress || 0}%` },
                ].map((m, i) => (
                  <View key={i} style={styles.detailMetricCard}>
                    <Ionicons name={m.icon} size={18} color={BRAND.blue} />
                    <Text style={styles.detailMetricLabel}>{m.label}</Text>
                    <Text style={styles.detailMetricValue}>{m.value}</Text>
                  </View>
                ))}
              </View>

              {/* Tags */}
              {(inn.tags || []).length > 0 && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>🏷️ Tags</Text>
                  <View style={styles.detailTagsContainer}>
                    {inn.tags.map((tag, idx) => (
                      <View key={idx} style={[styles.detailTag, { backgroundColor: BRAND.blue + '10', borderColor: BRAND.blue + '30' }]}>
                        <Text style={[styles.detailTagText, { color: BRAND.blue }]}>{tag}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Success metrics */}
              {inn.success_metrics && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>📊 Success Metrics</Text>
                  <Text style={styles.detailMetricsText}>{inn.success_metrics}</Text>
                </View>
              )}

              {/* Risks */}
              {inn.risks && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>⚠️ Risks & Challenges</Text>
                  <Text style={styles.detailRisksText}>{inn.risks}</Text>
                </View>
              )}

              {/* Notes */}
              <View style={styles.detailSection}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <Text style={styles.detailSectionTitle}>📝 Notes ({(inn.notes || []).length})</Text>
                  <TouchableOpacity onPress={() => setShowNotesModal(true)} style={{ backgroundColor: BRAND.blue + '15', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 }}>
                    <Text style={{ fontSize: 12, color: BRAND.blue, fontWeight: '700' }}>+ Add Note</Text>
                  </TouchableOpacity>
                </View>
                {(inn.notes || []).slice(-2).map((n, i) => (
                  <View key={i} style={{ backgroundColor: BRAND.bg, borderRadius: 8, padding: 10, marginBottom: 6 }}>
                    <Text style={{ fontSize: 13, color: BRAND.text }}>{n.text}</Text>
                    <Text style={{ fontSize: 10, color: BRAND.sub, marginTop: 4 }}>{new Date(n.timestamp).toLocaleString('en-GB')}</Text>
                  </View>
                ))}
              </View>

              {/* Generate business plan */}
              <TouchableOpacity style={[styles.detailExportButton, { marginBottom: 12 }]} onPress={() => { setShowDetailModal(false); handleGenerateDetailedBusinessPlan(inn); }} disabled={businessPlanLoading}>
                <LinearGradient colors={['#8b5cf6', '#6d28d9']} style={styles.detailExportGradient}>
                  {businessPlanLoading ? <ActivityIndicator size="small" color="#fff" /> : (
                    <><Ionicons name="document-text" size={18} color="#fff" /><Text style={styles.detailExportButtonText}>Generate Full Business Plan (9+ pages)</Text></>
                  )}
                </LinearGradient>
              </TouchableOpacity>

              {/* Export options */}
              <View style={styles.detailExportContainer}>
                <Text style={styles.detailExportTitle}>Export Options</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity style={[styles.detailExportButton, { flex: 1 }]} onPress={() => handleExportHTML(inn)} disabled={exportLoading}>
                    <LinearGradient colors={['#ef4444', '#dc2626']} style={styles.detailExportGradient}>
                      {exportLoading ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="document" size={16} color="#fff" /><Text style={styles.detailExportButtonText}>HTML</Text></>}
                    </LinearGradient>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.detailExportButton, { flex: 1 }]} onPress={() => handleExportPDF(inn)} disabled={pdfLoading}>
                    <LinearGradient colors={['#0e4e96', '#1a5a9c']} style={styles.detailExportGradient}>
                      {pdfLoading ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="document-attach" size={16} color="#fff" /><Text style={styles.detailExportButtonText}>PDF</Text></>}
                    </LinearGradient>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.detailExportButton, { flex: 1 }]} onPress={() => handlePrintPDF(inn)} disabled={printLoading}>
                    <LinearGradient colors={['#4B5563', '#6B7280']} style={styles.detailExportGradient}>
                      {printLoading ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="print" size={16} color="#fff" /><Text style={styles.detailExportButtonText}>Print</Text></>}
                    </LinearGradient>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={{ height: 30 }} />
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: BUSINESS PLAN DETAIL MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderBusinessPlanDetailModal = () => {
    if (!selectedBusinessPlan) return null;
    const plan = selectedBusinessPlan;
    return (
      <Modal visible={showBusinessPlanDetailModal} transparent animationType="slide" onRequestClose={() => setShowBusinessPlanDetailModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.detailContainer}>
            <LinearGradient colors={['#8b5cf6', '#6d28d9']} style={styles.detailHeader}>
              <View style={styles.detailHeaderTop}>
                <TouchableOpacity onPress={() => setShowBusinessPlanDetailModal(false)} style={styles.detailCloseButton}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
                <Text style={styles.detailHeaderTitle}>Business Plan</Text>
                <TouchableOpacity onPress={() => handleShareInnovation({ title: plan.title, description: plan.executive_summary })} style={styles.detailHeaderButton}><Ionicons name="share-outline" size={20} color="#fff" /></TouchableOpacity>
              </View>
            </LinearGradient>
            <ScrollView style={styles.detailContent}>
              <Text style={styles.detailTitle}>{plan.innovation_title || plan.title || 'Business Plan'}</Text>
              {plan.company_name && <Text style={[styles.detailSubtitle, { color: BRAND.purple, marginBottom: 16 }]}>{plan.company_name}</Text>}
              {[
                { title: '📌 Executive Summary', key: 'executive_summary' },
                { title: '❗ Problem', key: 'problem_statement' },
                { title: '💡 Solution', key: 'solution_description' },
                { title: '📊 Market Analysis', key: 'market_analysis' },
                { title: '💰 Financial Projections', key: 'financial_projections' },
                { title: '📢 Marketing Strategy', key: 'marketing_strategy' },
              ].filter(s => plan[s.key]).map(s => (
                <View key={s.key} style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>{s.title}</Text>
                  <Text style={styles.detailDescription}>{typeof plan[s.key] === 'string' ? plan[s.key] : JSON.stringify(plan[s.key], null, 2)}</Text>
                </View>
              ))}
              {plan.market_opportunity && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>📊 Market Opportunity</Text>
                  <Text style={styles.detailDescription}>TAM: {plan.market_opportunity.tam}{'\n'}SAM: {plan.market_opportunity.sam}{'\n'}SOM: {plan.market_opportunity.som}{'\n'}Growth: {plan.market_opportunity.growth_rate}</Text>
                </View>
              )}
              <View style={styles.detailExportContainer}>
                <Text style={styles.detailExportTitle}>Export</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <TouchableOpacity style={[styles.detailExportButton, { flex: 1 }]} onPress={async () => { setPdfLoading(true); try { const { uri } = await Print.printToFileAsync({ html: generateBusinessPlanHTML(plan) }); if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri, { mimeType: 'application/pdf' }); } catch {} finally { setPdfLoading(false); }}} disabled={pdfLoading}>
                    <LinearGradient colors={['#0e4e96', '#1a5a9c']} style={styles.detailExportGradient}>
                      {pdfLoading ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="document-attach" size={16} color="#fff" /><Text style={styles.detailExportButtonText}>PDF</Text></>}
                    </LinearGradient>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.detailExportButton, { flex: 1 }]} onPress={async () => { setPrintLoading(true); try { await Print.printAsync({ html: generateBusinessPlanHTML(plan) }); } catch {} finally { setPrintLoading(false); }}} disabled={printLoading}>
                    <LinearGradient colors={['#4B5563', '#6B7280']} style={styles.detailExportGradient}>
                      {printLoading ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="print" size={16} color="#fff" /><Text style={styles.detailExportButtonText}>Print</Text></>}
                    </LinearGradient>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: DETAILED BUSINESS PLAN (WebView)
  // ────────────────────────────────────────────────────────────────────────
  const renderDetailedBusinessPlanModal = () => (
    <Modal visible={showDetailedBusinessPlanModal} transparent animationType="slide" onRequestClose={() => setShowDetailedBusinessPlanModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={[styles.detailContainer, { height: height * 0.92 }]}>
          <LinearGradient colors={['#8b5cf6', '#6d28d9']} style={styles.detailHeader}>
            <View style={styles.detailHeaderTop}>
              <TouchableOpacity onPress={() => setShowDetailedBusinessPlanModal(false)} style={styles.detailCloseButton}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
              <Text style={styles.detailHeaderTitle}>Full Business Plan</Text>
              <TouchableOpacity onPress={() => handleShareInnovation({ title: detailedBPTitle, description: 'Full business plan' })} style={styles.detailHeaderButton}><Ionicons name="share-outline" size={20} color="#fff" /></TouchableOpacity>
            </View>
          </LinearGradient>
          <View style={{ flex: 1, padding: 12 }}>
            <WebView originWhitelist={['*']} source={{ html: detailedBPHtml }} style={{ flex: 1, borderRadius: 12, marginBottom: 10 }} />
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <TouchableOpacity style={[styles.detailExportButton, { flex: 1 }]} onPress={exportDetailedBPPDF} disabled={pdfLoading}>
                <LinearGradient colors={['#0e4e96', '#1a5a9c']} style={styles.detailExportGradient}>
                  {pdfLoading ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="document-attach" size={16} color="#fff" /><Text style={styles.detailExportButtonText}>Export PDF</Text></>}
                </LinearGradient>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.detailExportButton, { flex: 1 }]} onPress={async () => { try { await Print.printAsync({ html: detailedBPHtml }); } catch {} }} disabled={printLoading}>
                <LinearGradient colors={['#4B5563', '#6B7280']} style={styles.detailExportGradient}>
                  <Ionicons name="print" size={16} color="#fff" /><Text style={styles.detailExportButtonText}>Print</Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );


  // ────────────────────────────────────────────────────────────────────────
  // RENDER: ROI CALCULATOR MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderROICalculator = () => (
    <Modal visible={showROICalculator} transparent animationType="slide" onRequestClose={() => setShowROICalculator(false)}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modalCard, { maxHeight: height * 0.85 }]}>
          <View style={styles.modalHeader}>
            <View><Text style={styles.modalTitle}>💰 ROI Calculator</Text><Text style={styles.modalSubtitle}>Risk-adjusted return analysis</Text></View>
            <TouchableOpacity onPress={() => setShowROICalculator(false)}><Ionicons name="close" size={24} color={BRAND.sub} /></TouchableOpacity>
          </View>
          <ScrollView style={styles.modalScroll}>
            {[
              { label: 'Total Investment (£)', key: 'investment', placeholder: '150000' },
              { label: 'Expected Annual Revenue (£)', key: 'revenue', placeholder: '300000' },
              { label: 'Time Horizon (Years)', key: 'timeYears', placeholder: '3' },
              { label: 'Risk Factor (%)', key: 'riskFactor', placeholder: '15' },
            ].map(f => (
              <View key={f.key} style={{ marginBottom: 14 }}>
                <Text style={styles.fieldLabel}>{f.label}</Text>
                <TextInput style={styles.input} value={roiInputs[f.key]} onChangeText={v => setRoiInputs(p => ({ ...p, [f.key]: v }))} placeholder={f.placeholder} keyboardType="numeric" placeholderTextColor="#94a3b8" />
              </View>
            ))}
            <TouchableOpacity onPress={calculateROI} style={styles.submitButton}>
              <LinearGradient colors={[BRAND.success, '#059669']} style={styles.submitGradient}>
                <Ionicons name="calculator" size={18} color="#fff" />
                <Text style={styles.submitText}>Calculate ROI</Text>
              </LinearGradient>
            </TouchableOpacity>
            {roiResult && (
              <View style={{ marginTop: 16 }}>
                <LinearGradient colors={['#10b981', '#059669']} style={{ borderRadius: 14, padding: 20, marginBottom: 16 }}>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, marginBottom: 4 }}>Net ROI</Text>
                  <Text style={{ color: '#fff', fontSize: 36, fontWeight: '900' }}>{roiResult.roiPercent}%</Text>
                </LinearGradient>
                {[
                  { label: 'Total Revenue (risk-adjusted)', value: `£${parseFloat(roiResult.riskAdjusted).toLocaleString('en-GB', { maximumFractionDigits: 0 })}` },
                  { label: 'Net Profit', value: `£${parseFloat(roiResult.netProfit).toLocaleString('en-GB', { maximumFractionDigits: 0 })}` },
                  { label: 'Break-even (Years)', value: `${roiResult.breakeven} year${roiResult.breakeven !== 1 ? 's' : ''}` },
                  { label: 'NPV (8% discount)', value: `£${parseFloat(roiResult.npv).toLocaleString('en-GB', { maximumFractionDigits: 0 })}` },
                ].map(r => (
                  <View key={r.label} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: BRAND.line }}>
                    <Text style={{ fontSize: 13, color: BRAND.sub }}>{r.label}</Text>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: BRAND.text }}>{r.value}</Text>
                  </View>
                ))}
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: NOTES MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderNotesModal = () => {
    if (!selectedInnovation) return null;
    const inn = selectedInnovation;
    return (
      <Modal visible={showNotesModal} transparent animationType="slide" onRequestClose={() => setShowNotesModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { maxHeight: height * 0.75 }]}>
            <View style={styles.modalHeader}>
              <View><Text style={styles.modalTitle}>📝 Notes</Text><Text style={styles.modalSubtitle}>{inn.title}</Text></View>
              <TouchableOpacity onPress={() => setShowNotesModal(false)}><Ionicons name="close" size={24} color={BRAND.sub} /></TouchableOpacity>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, paddingHorizontal: 16, marginBottom: 12 }}>
              <TextInput style={[styles.input, { flex: 1, marginBottom: 0 }]} value={newNote} onChangeText={setNewNote} placeholder="Add a note..." placeholderTextColor="#94a3b8" multiline />
              <TouchableOpacity onPress={() => addNote(inn.id)} style={{ backgroundColor: BRAND.blue, borderRadius: 10, padding: 12, justifyContent: 'center' }}>
                <Ionicons name="send" size={16} color="#fff" />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ flex: 1, paddingHorizontal: 16 }}>
              {(inn.notes || []).length === 0 ? (
                <View style={{ alignItems: 'center', padding: 30 }}>
                  <Ionicons name="document-text-outline" size={40} color={BRAND.line} />
                  <Text style={{ color: BRAND.sub, marginTop: 8 }}>No notes yet. Add your first note.</Text>
                </View>
              ) : [...(inn.notes || [])].reverse().map((n, i) => (
                <View key={i} style={{ backgroundColor: BRAND.bg, borderRadius: 10, padding: 12, marginBottom: 10, borderLeftWidth: 3, borderLeftColor: BRAND.blue }}>
                  <Text style={{ fontSize: 13, color: BRAND.text, lineHeight: 19 }}>{n.text}</Text>
                  <Text style={{ fontSize: 10, color: BRAND.sub, marginTop: 6 }}>{new Date(n.timestamp).toLocaleString('en-GB')}</Text>
                </View>
              ))}
              <View style={{ height: 20 }} />
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: COMPETITOR ANALYSIS MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderCompetitorModal = () => (
    <Modal visible={showCompetitorModal} transparent animationType="slide" onRequestClose={() => setShowCompetitorModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modalCard, { maxHeight: height * 0.65 }]}>
          <View style={styles.modalHeader}>
            <View><Text style={styles.modalTitle}>🔍 Competitor Analysis</Text><Text style={styles.modalSubtitle}>{selectedInnovation?.title}</Text></View>
            <TouchableOpacity onPress={() => setShowCompetitorModal(false)}><Ionicons name="close" size={24} color={BRAND.sub} /></TouchableOpacity>
          </View>
          <ScrollView style={{ paddingHorizontal: 16, paddingBottom: 20 }}>
            {competitorLoading ? (
              <View style={{ alignItems: 'center', padding: 30 }}>
                <ActivityIndicator size="large" color={BRAND.blue} />
                <Text style={{ color: BRAND.sub, marginTop: 12 }}>Analysing competitive landscape...</Text>
              </View>
            ) : (
              <View style={{ backgroundColor: BRAND.bg, borderRadius: 12, padding: 16, borderLeftWidth: 3, borderLeftColor: BRAND.purple }}>
                <Text style={{ fontSize: 13, color: BRAND.text, lineHeight: 22 }}>{competitorAnalysis || 'No analysis yet.'}</Text>
              </View>
            )}
            <View style={{ height: 20 }} />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: COMPARE MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderCompareModal = () => {
    const all = getInnovations();
    const renderSide = (inn) => !inn ? (
      <View style={{ flex: 1, alignItems: 'center', padding: 16 }}>
        <Ionicons name="add-circle-outline" size={32} color={BRAND.line} />
        <Text style={{ color: BRAND.sub, marginTop: 8 }}>Select innovation</Text>
      </View>
    ) : (
      <View style={{ flex: 1, padding: 12 }}>
        <Text style={{ fontSize: 13, fontWeight: '700', color: BRAND.text, marginBottom: 8 }} numberOfLines={2}>{inn.title}</Text>
        {[
          ['ROI', `£${(inn.estimated_roi || 0).toLocaleString()}`],
          ['Budget', `£${(inn.budget || 0).toLocaleString()}`],
          ['Health', `${inn.health || 0}%`],
          ['Progress', `${inn.progress || 0}%`],
          ['Confidence', `${inn.confidence || 0}%`],
          ['Stage', inn.stage],
          ['Impact', inn.impact],
          ['Industry', inn.industry || 'N/A'],
        ].map(([k, v]) => (
          <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: BRAND.line }}>
            <Text style={{ fontSize: 11, color: BRAND.sub }}>{k}</Text>
            <Text style={{ fontSize: 11, fontWeight: '700', color: BRAND.text }}>{v}</Text>
          </View>
        ))}
      </View>
    );

    return (
      <Modal visible={showCompareModal} transparent animationType="slide" onRequestClose={() => setShowCompareModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { maxHeight: height * 0.85 }]}>
            <View style={styles.modalHeader}>
              <View><Text style={styles.modalTitle}>⚖️ Compare Innovations</Text></View>
              <TouchableOpacity onPress={() => setShowCompareModal(false)}><Ionicons name="close" size={24} color={BRAND.sub} /></TouchableOpacity>
            </View>
            <ScrollView style={{ paddingHorizontal: 16 }}>
              {/* Select buttons */}
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                {[{ key: 'A', val: compareA }, { key: 'B', val: compareB }].map(({ key, val }) => (
                  <TouchableOpacity key={key} style={{ flex: 1, backgroundColor: BRAND.blue + '15', borderRadius: 10, padding: 10, borderWidth: 1, borderColor: BRAND.blue, alignItems: 'center' }}
                    onPress={() => setCompareSelectFor(key)}>
                    <Text style={{ fontSize: 12, color: BRAND.blue, fontWeight: '600' }}>{val ? val.title.slice(0, 20) + '...' : `Select ${key}`}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              {/* Side-by-side */}
              <View style={{ flexDirection: 'row', gap: 8, borderWidth: 1, borderColor: BRAND.line, borderRadius: 12, overflow: 'hidden' }}>
                {renderSide(compareA)}
                <View style={{ width: 1, backgroundColor: BRAND.line }} />
                {renderSide(compareB)}
              </View>
              {/* Pick list */}
              {compareSelectFor && (
                <View style={{ marginTop: 16 }}>
                  <Text style={{ fontSize: 13, fontWeight: '600', color: BRAND.text, marginBottom: 8 }}>Select innovation for {compareSelectFor}:</Text>
                  {all.map(inn => (
                    <TouchableOpacity key={inn.id} style={{ paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: BRAND.line }} onPress={() => {
                      if (compareSelectFor === 'A') setCompareA(inn); else setCompareB(inn);
                      setCompareSelectFor(null);
                    }}>
                      <Text style={{ fontSize: 13, color: BRAND.text }}>{inn.title}</Text>
                      <Text style={{ fontSize: 11, color: BRAND.sub }}>{inn.stage} · £{(inn.estimated_roi || 0).toLocaleString()}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: MARKET NEWS MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderNewsModal = () => {
    const TOPIC_COLORS = { all:'#0e4e96', ai:'#8b5cf6', infra:'#0891b2', energy:'#10b981', robotics:'#f59e0b', funding:'#ef4444' };

    return (
      <Modal visible={showNewsModal} transparent animationType="slide" onRequestClose={() => setShowNewsModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { maxHeight: height * 0.88 }]}>

            {/* ── Header ─────────────────────────────────────────────── */}
            <LinearGradient colors={['#0e4e96', '#19abb5']}
              style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 18, paddingBottom: 14 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
                    <Ionicons name="newspaper" size={21} color="#fff" />
                  </View>
                  <View>
                    <Text style={{ color: '#fff', fontSize: 16, fontWeight: '800' }}>Market Intelligence</Text>
                    <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
                      <Text>{newsError ? '⚠️ Offline — curated news' : newsLoading ? 'Fetching from The Guardian…' : `The Guardian · ${marketNews.length} articles`}</Text>
                    </Text>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
                  <TouchableOpacity onPress={() => loadFetchNews(newsTopic, 1, false)} disabled={newsLoading}
                    style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
                    {newsLoading ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="refresh" size={18} color="#fff" />}
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowNewsModal(false)}>
                    <Ionicons name="close-circle" size={28} color="rgba(255,255,255,0.8)" />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Topic filter tabs */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 14 }} contentContainerStyle={{ gap: 6 }}>
                {GUARDIAN_TOPICS.map(t => {
                  const active = newsTopic === t.id;
                  const col    = TOPIC_COLORS[t.id] || '#0e4e96';
                  return (
                    <TouchableOpacity key={t.id} onPress={() => handleNewsTopicChange(t.id)} disabled={newsLoading}
                      style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20,
                        backgroundColor: active ? '#fff' : 'rgba(255,255,255,0.15)',
                        borderWidth: 1, borderColor: active ? '#fff' : 'transparent' }}>
                      <Text style={{ fontSize: 12, fontWeight: '700', color: active ? col : 'rgba(255,255,255,0.8)' }}>
                        {t.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </LinearGradient>

            {/* ── Error banner ────────────────────────────────────────── */}
            {newsError && (
              <View style={{ backgroundColor: '#fef3c7', paddingHorizontal: 16, paddingVertical: 10, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Ionicons name="wifi-outline" size={15} color="#d97706" />
                <Text style={{ fontSize: 12, color: '#92400e', flex: 1 }}>
                  Could not reach The Guardian. Showing curated engineering news.
                </Text>
                <TouchableOpacity onPress={() => loadFetchNews(newsTopic, 1, false)}
                  style={{ backgroundColor: '#d97706', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 }}>
                  <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>Retry</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* ── Loading skeleton ─────────────────────────────────────── */}
            {newsLoading && marketNews.length === 0 ? (
              <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                <ActivityIndicator size="large" color={BRAND.blue} />
                <Text style={{ color: BRAND.sub, marginTop: 14, fontSize: 14 }}>Fetching latest engineering news…</Text>
                <Text style={{ color: BRAND.sub, fontSize: 12, marginTop: 6 }}>Powered by The Guardian API</Text>
              </View>
            ) : (
              <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 20 }} showsVerticalScrollIndicator={false}>
                {marketNews.map((n, i) => {
                  const col = TOPIC_COLORS[n.topic] || BRAND.blue;
                  return (
                    <TouchableOpacity key={n.id || i}
                      activeOpacity={n.url ? 0.75 : 1}
                      onPress={() => n.url && Linking.openURL(n.url).catch(() => {})}
                      style={{ backgroundColor: '#fff', borderRadius: 14, marginBottom: 12, overflow: 'hidden',
                        shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 3,
                        borderWidth: 1, borderColor: BRAND.line }}>

                      {/* Thumbnail */}
                      {n.imageUrl ? (
                        <View style={{ height: 160, backgroundColor: BRAND.line }}>
                          <Image source={{ uri: (n.imageUrl && n.imageUrl.startsWith('http')) ? n.imageUrl : undefined }} style={{ width: '100%', height: '100%' }} resizeMode="cover"
                            onError={() => {}} />
                          <View style={{ position: 'absolute', top: 10, left: 10 }}>
                            <View style={{ backgroundColor: col, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 }}>
                              <Text style={{ color: '#fff', fontSize: 10, fontWeight: '800', textTransform: 'uppercase' }}>{n.section}</Text>
                            </View>
                          </View>
                        </View>
                      ) : (
                        <View style={{ height: 6, backgroundColor: col }} />
                      )}

                      <View style={{ padding: 14 }}>
                        {/* Section + date row (no thumbnail) */}
                        {!n.imageUrl && (
                          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                            <View style={{ backgroundColor: col + '15', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }}>
                              <Text style={{ fontSize: 10, color: col, fontWeight: '800', textTransform: 'uppercase' }}>{n.section}</Text>
                            </View>
                            <Text style={{ fontSize: 10, color: BRAND.sub }}>
                              {new Date(n.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </Text>
                          </View>
                        )}

                        <Text style={{ fontSize: 14, fontWeight: '800', color: BRAND.text, lineHeight: 21, marginBottom: 6 }}>
                          {n.title}
                        </Text>

                        {!!n.snippet && (
                          <Text style={{ fontSize: 12, color: BRAND.sub, lineHeight: 18, marginBottom: 8 }} numberOfLines={3}>
                            {n.snippet}
                          </Text>
                        )}

                        {/* Footer row */}
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            {n.imageUrl && (
                              <Text style={{ fontSize: 10, color: BRAND.sub }}>
                                {new Date(n.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </Text>
                            )}
                            {n.byline ? (
                              <Text style={{ fontSize: 10, color: BRAND.sub }} numberOfLines={1}>· {n.byline}</Text>
                            ) : null}
                          </View>
                          {n.url ? (
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                              <Text style={{ fontSize: 11, color: col, fontWeight: '700' }}>Read</Text>
                              <Ionicons name="open-outline" size={12} color={col} />
                            </View>
                          ) : null}
                        </View>

                        {/* Tags */}
                        {(n.tags || []).length > 0 && (
                          <View style={{ flexDirection: 'row', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
                            {n.tags.slice(0, 3).map((tag, ti) => (
                              <View key={ti} style={{ backgroundColor: col + '10', borderRadius: 5, paddingHorizontal: 7, paddingVertical: 2 }}>
                                <Text style={{ fontSize: 10, color: col, fontWeight: '600' }}>{tag}</Text>
                              </View>
                            ))}
                          </View>
                        )}
                      </View>
                    </TouchableOpacity>
                  );
                })}

                {/* Load more */}
                {newsPage < newsTotalPages && (
                  <TouchableOpacity onPress={handleNewsLoadMore} disabled={newsLoadingMore}
                    style={{ paddingVertical: 14, borderRadius: 12, borderWidth: 1, borderColor: BRAND.blue + '40',
                      alignItems: 'center', marginBottom: 8, flexDirection: 'row', justifyContent: 'center', gap: 8 }}>
                    {newsLoadingMore
                      ? <><ActivityIndicator size="small" color={BRAND.blue} /><Text style={{ color: BRAND.blue, fontWeight: '600' }}>Loading…</Text></>
                      : <><Ionicons name="chevron-down" size={16} color={BRAND.blue} /><Text style={{ color: BRAND.blue, fontWeight: '700' }}>Load More Articles</Text></>}
                  </TouchableOpacity>
                )}

                {!newsLoading && marketNews.length === 0 && (
                  <View style={{ alignItems: 'center', padding: 30 }}>
                    <Ionicons name="newspaper-outline" size={40} color={BRAND.line} />
                    <Text style={{ color: BRAND.sub, marginTop: 10, fontWeight: '600' }}>No articles found</Text>
                    <TouchableOpacity onPress={() => loadFetchNews(newsTopic, 1, false)} style={{ marginTop: 12 }}>
                      <Text style={{ color: BRAND.blue, fontWeight: '700' }}>Try Again</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </ScrollView>
            )}

          </View>
        </View>
      </Modal>
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: ADD INNOVATION MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderAddModal = () => (
    <Modal visible={showAddModal} transparent animationType="slide" onRequestClose={() => setShowAddModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modalCard, { maxHeight: height * 0.92 }]}>
          <View style={styles.modalHeader}>
            <View><Text style={styles.modalTitle}>➕ New Innovation</Text></View>
            <TouchableOpacity onPress={() => setShowAddModal(false)}><Ionicons name="close" size={24} color={BRAND.sub} /></TouchableOpacity>
          </View>
          <ScrollView style={styles.modalScroll}>
            <Text style={styles.fieldLabel}>Title *</Text>
            <TextInput style={styles.input} value={form.title} onChangeText={v => setForm(p => ({ ...p, title: v }))} placeholder="Innovation title" placeholderTextColor="#94a3b8" />

            <Text style={styles.fieldLabel}>Description</Text>
            <TextInput style={[styles.input, styles.textarea]} value={form.description} onChangeText={v => setForm(p => ({ ...p, description: v }))} placeholder="Describe this innovation..." placeholderTextColor="#94a3b8" multiline numberOfLines={3} />

            <Text style={styles.fieldLabel}>Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
              {INNOVATION_TYPES.map(t => (
                <TouchableOpacity key={t.id} style={[styles.typeChip, form.type === t.id && { backgroundColor: t.color + '20', borderColor: t.color }]} onPress={() => setForm(p => ({ ...p, type: t.id }))}>
                  <Ionicons name={t.icon} size={14} color={form.type === t.id ? t.color : BRAND.sub} />
                  <Text style={[styles.typeChipText, form.type === t.id && { color: t.color }]}>{t.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <Text style={styles.fieldLabel}>Stage</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
              {INNOVATION_STAGES.slice(0, 5).map(s => (
                <TouchableOpacity key={s.id} style={[styles.stageOption, form.stage === s.id && { backgroundColor: s.color + '20', borderColor: s.color }]} onPress={() => setForm(p => ({ ...p, stage: s.id }))}>
                  <Text style={[styles.stageOptionText, form.stage === s.id && { color: s.color }]}>{s.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <Text style={styles.fieldLabel}>Impact Level</Text>
            <View style={styles.pickerRow}>
              {IMPACT_LEVELS.map(l => (
                <TouchableOpacity key={l.id} style={[styles.impactOption, form.impact === l.id && { backgroundColor: l.color + '20', borderColor: l.color }]} onPress={() => setForm(p => ({ ...p, impact: l.id }))}>
                  <Text style={[styles.impactOptionText, form.impact === l.id && { color: l.color }]}>{l.name}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.fieldLabel}>Industry</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
              {ENGINEERING_INDUSTRIES.map(ind => (
                <TouchableOpacity key={ind} style={[styles.typeChip, form.industry === ind && { backgroundColor: BRAND.mint + '20', borderColor: BRAND.mint }]} onPress={() => setForm(p => ({ ...p, industry: ind }))}>
                  <Text style={[styles.typeChipText, form.industry === ind && { color: BRAND.mint }]}>{ind}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.fieldLabel}>Estimated ROI (£)</Text>
                <TextInput style={styles.input} value={form.estimated_roi} onChangeText={v => setForm(p => ({ ...p, estimated_roi: v }))} placeholder="250000" keyboardType="numeric" placeholderTextColor="#94a3b8" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.fieldLabel}>Budget (£)</Text>
                <TextInput style={styles.input} value={form.budget} onChangeText={v => setForm(p => ({ ...p, budget: v }))} placeholder="80000" keyboardType="numeric" placeholderTextColor="#94a3b8" />
              </View>
            </View>

            <Text style={styles.fieldLabel}>Timeline</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
              {['1 month', '3 months', '6 months', '1 year', '2 years'].map(t => (
                <TouchableOpacity key={t} style={[styles.typeChip, form.timeline === t && { backgroundColor: BRAND.blue + '20', borderColor: BRAND.blue }]} onPress={() => setForm(p => ({ ...p, timeline: t }))}>
                  <Text style={[styles.typeChipText, form.timeline === t && { color: BRAND.blue }]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <Text style={styles.fieldLabel}>Tags (comma-separated)</Text>
            <TextInput style={styles.input} value={form.tags} onChangeText={v => setForm(p => ({ ...p, tags: v }))} placeholder="AI, automation, UK" placeholderTextColor="#94a3b8" />

            <Text style={styles.fieldLabel}>Success Metrics</Text>
            <TextInput style={[styles.input, styles.textarea]} value={form.success_metrics} onChangeText={v => setForm(p => ({ ...p, success_metrics: v }))} placeholder="KPIs and milestones..." placeholderTextColor="#94a3b8" multiline numberOfLines={2} />

            <Text style={styles.fieldLabel}>Risks & Challenges</Text>
            <TextInput style={[styles.input, styles.textarea]} value={form.risks} onChangeText={v => setForm(p => ({ ...p, risks: v }))} placeholder="Potential blockers..." placeholderTextColor="#94a3b8" multiline numberOfLines={2} />

            <Text style={styles.fieldLabel}>Assign Owner</Text>
            <View style={styles.assignPicker}>
              {getUsers().map(u => (
                <TouchableOpacity key={u.id} style={[styles.assignOption, form.owner_id === u.id && { backgroundColor: BRAND.blue + '20', borderColor: BRAND.blue }]} onPress={() => setForm(p => ({ ...p, owner_id: u.id }))}>
                  <Text style={[styles.assignOptionText, form.owner_id === u.id && { color: BRAND.blue, fontWeight: '600' }]}>{u.name}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <TouchableOpacity style={styles.submitButton} onPress={handleSubmitInnovation}>
              <LinearGradient colors={[BRAND.blue, BRAND.mint]} style={styles.submitGradient}>
                <Ionicons name="save" size={18} color="#fff" />
                <Text style={styles.submitText}>Save Innovation</Text>
              </LinearGradient>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: AI GENERATOR MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderAIGeneratorModal = () => (
    <Modal visible={showAIGenerator} transparent animationType="slide" onRequestClose={() => setShowAIGenerator(false)}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modalCard, { maxHeight: height * 0.92 }]}>
          <View style={styles.modalHeader}>
            <View><Text style={styles.modalTitle}>🤖 AI Innovation Generator</Text><Text style={styles.modalSubtitle}>Powered by Claude AI</Text></View>
            <TouchableOpacity onPress={() => setShowAIGenerator(false)}><Ionicons name="close" size={24} color={BRAND.sub} /></TouchableOpacity>
          </View>
          <ScrollView style={styles.modalScroll}>
            {/* Provider badge */}
            <View style={styles.aiGeneratorContainer}>
              <View style={[styles.providerBadge, { backgroundColor: BRAND.claude + '20', alignSelf: 'center', marginBottom: 16 }]}>
                <MaterialCommunityIcons name="brain" size={16} color={BRAND.claude} />
                <Text style={[styles.providerBadgeText, { color: BRAND.claude }]}>{activeAIProvider} · Active</Text>
              </View>
              <Text style={styles.aiGeneratorTitle}>Generate AI-Powered Innovations</Text>
              <Text style={styles.aiGeneratorDesc}>Select an engineering discipline and number of ideas. Claude AI will generate tailored innovation concepts with ROI projections.</Text>

              <View style={styles.generatorControls}>
                <Text style={styles.fieldLabel}>Engineering Industry</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
                  {ENGINEERING_INDUSTRIES.map(ind => (
                    <TouchableOpacity key={ind} style={[styles.industryChip, generationIndustry === ind && { backgroundColor: BRAND.blue + '20', borderColor: BRAND.blue }]} onPress={() => setGenerationIndustry(ind)}>
                      <Text style={[styles.industryChipText, generationIndustry === ind && { color: BRAND.blue, fontWeight: '700' }]}>{ind}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>

                <Text style={styles.fieldLabel}>Number of Ideas</Text>
                <View style={styles.countSelector}>
                  {[1, 2, 3, 5].map(n => (
                    <TouchableOpacity key={n} style={[styles.countChip, generationCount === n && { backgroundColor: BRAND.blue + '20', borderColor: BRAND.blue }]} onPress={() => setGenerationCount(n)}>
                      <Text style={[styles.countChipText, generationCount === n && { color: BRAND.blue, fontWeight: '700' }]}>{n}</Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <TouchableOpacity style={[styles.generateButton, aiProcessing && styles.generateButtonDisabled]} onPress={handleGenerateIdeas} disabled={aiProcessing}>
                  <LinearGradient colors={[BRAND.claude, BRAND.blue]} style={styles.generateGradient}>
                    {aiProcessing ? (
                      <><ActivityIndicator size="small" color="#fff" /><Text style={styles.generateButtonText}>Claude is thinking...</Text></>
                    ) : (
                      <><MaterialCommunityIcons name="brain" size={20} color="#fff" /><Text style={styles.generateButtonText}>Generate {generationCount} Ideas</Text></>
                    )}
                  </LinearGradient>
                </TouchableOpacity>
              </View>

              {aiGeneratedIdeas.length > 0 && (
                <View style={styles.generatedResults}>
                  <View style={styles.generatedResultsHeader}>
                    <Text style={styles.generatedResultsTitle}>✅ {aiGeneratedIdeas.length} Ideas Generated</Text>
                    <TouchableOpacity onPress={() => setAiGeneratedIdeas([])}><Ionicons name="close" size={20} color={BRAND.sub} /></TouchableOpacity>
                  </View>
                  {aiGeneratedIdeas.map(idea => (
                    <View key={idea.id} style={styles.generatedIdeaCard}>
                      <View style={styles.generatedIdeaHeader}>
                        <Text style={styles.generatedIdeaTitle} numberOfLines={2}>{idea.title}</Text>
                        <View style={[styles.confidenceBadge, { backgroundColor: idea.confidence > 90 ? BRAND.success + '20' : BRAND.warning + '20' }]}>
                          <Text style={[styles.confidenceText, { color: idea.confidence > 90 ? BRAND.success : BRAND.warning }]}>{idea.confidence}%</Text>
                        </View>
                      </View>
                      <Text style={styles.generatedIdeaDesc} numberOfLines={3}>{idea.description}</Text>
                      <View style={styles.generatedIdeaTags}>
                        {(idea.tags || []).slice(0, 3).map((tag, i) => (
                          <View key={i} style={[styles.generatedIdeaTag, { backgroundColor: BRAND.claude + '10', borderColor: BRAND.claude + '30' }]}>
                            <Text style={[styles.generatedIdeaTagText, { color: BRAND.claude }]}>{tag}</Text>
                          </View>
                        ))}
                      </View>
                      <View style={styles.generatedIdeaFooter}>
                        <View style={styles.generatedIdeaMeta}>
                          <Ionicons name="cash-outline" size={13} color={BRAND.success} />
                          <Text style={styles.generatedIdeaRoi}>£{(idea.estimated_roi || 0).toLocaleString()}</Text>
                          <Ionicons name="time-outline" size={13} color={BRAND.sub} />
                          <Text style={styles.generatedIdeaTimeline}>{idea.timeline}</Text>
                        </View>
                        <View style={styles.generatedIdeaActions}>
                          <TouchableOpacity style={[styles.addIdeaButton, { backgroundColor: BRAND.success }]} onPress={() => handleAddAIIdea(idea)}>
                            <Text style={styles.addIdeaButtonText}>Add</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.rejectIdeaButton} onPress={() => handleRejectAIIdea(idea.id)}>
                            <Ionicons name="close" size={18} color={BRAND.danger} />
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: AI CHAT MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderAIChatModal = () => (
    <Modal visible={showAIChat} transparent animationType="slide" onRequestClose={() => setShowAIChat(false)}>
      <View style={styles.chatModalOverlay}>
        <View style={styles.chatModal}>

          {/* Header */}
          <LinearGradient colors={[BRAND.claude, '#c87941']}
            style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Animated.View style={[{ width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }, { opacity: aiPulse }]}>
                <MaterialCommunityIcons name="brain" size={20} color="#fff" />
              </Animated.View>
              <View>
                <Text style={{ color: '#fff', fontSize: 16, fontWeight: '800' }}>AI Innovation Advisor</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                  <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: aiProcessing ? '#fdb913' : '#10b981' }} />
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                    <Text>{aiProcessing ? 'Thinking…' : `${activeAIProvider} · Ready`}</Text>
                  </Text>
                </View>
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
              {chatMessages.length > 0 && (
                <TouchableOpacity onPress={() => { setChatMessages([]); setStreamingMessage(''); }}
                  style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
                  <Ionicons name="trash-outline" size={16} color="#fff" />
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={() => setShowAIChat(false)}>
                <Ionicons name="close-circle" size={28} color="rgba(255,255,255,0.8)" />
              </TouchableOpacity>
            </View>
          </LinearGradient>

          {/* Messages */}
          <ScrollView style={styles.chatMessages} ref={chatScrollRef}
            onContentSizeChange={() => chatScrollRef.current?.scrollToEnd({ animated: true })}>
            {chatMessages.length === 0 && !aiProcessing ? (
              <View style={styles.welcomeMessage}>
                <MaterialCommunityIcons name="brain" size={48} color={BRAND.line} />
                <Text style={styles.welcomeTitle}>AI Innovation Advisor</Text>
                <Text style={styles.welcomeText}>
                  {"Ask me about your innovation portfolio!\n\n💡 \"What project should I prioritise?\"\n📊 \"Analyse my top innovation\"\n🚀 \"Suggest ideas for Aerospace\"\n💰 \"What's the ROI potential?\"\n⚠️ \"What risks should I watch?\""}
                </Text>
              </View>
            ) : (
              <>
                {chatMessages.map((msg, i) => (
                  <View key={i} style={[styles.messageRow, msg.role === 'user' ? styles.userMessageRow : styles.aiMessageRow]}>
                    {msg.role === 'assistant' && (
                      <View style={[styles.aiAvatar, { backgroundColor: BRAND.claude }]}>
                        <MaterialCommunityIcons name="brain" size={14} color="#fff" />
                      </View>
                    )}
                    <View style={{ flex: 1, maxWidth: msg.role === 'user' ? '80%' : '90%', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                      <View style={[styles.messageBubble, msg.role === 'user' ? styles.userBubble : styles.aiBubble]}>
                        <Text style={msg.role === 'user' ? styles.userText : styles.aiText}>{msg.content}</Text>
                      </View>
                      {msg.role === 'assistant' && msg.provider && (
                        <Text style={{ fontSize: 10, color: BRAND.sub, marginTop: 3, marginLeft: 4 }}>{msg.provider}</Text>
                      )}
                    </View>
                  </View>
                ))}

                {/* Live streaming bubble */}
                {aiProcessing && (
                  <View style={[styles.messageRow, styles.aiMessageRow]}>
                    <View style={[styles.aiAvatar, { backgroundColor: BRAND.claude }]}>
                      <MaterialCommunityIcons name="brain" size={14} color="#fff" />
                    </View>
                    <View style={[styles.messageBubble, styles.aiBubble, { flex: 1, maxWidth: '90%' }]}>
                      {streamingMessage ? (
                        <Text style={[styles.aiText, { opacity: 0.9 }]}>{streamingMessage}</Text>
                      ) : (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ActivityIndicator size="small" color={BRAND.claude} />
                          <Text style={[styles.aiText, { color: BRAND.sub }]}>Thinking…</Text>
                        </View>
                      )}
                    </View>
                  </View>
                )}
              </>
            )}
            <View style={{ height: 8 }} />
          </ScrollView>

          {/* Quick prompts */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false}
            style={{ borderTopWidth: 1, borderTopColor: BRAND.line, paddingVertical: 8, paddingHorizontal: 12, maxHeight: 52 }}>
            {['Prioritise my projects', 'Best ROI innovation?', 'Key risks to watch', 'Suggest 3 ideas', 'Market opportunity analysis', 'Investment readiness?'].map(q => (
              <TouchableOpacity key={q}
                style={{ backgroundColor: BRAND.blue + '10', borderRadius: 14, paddingHorizontal: 12, paddingVertical: 6, marginRight: 8, borderWidth: 1, borderColor: BRAND.blue + '30' }}
                onPress={() => {
                  setChatInput(q);
                  // Auto-send after brief tick so state updates
                  setTimeout(async () => {
                    if (aiProcessing) return;
                    const userMsg = { role: 'user', content: q, timestamp: new Date().toISOString() };
                    setChatMessages(p => [...p, userMsg]);
                    setChatInput('');
                    setAiProcessing(true);
                    setStreamingMessage('');
                    const history = chatMessages.slice(-6).map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));
                    try {
                      const result = await getAIAdvice(q, getInnovations(), getMetrics(), history, (partial) => setStreamingMessage(partial));
                      const content = result?.message || 'No response received.';
                      setChatMessages(p => [...p, { role: 'assistant', content, provider: result?.provider, timestamp: result?.timestamp || new Date().toISOString() }]);
                    } catch {
                      setChatMessages(p => [...p, { role: 'assistant', content: 'I\'m currently offline. Please check your connection.', timestamp: new Date().toISOString() }]);
                    } finally {
                      setAiProcessing(false);
                      setStreamingMessage('');
                      setTimeout(() => chatScrollRef.current?.scrollToEnd({ animated: true }), 100);
                    }
                  }, 50);
                }}>
                <Text style={{ fontSize: 12, color: BRAND.blue, fontWeight: '600' }}>{q}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Input row */}
          <View style={styles.chatInputContainer}>
            <TextInput
              style={styles.chatInput}
              value={chatInput}
              onChangeText={setChatInput}
              placeholder="Ask about your innovations…"
              placeholderTextColor="#94a3b8"
              multiline
              maxHeight={80}
              onSubmitEditing={handleSendAIMessage}
              returnKeyType="send"
              blurOnSubmit={false}
              editable={!aiProcessing}
            />
            <TouchableOpacity
              style={[styles.chatSendButton, { backgroundColor: BRAND.claude }, (!chatInput.trim() || aiProcessing) && styles.chatSendButtonDisabled]}
              onPress={handleSendAIMessage}
              disabled={!chatInput.trim() || aiProcessing}>
              {aiProcessing
                ? <ActivityIndicator size="small" color="#fff" />
                : <Ionicons name="send" size={18} color="#fff" />}
            </TouchableOpacity>
          </View>

        </View>
      </View>
    </Modal>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: BUSINESS PLAN FORM MODAL
  // ────────────────────────────────────────────────────────────────────────
  const renderBusinessPlanModal = () => (
    <Modal visible={showBusinessPlanModal} transparent animationType="slide" onRequestClose={() => setShowBusinessPlanModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modalCard, { maxHeight: height * 0.92 }]}>
          <View style={styles.modalHeader}>
            <View><Text style={styles.modalTitle}>📄 Business Plan</Text><Text style={styles.modalSubtitle}>Create or AI-generate</Text></View>
            <TouchableOpacity onPress={() => setShowBusinessPlanModal(false)}><Ionicons name="close" size={24} color={BRAND.sub} /></TouchableOpacity>
          </View>
          <ScrollView style={styles.modalScroll}>
            <Text style={styles.fieldLabel}>Plan Title *</Text>
            <TextInput style={styles.input} value={businessPlanForm.title} onChangeText={v => setBusinessPlanForm(p => ({ ...p, title: v }))} placeholder="2025 Innovation Strategy" placeholderTextColor="#94a3b8" />
            <Text style={styles.fieldLabel}>Company Name *</Text>
            <TextInput style={styles.input} value={businessPlanForm.company_name} onChangeText={v => setBusinessPlanForm(p => ({ ...p, company_name: v }))} placeholder="JobEng AI Solutions" placeholderTextColor="#94a3b8" />

            <TouchableOpacity style={styles.aiGenerateButton} onPress={handleGenerateBusinessPlan} disabled={aiProcessing}>
              <LinearGradient colors={[BRAND.claude, BRAND.blue]} style={styles.aiGenerateGradient}>
                {aiProcessing ? <ActivityIndicator size="small" color="#fff" /> : <><MaterialCommunityIcons name="brain" size={16} color="#fff" /><Text style={styles.aiGenerateText}>AI-Generate Content (Claude)</Text></>}
              </LinearGradient>
            </TouchableOpacity>

            {[
              { label: 'Executive Summary', key: 'executive_summary', placeholder: 'Company overview...' },
              { label: 'Market Analysis', key: 'market_analysis', placeholder: 'Market size, competition...' },
              { label: 'Products & Services', key: 'products_services', placeholder: 'Core offerings...' },
              { label: 'Marketing Strategy', key: 'marketing_strategy', placeholder: 'GTM approach...' },
              { label: 'Financial Projections', key: 'financial_projections', placeholder: 'Year 1: £Xm, Year 2: £Xm...' },
              { label: 'Funding Requirements (£)', key: 'funding_requirements', placeholder: '500000' },
            ].map(f => (
              <View key={f.key}>
                <Text style={styles.fieldLabel}>{f.label}</Text>
                <TextInput style={[styles.input, f.key !== 'funding_requirements' && styles.textarea]} value={businessPlanForm[f.key]} onChangeText={v => setBusinessPlanForm(p => ({ ...p, [f.key]: v }))} placeholder={f.placeholder} placeholderTextColor="#94a3b8" multiline={f.key !== 'funding_requirements'} numberOfLines={3} keyboardType={f.key === 'funding_requirements' ? 'numeric' : 'default'} />
              </View>
            ))}

            <TouchableOpacity style={styles.submitButton} onPress={saveBusinessPlan}>
              <LinearGradient colors={[BRAND.purple, '#6d28d9']} style={styles.submitGradient}>
                <Ionicons name="save" size={18} color="#fff" />
                <Text style={styles.submitText}>Save Business Plan</Text>
              </LinearGradient>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ────────────────────────────────────────────────────────────────────────
  // LOADING
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <Animated.View style={{ opacity: aiPulse, transform: [{ scale: aiPulse.interpolate({ inputRange: [0.3, 1], outputRange: [0.8, 1.2] }) }] }}>
          <MaterialCommunityIcons name="brain" size={64} color={BRAND.blue} />
        </Animated.View>
        <Text style={styles.loadingText}>AI Innovation Hub</Text>
        <Text style={styles.loadingSubtext}>Initialising Claude AI · Loading portfolio...</Text>
        <ActivityIndicator size="large" color={BRAND.blue} style={{ marginTop: 20 }} />
      </View>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ────────────────────────────────────────────────────────────────────────
  const headerOpacity = scrollY.interpolate({ inputRange: [0, 100], outputRange: [1, 0.92], extrapolate: 'clamp' });

  return (
    <SafeAreaView style={styles.container}>
      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <Animated.View style={[styles.header, { opacity: headerOpacity }]}>
        <LinearGradient colors={['#0e4e96', '#1a5a9c']} style={styles.headerGradient}>
          <View style={styles.headerTop}>
            <TouchableOpacity style={styles.headerIconBtn} onPress={() => navigation?.goBack?.()}>
              <Ionicons name="chevron-back" size={22} color="#fff" />
            </TouchableOpacity>
            <View style={{ flex: 1 }}>
              <Text style={styles.headerTitle}>AI Innovation Hub</Text>
              <View style={styles.headerStatusRow}>
                <LiveDot color={isOnline ? BRAND.success : BRAND.danger} />
                <Text style={styles.headerSubtitle}>Claude AI · {isOnline ? 'Online' : 'Offline'}</Text>
              </View>
            </View>
            <View style={styles.headerActions}>
              <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowNewsModal(true)}>
                <Ionicons name="newspaper-outline" size={18} color="#fff" />
                {marketNews.length > 0 && <View style={styles.newsBadge}><Text style={styles.newsBadgeText}>{marketNews.length}</Text></View>}
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowCompareModal(true)}>
                <Ionicons name="git-compare-outline" size={18} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowAIChat(true)}>
                <Ionicons name="chatbubble-ellipses" size={18} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowFilters(!showFilters)}>
                <Ionicons name="filter" size={18} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowAddModal(true)}>
                <Ionicons name="add" size={22} color="#fff" />
              </TouchableOpacity>
            </View>
          </View>

          {/* AI spotlight */}
          <LinearGradient colors={['rgba(176,105,57,0.25)', 'rgba(14,78,150,0.25)']} style={styles.spotlightWrap}>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <MaterialCommunityIcons name="brain" size={16} color="#fff" />
                <Text style={styles.spotlightTitle}>Claude AI · Innovation Intelligence</Text>
              </View>
              <Text style={styles.spotlightText}>
                {getInnovations().length} innovations · Total ROI £{(getInnovations().reduce((s, i) => s + (i.estimated_roi || 0), 0) / 1000).toFixed(0)}k ·{' '}
                <Text style={styles.spotlightLink} onPress={() => setShowAIGenerator(true)}>Generate ideas →</Text>
              </Text>
            </View>
            <Animated.View style={{ opacity: aiPulse }}>
              <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: BRAND.success }} />
            </Animated.View>
          </LinearGradient>
        </LinearGradient>
      </Animated.View>

      {/* ── ERROR BANNER ──────────────────────────────────────────────── */}
      {error && (
        <View style={styles.errorBanner}>
          <Ionicons name="warning-outline" size={18} color="#fff" />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={loadAllData}><Text style={styles.retryText}>Retry</Text></TouchableOpacity>
        </View>
      )}

      {/* ── FILTER PANEL ─────────────────────────────────────────────── */}
      {showFilters && (
        <View style={styles.filtersPanel}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.filterRow}>
              {INNOVATION_TYPES.map(type => (
                <TouchableOpacity key={type.id} style={[styles.filterChip, activeType === type.id && { backgroundColor: type.color + '20', borderColor: type.color }]} onPress={() => setActiveType(activeType === type.id ? 'all' : type.id)}>
                  <Ionicons name={type.icon} size={13} color={activeType === type.id ? type.color : BRAND.sub} />
                  <Text style={[styles.filterChipText, activeType === type.id && { color: type.color }]}>{type.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 6 }}>
            <View style={styles.filterRow}>
              <TouchableOpacity style={[styles.filterChip, filters.industry === 'all' && { backgroundColor: BRAND.blue + '20', borderColor: BRAND.blue }]} onPress={() => setFilters(p => ({ ...p, industry: 'all' }))}>
                <Text style={[styles.filterChipText, filters.industry === 'all' && { color: BRAND.blue }]}>All Industries</Text>
              </TouchableOpacity>
              {ENGINEERING_INDUSTRIES.map(ind => (
                <TouchableOpacity key={ind} style={[styles.filterChip, filters.industry === ind && { backgroundColor: BRAND.mint + '20', borderColor: BRAND.mint }]} onPress={() => setFilters(p => ({ ...p, industry: ind }))}>
                  <Text style={[styles.filterChipText, filters.industry === ind && { color: BRAND.mint }]}>{ind}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
          <View style={{ flexDirection: 'row', gap: 8, paddingTop: 6 }}>
            {IMPACT_LEVELS.map(l => (
              <TouchableOpacity key={l.id} style={[styles.filterChip, filters.impact === l.id && { backgroundColor: l.color + '20', borderColor: l.color }]} onPress={() => setFilters(p => ({ ...p, impact: filters.impact === l.id ? 'all' : l.id }))}>
                <Text style={[styles.filterChipText, filters.impact === l.id && { color: l.color }]}>{l.name}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={[styles.filterChip, filters.bookmarked && { backgroundColor: BRAND.canary + '20', borderColor: BRAND.canary }]} onPress={() => setFilters(p => ({ ...p, bookmarked: !p.bookmarked }))}>
              <Ionicons name="bookmark" size={13} color={filters.bookmarked ? BRAND.canary : BRAND.sub} />
              <Text style={[styles.filterChipText, filters.bookmarked && { color: BRAND.canary }]}>Bookmarked</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* ── MAIN SCROLL ───────────────────────────────────────────────── */}
      <Animated.ScrollView style={{ opacity: fadeAnim }} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} colors={[BRAND.blue]} />}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: true })}
        scrollEventThrottle={16}
      >
        {/* ── AI Generated ideas strip ─ */}
        {aiGeneratedIdeas.length > 0 && (
          <View style={styles.aiRecommendations}>
            <View style={styles.aiHeader}>
              <View style={styles.aiTitleContainer}>
                <MaterialCommunityIcons name="brain" size={22} color={BRAND.claude} />
                <Text style={[styles.aiTitle, { color: BRAND.claude }]}>AI Generated Ideas</Text>
              </View>
              <TouchableOpacity onPress={handleGenerateIdeas}><Ionicons name="refresh" size={18} color={BRAND.claude} /></TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {aiGeneratedIdeas.map(idea => (
                <TouchableOpacity key={idea.id} style={[styles.aiCard, { borderColor: BRAND.claude + '40' }]} onPress={() => { setSelectedInnovation(idea); setShowDetailModal(true); }}>
                  <View style={styles.aiCardHeader}>
                    <View style={[styles.aiImpactBadge, { backgroundColor: BRAND.claude + '20' }]}>
                      <Text style={[styles.aiImpactText, { color: BRAND.claude }]}>{idea.provider || idea.ai_mode || activeAIProvider}</Text>
                    </View>
                    <Text style={styles.aiConfidence}>{idea.confidence}%</Text>
                  </View>
                  <Text style={styles.aiCardTitle} numberOfLines={2}>{idea.title}</Text>
                  <Text style={styles.aiCardDesc} numberOfLines={2}>{idea.description}</Text>
                  <View style={styles.aiCardFooter}>
                    <Text style={styles.aiRoiText}>£{(idea.estimated_roi || 0).toLocaleString()}</Text>
                    <TouchableOpacity style={[styles.aiImplementBtn, { backgroundColor: BRAND.success + '20' }]} onPress={() => handleAddAIIdea(idea)}>
                      <Text style={[styles.aiImplementText, { color: BRAND.success }]}>Add</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* ── KPIs ─ */}
        <View style={styles.kpiGrid}>
          {getKPIs().map(kpi => (
            <View key={kpi.id} style={styles.kpiCard}>
              <View style={[styles.kpiIcon, { backgroundColor: kpi.color + '15' }]}>
                <Ionicons name={kpi.icon} size={18} color={kpi.color} />
              </View>
              <Text style={[styles.kpiValue, { color: kpi.color }]}>{kpi.value}</Text>
              <Text style={styles.kpiLabel}>{kpi.label}</Text>
              <View style={styles.kpiTrendContainer}>
                <Ionicons name="trending-up" size={11} color={BRAND.success} />
                <Text style={[styles.kpiTrend, { color: BRAND.success }]}>{kpi.trend}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* ── View mode + sort + search ─ */}
        <View style={{ paddingHorizontal: 16 }}>
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10 }}>
            {VIEW_MODES.map(m => (
              <TouchableOpacity key={m} style={[styles.viewModeBtn, viewMode === m && styles.viewModeBtnActive]} onPress={() => setViewMode(m)}>
                <Ionicons name={m === 'pipeline' ? 'list' : m === 'kanban' ? 'grid' : 'bar-chart'} size={14} color={viewMode === m ? '#fff' : BRAND.sub} />
                <Text style={[styles.viewModeBtnText, viewMode === m && { color: '#fff' }]}>{m.charAt(0).toUpperCase() + m.slice(1)}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={[styles.viewModeBtn, { marginLeft: 'auto' }]} onPress={() => setShowSortModal(true)}>
              <Ionicons name="swap-vertical" size={14} color={BRAND.sub} />
              <Text style={styles.viewModeBtnText}>Sort</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.searchWrap}>
            <Ionicons name="search" size={18} color={BRAND.sub} />
            <TextInput value={query} onChangeText={setQuery} placeholder="Search innovations..." placeholderTextColor="#94a3b8" style={styles.searchInput} returnKeyType="search" onSubmitEditing={handleAISearch} />
            {searchAiLoading ? <ActivityIndicator size="small" color={BRAND.blue} /> : query ? (
              <TouchableOpacity onPress={() => setQuery('')}><Ionicons name="close-circle" size={18} color="#94a3b8" /></TouchableOpacity>
            ) : null}
          </View>
        </View>

        {/* ── AI Generator CTA ─ */}
        <TouchableOpacity style={{ marginHorizontal: 16, marginBottom: 14 }} onPress={() => setShowAIGenerator(true)} activeOpacity={0.9}>
          <LinearGradient colors={[BRAND.claude, BRAND.blue]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={styles.businessPlanGradient}>
            <View style={styles.businessPlanContent}>
              <View>
                <Text style={styles.businessPlanCTATitle}>🤖 Claude AI Generator</Text>
                <Text style={styles.businessPlanCTAText}>Generate innovation ideas for any engineering discipline</Text>
              </View>
              <View style={styles.businessPlanCTAButton}>
                <MaterialCommunityIcons name="brain" size={16} color={BRAND.canary} />
                <Text style={[styles.businessPlanCTAButtonText, { color: BRAND.canary }]}>Generate</Text>
              </View>
            </View>
          </LinearGradient>
        </TouchableOpacity>

        {/* ── Stage filter chips ─ */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.stageScroll} contentContainerStyle={styles.stageScrollContent}>
          {[{ id: 'all', name: 'All', color: BRAND.text, icon: 'apps-outline' }, ...INNOVATION_STAGES].map(stage => (
            <TouchableOpacity key={stage.id} style={[styles.stageChip, activeStage === stage.id && { backgroundColor: stage.color + '20', borderColor: stage.color }]} onPress={() => setActiveStage(activeStage === stage.id ? 'all' : stage.id)}>
              <View style={[styles.stageDot, { backgroundColor: stage.color }]} />
              <Text style={[styles.stageChipText, activeStage === stage.id && { color: stage.color }]}>
                {stage.name} ({stage.id === 'all' ? getInnovations().length : getInnovations().filter(i => i.stage === stage.id).length})
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* ── CONTENT AREA: Pipeline / Kanban / Analytics ─ */}
        {viewMode === 'kanban' ? (
          <View style={{ marginBottom: 20 }}>
            <Text style={[styles.sectionTitle, { paddingHorizontal: 16 }]}>Kanban Board</Text>
            {renderKanban()}
          </View>
        ) : viewMode === 'analytics' ? (
          renderAnalytics()
        ) : (
          <View style={{ paddingHorizontal: 16 }}>
            {/* Business plans */}
            {businessPlans.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>Business Plans</Text>
                  <TouchableOpacity style={styles.smallBtn} onPress={() => setShowBusinessPlanModal(true)}>
                    <Ionicons name="add-circle-outline" size={15} color={BRAND.purple} />
                    <Text style={[styles.smallBtnText, { color: BRAND.purple }]}>New</Text>
                  </TouchableOpacity>
                </View>
                {businessPlans.slice(0, 2).map(plan => (
                  <TouchableOpacity key={plan.id} style={styles.businessPlanCard} onPress={() => { setSelectedBusinessPlan(plan); setShowBusinessPlanDetailModal(true); }}>
                    <View style={styles.businessPlanCardHeader}>
                      <View style={styles.businessPlanIcon}><Ionicons name="document-text" size={18} color={BRAND.purple} /></View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.businessPlanTitle} numberOfLines={1}>{plan.title}</Text>
                        <Text style={styles.businessPlanDate}>{new Date(plan.created_at).toLocaleDateString('en-GB')}</Text>
                      </View>
                      <View style={[styles.businessPlanStatus, { backgroundColor: plan.status === 'draft' ? BRAND.warning + '20' : BRAND.success + '20' }]}>
                        <Text style={[styles.businessPlanStatusText, { color: plan.status === 'draft' ? BRAND.warning : BRAND.success }]}>{plan.status || 'Draft'}</Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* Innovation Pipeline */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Innovation Pipeline</Text>
                <View style={styles.sectionActions}>
                  <TouchableOpacity style={styles.smallBtn} onPress={() => setShowROICalculator(true)}>
                    <Ionicons name="calculator-outline" size={15} color={BRAND.warning} />
                    <Text style={[styles.smallBtnText, { color: BRAND.warning }]}>ROI Calc</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.smallBtn} onPress={() => setShowAddModal(true)}>
                    <Ionicons name="add-circle-outline" size={15} color={BRAND.blue} />
                    <Text style={styles.smallBtnText}>New</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {getFilteredInnovations().length === 0 ? (
                <View style={styles.emptyState}>
                  <Ionicons name="flask-outline" size={48} color={BRAND.line} />
                  <Text style={styles.emptyTitle}>No innovations found</Text>
                  <Text style={styles.emptyText}>Create your first or generate with Claude AI</Text>
                  <TouchableOpacity style={[styles.emptyButton, { backgroundColor: BRAND.claude }]} onPress={() => setShowAIGenerator(true)}>
                    <MaterialCommunityIcons name="brain" size={16} color="#fff" />
                    <Text style={styles.emptyButtonText}>Generate with Claude AI</Text>
                  </TouchableOpacity>
                </View>
              ) : getFilteredInnovations().map(inn => renderInnovationCard(inn))}
            </View>

            {/* Roadmap */}
            {roadmap.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>Roadmap</Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {roadmap.map(item => (
                    <View key={item.id} style={styles.roadmapCard}>
                      <Text style={styles.roadmapQuarter}>{item.quarter}</Text>
                      <Text style={styles.roadmapTitle} numberOfLines={2}>{item.title}</Text>
                      <View style={styles.progressBar} style={{ height: 4, borderRadius: 2, backgroundColor: BRAND.line, marginVertical: 8 }}>
                        <View style={{ height: 4, borderRadius: 2, width: `${item.progress}%`, backgroundColor: item.status === 'completed' ? BRAND.success : item.status === 'in_progress' ? BRAND.blue : BRAND.warning }} />
                      </View>
                      <View style={[styles.roadmapStatus, { backgroundColor: item.status === 'completed' ? BRAND.success + '20' : item.status === 'in_progress' ? BRAND.blue + '20' : BRAND.warning + '20' }]}>
                        <Text style={[styles.roadmapStatusText, { color: item.status === 'completed' ? BRAND.success : item.status === 'in_progress' ? BRAND.blue : BRAND.warning }]}>
                          {item.status.replace('_', ' ').toUpperCase()}
                        </Text>
                      </View>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}

            {/* Market News teaser */}
            {marketNews.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>Market Intelligence</Text>
                  <TouchableOpacity style={styles.smallBtn} onPress={() => setShowNewsModal(true)}>
                    <Text style={[styles.smallBtnText, { color: BRAND.blue }]}>View All</Text>
                  </TouchableOpacity>
                </View>
                {marketNews.slice(0, 2).map((n, i) => (
                  <View key={i} style={{ backgroundColor: BRAND.white, borderRadius: 12, padding: 12, marginBottom: 8, borderLeftWidth: 3, borderLeftColor: BRAND.blue, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 }}>
                    <Text style={{ fontSize: 10, color: BRAND.blue, fontWeight: '700', textTransform: 'uppercase', marginBottom: 3 }}>{n.section}</Text>
                    <Text style={{ fontSize: 13, fontWeight: '600', color: BRAND.text, lineHeight: 18 }} numberOfLines={2}>{n.title}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* ML Platform widget */}
            <TouchableOpacity onPress={() => navigateToML('models')} activeOpacity={0.88}
              style={{ marginBottom: 12, borderRadius: 14, overflow: 'hidden', borderWidth: 1.5, borderColor: '#667eea30' }}>
              <LinearGradient colors={['#667eea15','#764ba215']} start={{ x:0,y:0 }} end={{ x:1,y:1 }}
                style={{ padding: 14 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#667eea20',
                                 alignItems: 'center', justifyContent: 'center' }}>
                    <MaterialCommunityIcons name="brain" size={20} color="#667eea" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, fontWeight: '800', color: BRAND.text }}>ML Platform</Text>
                    <Text style={{ fontSize: 11, color: BRAND.sub }}>
                      {mlModelsSnapshot.length > 0
                        ? `${mlModelsSnapshot.length} models · avg ${(mlModelsSnapshot.filter(m=>m.accuracy>0).reduce((s,m)=>s+m.accuracy,0)/Math.max(1,mlModelsSnapshot.filter(m=>m.accuracy>0).length)).toFixed(1)}% accuracy`
                        : 'Machine Learning Model Management'}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4,
                                 backgroundColor: '#667eea18', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 5 }}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#667eea' }}>Open</Text>
                    <Ionicons name="arrow-forward" size={13} color="#667eea" />
                  </View>
                </View>
                {mlModelsSnapshot.length > 0 ? (
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    {mlModelsSnapshot.slice(0,3).map((m,i) => (
                      <View key={m.id||i} style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.7)',
                                                    borderRadius: 8, padding: 8, alignItems: 'center' }}>
                        <MaterialCommunityIcons name={m.icon||'brain'} size={16} color={m.color||'#667eea'} />
                        <Text style={{ fontSize: 11, fontWeight: '800', color: BRAND.text, marginTop: 4 }}>
                          <Text>{m.accuracy > 0 ? `${m.accuracy}%` : 'Draft'}</Text>
                        </Text>
                        <Text style={{ fontSize: 9, color: BRAND.sub, textAlign: 'center' }} numberOfLines={1}>
                          {m.name}
                        </Text>
                      </View>
                    ))}
                    {mlModelsSnapshot.length > 3 && (
                      <View style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 8,
                                     padding: 8, alignItems: 'center', justifyContent: 'center' }}>
                        <Text style={{ fontSize: 14, fontWeight: '800', color: '#667eea' }}>+{mlModelsSnapshot.length-3}</Text>
                        <Text style={{ fontSize: 9, color: BRAND.sub }}>more</Text>
                      </View>
                    )}
                  </View>
                ) : (
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    {[
                      { label:'Models', value:'6', color:'#667eea' },
                      { label:'Active', value:'5', color:BRAND.success },
                      { label:'Avg Acc',value:'89%',color:BRAND.warning },
                    ].map((s,i) => (
                      <View key={i} style={{ flex:1, backgroundColor:'rgba(255,255,255,0.7)', borderRadius:8,
                                             padding:8, alignItems:'center' }}>
                        <Text style={{ fontSize:15, fontWeight:'800', color:s.color }}>{s.value}</Text>
                        <Text style={{ fontSize:9, color:BRAND.sub }}>{s.label}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </LinearGradient>
            </TouchableOpacity>

            {/* Quick Actions */}
            <View style={styles.quickActions}>
              {[
                { icon: 'brain',               color: BRAND.claude,   label: 'Generate', action: () => setShowAIGenerator(true),  mc: true },
                { icon: 'school-outline',       color: BRAND.purple,   label: 'Mentor',   action: () => { setMentorStep(0); setMentorAnswers({}); setMentorProfile(null); setMentorIdeas([]); setMentorRoadmap(null); setMentorIdeaDetail({}); setShowMentorModal(true); } },
                { icon: 'add-circle',           color: BRAND.blue,     label: 'New Idea', action: () => setShowAddModal(true) },
                { icon: 'send-outline',         color: '#e91e63',      label: 'Submit',   action: () => { setIdeaForm({ idea_title:'', idea_description:'', innovation_type:'technology', customer_pain_point:'', proposed_solution:'', expected_benefits:'', potential_impact:'MEDIUM', effort_required:'medium', estimated_cost:'', estimated_timeline:'', similar_existing_solutions:'', unique_aspects:'', supporting_data:'' }); setShowSubmitIdeaModal(true); } },
                { icon: 'list-outline',         color: '#9c27b0',      label: 'Ideas',    action: () => { loadIdeasData(); setShowIdeasListModal(true); } },
                { icon: 'document-text',        color: BRAND.info,     label: 'Biz Plan', action: () => setShowBusinessPlanModal(true) },
                { icon: 'chatbubble-ellipses',  color: BRAND.success,  label: 'Advisor',  action: () => setShowAIChat(true) },
                { icon: 'calculator',           color: BRAND.warning,  label: 'ROI Calc', action: () => setShowROICalculator(true) },
                { icon: 'git-compare-outline',  color: BRAND.mint,     label: 'Compare',  action: () => setShowCompareModal(true) },
                { icon: 'download-outline',     color: BRAND.sepia,    label: 'CSV',      action: handleExportCSV },
                { icon: 'search',               color: '#0e4e96',      label: 'DeepSearch',action: () => { if (getInnovations().length === 0) { Alert.alert('No Ideas', 'Add or generate an innovation idea first, then tap Deep Search on it.'); return; } Alert.alert('Deep Search', 'Open any innovation idea and tap the Deep Search button to run a full analysis.'); } },
                { icon: 'newspaper-outline',    color: BRAND.text,     label: 'News',     action: () => setShowNewsModal(true) },
                { icon: 'chart-line',           color: '#667eea',      label: 'ML Models',action: () => navigateToML('models'), mc: false, mci: true },
              ].map((a, i) => (
                <TouchableOpacity key={i} style={styles.quickActionBtn} onPress={a.action}>
                  {a.mc ? (
                    <Animated.View style={{ opacity: aiPulse }}><MaterialCommunityIcons name={a.icon} size={20} color={a.color} /></Animated.View>
                  ) : a.mci ? (
                    <MaterialCommunityIcons name={a.icon} size={20} color={a.color} />
                  ) : (
                    <Ionicons name={a.icon} size={20} color={a.color} />
                  )}
                  <Text style={[styles.quickActionText, { color: a.color }]}>{a.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        <View style={{ height: 100 }} />
      </Animated.ScrollView>

      {/* ── SORT MODAL ──────────────────────────────────────────────── */}
      <Modal visible={showSortModal} transparent animationType="fade" onRequestClose={() => setShowSortModal(false)}>
        <TouchableOpacity style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' }} onPress={() => setShowSortModal(false)} activeOpacity={1}>
          <View style={{ backgroundColor: BRAND.white, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: '700', color: BRAND.text, marginBottom: 16 }}>Sort By</Text>
            {SORT_OPTIONS.map(o => (
              <TouchableOpacity key={o.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: BRAND.line, gap: 12 }} onPress={() => { setSortBy(o.id); setShowSortModal(false); }}>
                <Ionicons name={o.icon} size={18} color={sortBy === o.id ? BRAND.blue : BRAND.sub} />
                <Text style={{ fontSize: 14, color: sortBy === o.id ? BRAND.blue : BRAND.text, fontWeight: sortBy === o.id ? '700' : '400' }}>{o.label}</Text>
                {sortBy === o.id && <Ionicons name="checkmark" size={18} color={BRAND.blue} style={{ marginLeft: 'auto' }} />}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ── ALL OTHER MODALS ─────────────────────────────────────────── */}
      {renderDetailModal()}
      {renderBusinessPlanDetailModal()}
      {renderDetailedBusinessPlanModal()}
      {renderROICalculator()}
      {renderNotesModal()}
      {renderCompetitorModal()}
      {renderCompareModal()}
      {renderNewsModal()}
      {renderAddModal()}
      {renderAIGeneratorModal()}
      {renderAIChatModal()}
      {renderBusinessPlanModal()}
      {renderMentorModal()}
      {renderSubmitIdeaModal()}
      {renderIdeasListModal()}
      {renderDeepSearchModal()}
    </SafeAreaView>
  );

  // ────────────────────────────────────────────────────────────────────────
  // BUSINESS MENTOR — HANDLERS
  // ────────────────────────────────────────────────────────────────────────

  function mentorAnswerCurrent() {
    const q = MENTOR_QUESTIONS[mentorStep - 1];
    if (!q) return null;
    if (q.type === 'text') return mentorInput.trim() || null;
    return mentorSelected.length > 0 ? mentorSelected.join(', ') : null;
  }

  function handleMentorNext() {
    const q = MENTOR_QUESTIONS[mentorStep - 1];
    const answer = mentorAnswerCurrent();
    if (!answer) { Alert.alert('Please answer before continuing'); return; }
    const updated = { ...mentorAnswers, [q.key]: answer };
    setMentorAnswers(updated);
    setMentorInput('');
    setMentorSelected([]);
    if (mentorStep < 9) {
      setMentorStep(mentorStep + 1);
    } else {
      setMentorStep(10);
      runMentorGeneration(updated);
    }
  }

  function toggleMentorChip(option) {
    const q = MENTOR_QUESTIONS[mentorStep - 1];
    if (q.type === 'single') {
      setMentorSelected([option]);
    } else {
      setMentorSelected(prev =>
        prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
      );
    }
  }

  async function runMentorGeneration(answers) {
    setMentorLoading(true);
    try {
      setMentorLoadMsg('Building your entrepreneur profile…');
      const profilePrompt =
        `You are an elite business mentor. Summarise this entrepreneur profile.\n` +
        `Skills: ${answers.skills}\nExperience: ${answers.experience}\nPersonality: ${answers.personality}\n` +
        `Risk: ${answers.risk}\nResources: ${answers.resources}\nMarket: ${answers.location}\n` +
        `Income goal: ${answers.income}\nValues: ${answers.values}\nModels: ${answers.models}\n\n` +
        `Return ONLY valid JSON (no markdown):\n` +
        `{"entrepreneur_type":"2-3 words","strengths":["s1","s2","s3"],"constraints":["c1","c2"],"summary":"2 sentences about best-fit businesses"}`;

      const profileText = await callClaude(profilePrompt, 500);
      const profile = repairJSONInScreen(profileText) || {
        entrepreneur_type: 'Independent Builder',
        strengths: ['Self-motivated', 'Resourceful', 'Practical'],
        constraints: ['Limited capital', 'Solo founder'],
        summary: 'You are best suited to lean service or digital businesses where your existing skills are the main asset. Start lean, validate fast.',
      };
      setMentorProfile(profile);

      setMentorLoadMsg('Generating 5 custom business ideas…');
      const buildIdeaPrompt = (n, exclude) =>
        `Elite business mentor generating ${n} realistic business idea${n > 1 ? 's' : ''}.` +
        (exclude ? ` Not these: ${exclude}.` : '') +
        `\nProfile — Skills: ${answers.skills}. Experience: ${answers.experience}. Personality: ${answers.personality}. Risk: ${answers.risk}. Resources: ${answers.resources}. Market: ${answers.location}. Income goal: ${answers.income}. Values: ${answers.values}. Models: ${answers.models}.\n` +
        `For each idea use these JSON keys: name, concept (1 sentence), problem (1 sentence), customer (specific), revenue_model (1 sentence), startup_cost ("Low <£500"|"Medium £500-5k"|"High £5k+"), time_to_profit ("X weeks/months"), risk_level ("Low"|"Medium"|"High"), fit_reason (1 sentence).\n` +
        `Reply ONLY with a JSON array of ${n} objects. No markdown.`;

      const ideas = [];
      const r1 = await callClaude(buildIdeaPrompt(2, ''), 900);
      const b1 = repairJSONInScreen(r1);
      ideas.push(...(Array.isArray(b1) ? b1 : b1?.name ? [b1] : []));

      const r2 = await callClaude(buildIdeaPrompt(2, ideas.map(i => i.name).join(', ')), 900);
      const b2 = repairJSONInScreen(r2);
      ideas.push(...(Array.isArray(b2) ? b2 : b2?.name ? [b2] : []));

      const r3 = await callClaude(buildIdeaPrompt(1, ideas.map(i => i.name).join(', ')), 600);
      const b3 = repairJSONInScreen(r3);
      ideas.push(...(Array.isArray(b3) ? b3 : b3?.name ? [b3] : []));

      while (ideas.length < 5) {
        ideas.push({ name: `Skills-Based Business ${ideas.length + 1}`, concept: 'A lean business built directly on your existing expertise.', problem: 'Clients need reliable expert help in your field.', customer: 'Small businesses in your local or online market.', revenue_model: 'Monthly retainer or per-project fees.', startup_cost: 'Low <£500', time_to_profit: '4-8 weeks', risk_level: 'Low', fit_reason: 'Uses your existing skills with minimal upfront investment.' });
      }

      setMentorIdeas(ideas.slice(0, 5));
      setMentorStep(11);
    } catch (err) {
      console.error('Mentor error:', err.message);
      setMentorIdeas(getMentorFallbackIdeas(answers));
      setMentorStep(11);
    } finally {
      setMentorLoading(false);
      setMentorLoadMsg('');
    }
  }

  async function handleMentorExpandIdea(idx) {
    if (mentorExpanded === idx) { setMentorExpanded(null); return; }
    setMentorExpanded(idx);
    if (mentorIdeaDetail[idx]) return;
    const idea = mentorIdeas[idx];
    const prompt =
      `Business mentor breakdown for: "${idea.name}" — ${idea.concept}\n` +
      `Write in plain text, no markdown:\n` +
      `GROWTH PLAN: How this grows over 1-3 years (2-3 sentences).\n` +
      `RISK & MITIGATION: Key risks and how to reduce them (2-3 sentences).\n` +
      `VALIDATION SIGNALS: Real demand evidence — trends, platforms, or behaviour (2-3 sentences).\n` +
      `FIRST 3 ACTIONS: Exactly 3 concrete steps to take this week.`;
    try {
      const text = await callClaude(prompt, 700);
      setMentorIdeaDetail(prev => ({ ...prev, [idx]: text || 'Details unavailable.' }));
    } catch {
      setMentorIdeaDetail(prev => ({ ...prev, [idx]: 'Could not load details. Check your connection.' }));
    }
  }

  async function handleMentorRoadmap() {
    const topIdea = mentorIdeas[0];
    if (!topIdea) return;
    setMentorLoading(true);
    setMentorLoadMsg('Building your 30-day action roadmap…');
    setMentorStep(12);
    const prompt =
      `Business mentor 30-day launch roadmap.\n` +
      `Business: "${topIdea.name}" — ${topIdea.concept}\n` +
      `Profile: Skills: ${mentorAnswers.skills}. Resources: ${mentorAnswers.resources}. Market: ${mentorAnswers.location}.\n\n` +
      `Write in plain text, no markdown symbols:\n` +
      `WEEK 1 — FOUNDATION: 3 specific daily actions.\n` +
      `WEEK 2 — BUILD: 3 specific actions.\n` +
      `WEEK 3 — LAUNCH: 3 specific actions.\n` +
      `WEEK 4 — GROW: 3 specific actions.\n` +
      `TOOLS NEEDED: 4-5 free or low-cost tools.\n` +
      `FIRST CUSTOMER: Exactly how to get the first paying customer.\n` +
      `AVOID: 3 common beginner mistakes for this business type.`;
    try {
      const text = await callClaude(prompt, 900);
      setMentorRoadmap(text || 'Roadmap unavailable. Try again.');
    } catch {
      setMentorRoadmap('Could not generate roadmap. Check your connection.');
    } finally {
      setMentorLoading(false);
      setMentorLoadMsg('');
    }
  }

  function getMentorFallbackIdeas(answers) {
    return [
      { name: 'Freelance Consulting', concept: 'Sell your expertise directly to businesses as a paid consultant.', problem: 'Businesses need specialist knowledge without a full-time hire.', customer: 'SMEs and startups in your industry.', revenue_model: 'Hourly rate or monthly retainer (£500–£3,000/month).', startup_cost: 'Low <£500', time_to_profit: '2-4 weeks', risk_level: 'Low', fit_reason: 'Zero product development — your skills are the product.' },
      { name: 'Online Course or Workshop', concept: 'Package your knowledge into a paid digital course or live workshop.', problem: 'People want to learn your skills but cannot afford 1-on-1 coaching.', customer: 'Career changers and professionals in your field.', revenue_model: 'Course sales (£50–£500/student) or live workshop tickets.', startup_cost: 'Low <£500', time_to_profit: '4-8 weeks', risk_level: 'Low', fit_reason: 'Scales without more of your time once created.' },
      { name: 'Niche Paid Newsletter', concept: 'A paid newsletter with curated expert insights for a specific audience.', problem: 'Professionals want depth, not generic news.', customer: 'Enthusiasts and professionals in a niche you know well.', revenue_model: 'Paid subscriptions (£5–£20/month) + sponsorships.', startup_cost: 'Low <£500', time_to_profit: '2-6 months', risk_level: 'Low', fit_reason: 'Fully remote, compounds over time, low overhead.' },
      { name: 'Done-For-You Service', concept: 'Execute a specific repeatable task that businesses outsource regularly.', problem: 'Business owners are time-poor and pay for reliable execution.', customer: 'Busy entrepreneurs and SME owners.', revenue_model: 'Per-project fee or retainer (£300–£2,000/project).', startup_cost: 'Low <£500', time_to_profit: '2-4 weeks', risk_level: 'Low', fit_reason: 'Immediate cash flow with no product to build.' },
      { name: 'AI-Powered Niche Tool', concept: 'A simple AI-assisted tool solving one specific problem for a niche audience.', problem: 'Niche users have specific problems generic AI tools ignore.', customer: 'Professionals in an industry you understand deeply.', revenue_model: 'Monthly subscription (£10–£50/user/month).', startup_cost: 'Medium £500-5k', time_to_profit: '2-4 months', risk_level: 'Medium', fit_reason: 'Your domain knowledge is your competitive moat.' },
    ];
  }

  function renderMentorModal() {
    const q = (mentorStep >= 1 && mentorStep <= 9) ? MENTOR_QUESTIONS[mentorStep - 1] : null;
    const riskColor = { Low: BRAND.success, Medium: BRAND.warning, High: BRAND.danger };

    return (
      <Modal visible={showMentorModal} transparent animationType="slide" onRequestClose={() => setShowMentorModal(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: BRAND.white, borderTopLeftRadius: 28, borderTopRightRadius: 28, height: height * 0.90 }}>

            {/* ── Header ── */}
            <LinearGradient colors={[BRAND.purple, BRAND.blue]} style={{ borderTopLeftRadius: 28, borderTopRightRadius: 28, padding: 20, paddingBottom: 16 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <View style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
                    <Ionicons name="school-outline" size={20} color="#fff" />
                  </View>
                  <View>
                    <Text style={{ color: '#fff', fontSize: 17, fontWeight: '800' }}>Elite Business Mentor</Text>
                    <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 1 }}>
                      <Text>{mentorStep === 0 ? 'Powered by Claude AI' : mentorStep <= 9 ? `Question ${mentorStep} of 9` : mentorStep === 10 ? 'Analysing your profile…' : mentorStep === 11 ? '5 Custom Business Ideas' : '30-Day Roadmap'}</Text>
                    </Text>
                  </View>
                </View>
                <TouchableOpacity onPress={() => setShowMentorModal(false)}>
                  <Ionicons name="close-circle" size={28} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>
              {mentorStep >= 1 && mentorStep <= 9 && (
                <View style={{ marginTop: 14, height: 4, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                  <View style={{ height: 4, backgroundColor: '#fff', borderRadius: 2, width: `${(mentorStep / 9) * 100}%` }} />
                </View>
              )}
            </LinearGradient>

            <ScrollView ref={mentorScrollRef} style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 40 }} showsVerticalScrollIndicator={false}>

              {/* ── INTRO ── */}
              {mentorStep === 0 && (
                <View style={{ alignItems: 'center', paddingTop: 16 }}>
                  <View style={{ width: 76, height: 76, borderRadius: 38, backgroundColor: BRAND.purple + '15', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                    <Ionicons name="school-outline" size={38} color={BRAND.purple} />
                  </View>
                  <Text style={{ fontSize: 22, fontWeight: '800', color: BRAND.text, textAlign: 'center', marginBottom: 10 }}>Welcome.</Text>
                  <Text style={{ fontSize: 14, color: BRAND.sub, textAlign: 'center', lineHeight: 23, marginBottom: 24 }}>
                    {'Before we talk business ideas, I want to understand '}
                    <Text style={{ fontWeight: '700', color: BRAND.text }}>you</Text>
                    {' — your skills, your situation, what you\'re actually working with.\n\nThat\'s the only way I can give you ideas that will '}
                    <Text style={{ fontWeight: '700', color: BRAND.purple }}>genuinely work for you</Text>
                    {', not just sound good on paper.\n\n9 questions. Then I build your custom business plan.'}
                  </Text>
                  {[
                    { icon: 'person-outline',           label: 'Deep entrepreneur profile' },
                    { icon: 'bulb-outline',              label: '5 custom-built business ideas' },
                    { icon: 'map-outline',               label: '30-day action roadmap' },
                    { icon: 'shield-checkmark-outline',  label: 'Real demand validation' },
                  ].map((f, i) => (
                    <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, width: '100%', marginBottom: 9, backgroundColor: BRAND.bg, padding: 13, borderRadius: 12 }}>
                      <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: BRAND.purple + '15', alignItems: 'center', justifyContent: 'center' }}>
                        <Ionicons name={f.icon} size={17} color={BRAND.purple} />
                      </View>
                      <Text style={{ fontSize: 14, color: BRAND.text, fontWeight: '600' }}>{f.label}</Text>
                    </View>
                  ))}
                  <TouchableOpacity onPress={() => setMentorStep(1)} style={{ marginTop: 20, width: '100%', borderRadius: 16, overflow: 'hidden' }}>
                    <LinearGradient colors={[BRAND.purple, BRAND.blue]} style={{ paddingVertical: 16, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 8 }}>
                      <Text style={{ color: '#fff', fontSize: 16, fontWeight: '800' }}>Let's Begin</Text>
                      <Ionicons name="arrow-forward" size={18} color="#fff" />
                    </LinearGradient>
                  </TouchableOpacity>
                </View>
              )}

              {/* ── QUESTIONS 1–9 ── */}
              {q && (
                <View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                    <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: BRAND.purple + '15', alignItems: 'center', justifyContent: 'center' }}>
                      <Ionicons name={q.icon} size={22} color={BRAND.purple} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 11, color: BRAND.purple, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>Question {mentorStep} of 9</Text>
                      <Text style={{ fontSize: 16, fontWeight: '800', color: BRAND.text, lineHeight: 22 }}>{q.label}</Text>
                    </View>
                  </View>
                  {q.hint && (
                    <View style={{ backgroundColor: BRAND.purple + '08', borderRadius: 10, padding: 12, marginBottom: 14, borderLeftWidth: 3, borderLeftColor: BRAND.purple + '40' }}>
                      <Text style={{ fontSize: 13, color: BRAND.sub, lineHeight: 20 }}>{q.hint}</Text>
                    </View>
                  )}
                  {q.type === 'text' ? (
                    <TextInput
                      style={{ backgroundColor: BRAND.bg, borderRadius: 14, padding: 16, fontSize: 14, color: BRAND.text, minHeight: 110, textAlignVertical: 'top', borderWidth: 1.5, borderColor: mentorInput ? BRAND.purple : BRAND.line, lineHeight: 22 }}
                      placeholder="Type your answer here…"
                      placeholderTextColor={BRAND.sub}
                      multiline
                      value={mentorInput}
                      onChangeText={setMentorInput}
                    />
                  ) : (
                    <View style={{ gap: 9 }}>
                      {q.options.map((opt, oi) => {
                        const sel = mentorSelected.includes(opt);
                        return (
                          <TouchableOpacity key={oi} onPress={() => toggleMentorChip(opt)}
                            style={{ flexDirection: 'row', alignItems: 'center', gap: 13, padding: 15, borderRadius: 14, borderWidth: 1.5, borderColor: sel ? BRAND.purple : BRAND.line, backgroundColor: sel ? BRAND.purple + '08' : BRAND.white }}>
                            <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: sel ? BRAND.purple + '20' : BRAND.bg, alignItems: 'center', justifyContent: 'center' }}>
                              <Ionicons name={q.icons?.[oi] || 'ellipse-outline'} size={17} color={sel ? BRAND.purple : BRAND.sub} />
                            </View>
                            <Text style={{ flex: 1, fontSize: 14, color: sel ? BRAND.purple : BRAND.text, fontWeight: sel ? '700' : '500' }}>{opt}</Text>
                            {sel && <Ionicons name="checkmark-circle" size={22} color={BRAND.purple} />}
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}
                  <View style={{ flexDirection: 'row', gap: 10, marginTop: 22 }}>
                    {mentorStep > 1 && (
                      <TouchableOpacity onPress={() => { setMentorStep(mentorStep - 1); setMentorInput(''); setMentorSelected([]); }}
                        style={{ flex: 1, paddingVertical: 14, borderRadius: 14, borderWidth: 1, borderColor: BRAND.line, alignItems: 'center' }}>
                        <Text style={{ color: BRAND.sub, fontWeight: '600' }}>← Back</Text>
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity onPress={handleMentorNext} style={{ flex: 2, borderRadius: 14, overflow: 'hidden' }}>
                      <LinearGradient colors={[BRAND.purple, BRAND.blue]} style={{ paddingVertical: 14, alignItems: 'center' }}>
                        <Text style={{ color: '#fff', fontWeight: '800', fontSize: 15 }}>{mentorStep === 9 ? 'Generate My Ideas →' : 'Next →'}</Text>
                      </LinearGradient>
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              {/* ── GENERATING ── */}
              {mentorStep === 10 && (
                <View style={{ alignItems: 'center', paddingTop: 50 }}>
                  <ActivityIndicator size="large" color={BRAND.purple} />
                  <Text style={{ fontSize: 17, fontWeight: '700', color: BRAND.text, marginTop: 20, marginBottom: 8 }}>Working on it…</Text>
                  <Text style={{ fontSize: 14, color: BRAND.sub, textAlign: 'center', lineHeight: 22 }}>{mentorLoadMsg || 'Building your personalised business plan…'}</Text>
                </View>
              )}

              {/* ── RESULTS ── */}
              {mentorStep === 11 && mentorProfile && (
                <View>
                  {/* Profile summary card */}
                  <View style={{ backgroundColor: BRAND.purple + '08', borderRadius: 18, padding: 18, marginBottom: 20, borderWidth: 1, borderColor: BRAND.purple + '20' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: BRAND.purple + '20', alignItems: 'center', justifyContent: 'center' }}>
                        <Ionicons name="person-outline" size={17} color={BRAND.purple} />
                      </View>
                      <View>
                        <Text style={{ fontSize: 10, color: BRAND.purple, fontWeight: '700', textTransform: 'uppercase' }}>Entrepreneur Type</Text>
                        <Text style={{ fontSize: 15, fontWeight: '800', color: BRAND.text }}>{mentorProfile.entrepreneur_type}</Text>
                      </View>
                    </View>
                    <Text style={{ fontSize: 13, color: BRAND.sub, lineHeight: 21, marginBottom: 10 }}>{mentorProfile.summary}</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      {(mentorProfile.strengths || []).map((s, i) => (
                        <View key={i} style={{ backgroundColor: BRAND.success + '15', borderRadius: 8, paddingHorizontal: 9, paddingVertical: 4 }}>
                          <Text style={{ fontSize: 12, color: BRAND.success, fontWeight: '600' }}>✓ {s}</Text>
                        </View>
                      ))}
                      {(mentorProfile.constraints || []).map((c, i) => (
                        <View key={i} style={{ backgroundColor: BRAND.warning + '15', borderRadius: 8, paddingHorizontal: 9, paddingVertical: 4 }}>
                          <Text style={{ fontSize: 12, color: BRAND.warning, fontWeight: '600' }}>⚑ {c}</Text>
                        </View>
                      ))}
                    </View>
                  </View>

                  <Text style={{ fontSize: 16, fontWeight: '800', color: BRAND.text, marginBottom: 12 }}>Your 5 Custom Business Ideas</Text>

                  {mentorIdeas.map((idea, idx) => (
                    <View key={idx} style={{ backgroundColor: BRAND.white, borderRadius: 18, marginBottom: 12, borderWidth: 1, borderColor: BRAND.line, overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 6, elevation: 2 }}>
                      <TouchableOpacity onPress={() => handleMentorExpandIdea(idx)} style={{ padding: 16 }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <View style={{ flex: 1, paddingRight: 8 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                              <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: BRAND.purple + '15', alignItems: 'center', justifyContent: 'center' }}>
                                <Text style={{ fontSize: 11, fontWeight: '800', color: BRAND.purple }}>{idx + 1}</Text>
                              </View>
                              <View style={{ backgroundColor: (riskColor[idea.risk_level] || BRAND.sub) + '15', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 }}>
                                <Text style={{ fontSize: 11, fontWeight: '700', color: riskColor[idea.risk_level] || BRAND.sub }}>{idea.risk_level || 'Low'} Risk</Text>
                              </View>
                              <View style={{ backgroundColor: BRAND.success + '15', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 }}>
                                <Text style={{ fontSize: 11, fontWeight: '700', color: BRAND.success }}>{idea.startup_cost || 'Low <£500'}</Text>
                              </View>
                            </View>
                            <Text style={{ fontSize: 15, fontWeight: '800', color: BRAND.text, marginBottom: 4 }}>{idea.name}</Text>
                            <Text style={{ fontSize: 13, color: BRAND.sub, lineHeight: 19 }}>{idea.concept}</Text>
                          </View>
                          <Ionicons name={mentorExpanded === idx ? 'chevron-up' : 'chevron-down'} size={20} color={BRAND.sub} />
                        </View>
                      </TouchableOpacity>

                      {mentorExpanded === idx && (
                        <View style={{ borderTopWidth: 1, borderTopColor: BRAND.line, padding: 16 }}>
                          {[
                            { label: '🎯 Problem Solved',  value: idea.problem },
                            { label: '👤 Target Customer', value: idea.customer },
                            { label: '💰 Revenue Model',   value: idea.revenue_model },
                            { label: '⏱ Time to Profit',   value: idea.time_to_profit },
                            { label: '✅ Why It Fits You', value: idea.fit_reason },
                          ].map((row, ri) => (
                            <View key={ri} style={{ marginBottom: 11 }}>
                              <Text style={{ fontSize: 11, fontWeight: '700', color: BRAND.purple, marginBottom: 2 }}>{row.label}</Text>
                              <Text style={{ fontSize: 13, color: BRAND.text, lineHeight: 20 }}>{row.value || '—'}</Text>
                            </View>
                          ))}
                          {mentorIdeaDetail[idx] ? (
                            <View style={{ backgroundColor: BRAND.bg, borderRadius: 12, padding: 14, marginTop: 4 }}>
                              <Text style={{ fontSize: 12, fontWeight: '700', color: BRAND.blue, marginBottom: 6 }}>Deep Analysis</Text>
                              <Text style={{ fontSize: 13, color: BRAND.text, lineHeight: 21 }}>{mentorIdeaDetail[idx]}</Text>
                            </View>
                          ) : (
                            <View style={{ backgroundColor: BRAND.bg, borderRadius: 10, padding: 12, alignItems: 'center', marginTop: 4 }}>
                              <ActivityIndicator size="small" color={BRAND.purple} />
                              <Text style={{ fontSize: 12, color: BRAND.sub, marginTop: 6 }}>Loading deep analysis…</Text>
                            </View>
                          )}
                        </View>
                      )}
                    </View>
                  ))}

                  <TouchableOpacity onPress={handleMentorRoadmap} style={{ borderRadius: 16, overflow: 'hidden', marginTop: 6 }}>
                    <LinearGradient colors={[BRAND.success, BRAND.mint]} style={{ paddingVertical: 16, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 10 }}>
                      <Ionicons name="map-outline" size={20} color="#fff" />
                      <Text style={{ color: '#fff', fontSize: 15, fontWeight: '800' }}>Build My 30-Day Roadmap →</Text>
                    </LinearGradient>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => { setMentorStep(0); setMentorAnswers({}); setMentorProfile(null); setMentorIdeas([]); setMentorIdeaDetail({}); setMentorRoadmap(null); setMentorExpanded(null); }}
                    style={{ alignItems: 'center', paddingVertical: 14 }}>
                    <Text style={{ fontSize: 13, color: BRAND.sub }}>Start Over</Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* ── ROADMAP ── */}
              {mentorStep === 12 && (
                <View>
                  {mentorLoading ? (
                    <View style={{ alignItems: 'center', paddingTop: 50 }}>
                      <ActivityIndicator size="large" color={BRAND.success} />
                      <Text style={{ fontSize: 15, color: BRAND.sub, marginTop: 16, textAlign: 'center' }}>{mentorLoadMsg}</Text>
                    </View>
                  ) : (
                    <>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: BRAND.success + '15', alignItems: 'center', justifyContent: 'center' }}>
                          <Ionicons name="map-outline" size={20} color={BRAND.success} />
                        </View>
                        <View>
                          <Text style={{ fontSize: 10, color: BRAND.success, fontWeight: '700', textTransform: 'uppercase' }}>Personalised Plan</Text>
                          <Text style={{ fontSize: 16, fontWeight: '800', color: BRAND.text }}>Your 30-Day Roadmap</Text>
                        </View>
                      </View>
                      <View style={{ backgroundColor: BRAND.bg, borderRadius: 16, padding: 18, borderLeftWidth: 4, borderLeftColor: BRAND.success }}>
                        <Text style={{ fontSize: 14, color: BRAND.text, lineHeight: 24 }}>{mentorRoadmap}</Text>
                      </View>
                      <View style={{ flexDirection: 'row', gap: 10, marginTop: 18 }}>
                        <TouchableOpacity onPress={() => setMentorStep(11)}
                          style={{ flex: 1, paddingVertical: 14, borderRadius: 14, borderWidth: 1, borderColor: BRAND.line, alignItems: 'center' }}>
                          <Text style={{ color: BRAND.sub, fontWeight: '600' }}>← Ideas</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => Share.share({ message: `My 30-Day Business Roadmap\n\n${mentorRoadmap}`, title: 'Business Roadmap' })}
                          style={{ flex: 2, borderRadius: 14, overflow: 'hidden' }}>
                          <LinearGradient colors={[BRAND.blue, BRAND.purple]} style={{ paddingVertical: 14, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 8 }}>
                            <Ionicons name="share-outline" size={16} color="#fff" />
                            <Text style={{ color: '#fff', fontWeight: '800' }}>Share Roadmap</Text>
                          </LinearGradient>
                        </TouchableOpacity>
                      </View>
                    </>
                  )}
                </View>
              )}

            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  }


  // ── Innovation Ideas list + submit — helpers ─────────────────────────────
  async function loadIdeasData() {
    setIdeasLoading(true);
    try {
      const [ideasRes, statsRes] = await Promise.all([
        innovationService.getIdeas(ideasFilter !== 'all' ? { review_status: ideasFilter } : {}),
        innovationService.getStats(),
      ]);
      if (ideasRes?.success) setSubmittedIdeas(ideasRes.data || []);
      if (statsRes?.success) setIdeasStats(statsRes.data);
    } catch { setSubmittedIdeas([]); }
    finally { setIdeasLoading(false); }
  }

  function renderSubmitIdeaModal() {
    return (
      <SubmitIdeaModal
        visible={showSubmitIdeaModal}
        onClose={() => setShowSubmitIdeaModal(false)}
        onSubmitted={() => { loadIdeasData(); }}
      />
    );
  }

  function renderIdeasListModal() {
    return (
      <IdeasListModal
        visible={showIdeasListModal}
        onClose={() => setShowIdeasListModal(false)}
        onSubmitNew={() => {
          setIdeaForm({ idea_title:'', idea_description:'', innovation_type:'technology', customer_pain_point:'', proposed_solution:'', expected_benefits:'', potential_impact:'MEDIUM', effort_required:'medium', estimated_cost:'', estimated_timeline:'', similar_existing_solutions:'', unique_aspects:'', supporting_data:'' });
          setShowSubmitIdeaModal(true);
        }}
      />
    );
  }

  // ── DEEP SEARCH MODAL ─────────────────────────────────────────────────────
  function renderDeepSearchModal() {
    const TABS = [
      { label: '🛠 Implement',  key: 'implementation', color: BRAND.blue   },
      { label: '🔬 Feasibility',key: 'feasibility',    color: '#10b981'    },
      { label: '📊 Market',     key: 'market',         color: '#f59e0b'    },
      { label: '⚠️ Risks',      key: 'risks',          color: '#ef4444'    },
      { label: '💰 Funding',    key: 'funding',        color: '#8b5cf6'    },
    ];

    const PHASES = [
      { num: 1, label: 'Implementation Plan',  icon: 'build-outline'       },
      { num: 2, label: 'Feasibility Study',    icon: 'flask-outline'       },
      { num: 3, label: 'Market Research',      icon: 'bar-chart-outline'   },
      { num: 4, label: 'Risk Analysis',        icon: 'warning-outline'     },
      { num: 5, label: 'Funding Strategy',     icon: 'cash-outline'        },
    ];

    const activeTab   = TABS[deepSearchTab];
    const resultText  = deepSearchResults?.[activeTab?.key] || '';
    const isLoading   = deepSearchLoading;
    const isDone      = !isLoading && deepSearchResults;

    return (
      <Modal visible={showDeepSearchModal} transparent animationType="slide"
        onRequestClose={() => { if (!isLoading) setShowDeepSearchModal(false); }}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: BRAND.bg, borderTopLeftRadius: 28, borderTopRightRadius: 28, height: height * 0.95 }}>

            {/* ── Header ──────────────────────────────────────────────── */}
            <LinearGradient colors={['#0e4e96', '#19abb5']}
              style={{ borderTopLeftRadius: 28, borderTopRightRadius: 28, padding: 20, paddingBottom: 16 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
                  <View style={{ width: 42, height: 42, borderRadius: 21, backgroundColor: 'rgba(255,255,255,0.2)',
                                 alignItems: 'center', justifyContent: 'center' }}>
                    <Ionicons name="search" size={22} color="#fff" />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: '#fff', fontSize: 17, fontWeight: '800' }}>Deep Search</Text>
                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, marginTop: 2 }} numberOfLines={1}>
                      {deepSearchIdea?.title || 'Innovation Analysis'}
                    </Text>
                  </View>
                </View>
                {!isLoading && (
                  <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    {isDone && (
                      <TouchableOpacity onPress={handleDeepSearchExportPDF} disabled={pdfLoading}
                        style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 20, paddingHorizontal: 12,
                                 paddingVertical: 7, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        {pdfLoading
                          ? <ActivityIndicator size="small" color="#fff" />
                          : <><Ionicons name="document-attach-outline" size={15} color="#fff" /><Text style={{ color: '#fff', fontSize: 12, fontWeight: '700' }}>PDF</Text></>}
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity onPress={() => setShowDeepSearchModal(false)}>
                      <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Phase progress bar when loading */}
              {isLoading && (
                <View style={{ marginTop: 16 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <ActivityIndicator size="small" color="#fdb913" />
                    <Text style={{ color: '#fdb913', fontSize: 13, fontWeight: '700' }}>{deepSearchPhaseMsg}</Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 4 }}>
                    {PHASES.map(p => (
                      <View key={p.num} style={{ flex: 1, height: 5, borderRadius: 3,
                        backgroundColor: deepSearchPhase >= p.num ? '#fdb913' : 'rgba(255,255,255,0.25)' }} />
                    ))}
                  </View>
                  <View style={{ flexDirection: 'row', marginTop: 8 }}>
                    {PHASES.map(p => (
                      <View key={p.num} style={{ flex: 1, alignItems: 'center' }}>
                        <Ionicons name={p.icon}
                          size={14} color={deepSearchPhase >= p.num ? '#fdb913' : 'rgba(255,255,255,0.35)'} />
                        <Text style={{ fontSize: 8, color: deepSearchPhase >= p.num ? '#fdb913' : 'rgba(255,255,255,0.35)',
                          fontWeight: '700', marginTop: 2, textAlign: 'center' }} numberOfLines={2}>
                          {p.label}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Tab bar when results are available */}
              {isDone && (
                <ScrollView horizontal showsHorizontalScrollIndicator={false}
                  style={{ marginTop: 14 }}
                  contentContainerStyle={{ gap: 6, flexDirection: 'row' }}>
                  {TABS.map((t, i) => (
                    <TouchableOpacity key={i} onPress={() => { setDeepSearchTab(i); deepSearchScrollRef.current?.scrollTo({ y: 0, animated: true }); }}
                      style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20,
                        backgroundColor: deepSearchTab === i ? '#fff' : 'rgba(255,255,255,0.15)',
                        borderWidth: 1, borderColor: deepSearchTab === i ? '#fff' : 'transparent' }}>
                      <Text style={{ fontSize: 12, fontWeight: '700',
                        color: deepSearchTab === i ? t.color : 'rgba(255,255,255,0.75)' }}>
                        {t.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}
            </LinearGradient>

            {/* ── Loading state ────────────────────────────────────────── */}
            {isLoading && (
              <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
                <View style={{ width: 80, height: 80, borderRadius: 40,
                               backgroundColor: BRAND.blue + '15', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <Ionicons name={PHASES[Math.max(0, deepSearchPhase - 1)]?.icon || 'search'} size={36} color={BRAND.blue} />
                </View>
                <Text style={{ fontSize: 20, fontWeight: '800', color: BRAND.text, marginBottom: 8 }}>
                  Running Deep Search
                </Text>
                <Text style={{ fontSize: 14, color: BRAND.sub, textAlign: 'center', lineHeight: 22, marginBottom: 32 }}>
                  Claude AI is conducting {PHASES.length} research phases.{'\n'}This takes 30–60 seconds.
                </Text>
                <View style={{ width: '100%', gap: 10 }}>
                  {PHASES.map(p => {
                    const done    = deepSearchPhase >  p.num;
                    const active  = deepSearchPhase === p.num;
                    return (
                      <View key={p.num} style={{ flexDirection: 'row', alignItems: 'center', gap: 12,
                        backgroundColor: done ? BRAND.success + '10' : active ? BRAND.blue + '10' : BRAND.bg,
                        borderRadius: 12, padding: 12, borderWidth: 1,
                        borderColor: done ? BRAND.success + '30' : active ? BRAND.blue + '30' : BRAND.line }}>
                        <View style={{ width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center',
                          backgroundColor: done ? BRAND.success : active ? BRAND.blue : BRAND.line }}>
                          {done
                            ? <Ionicons name="checkmark" size={18} color="#fff" />
                            : active
                              ? <ActivityIndicator size="small" color="#fff" />
                              : <Text style={{ fontSize: 12, fontWeight: '800', color: '#fff' }}>{p.num}</Text>}
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 14, fontWeight: '700',
                            color: done ? BRAND.success : active ? BRAND.blue : BRAND.sub }}>
                            {p.label}
                          </Text>
                          <Text style={{ fontSize: 11, color: BRAND.sub, marginTop: 1 }}>
                            {done ? 'Complete ✓' : active ? deepSearchPhaseMsg : 'Queued'}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            )}

            {/* ── Results ──────────────────────────────────────────────── */}
            {isDone && (
              <ScrollView ref={deepSearchScrollRef} style={{ flex: 1 }}
                contentContainerStyle={{ padding: 20, paddingBottom: 40 }}
                showsVerticalScrollIndicator={false}>

                {/* Tab header */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16,
                  backgroundColor: activeTab.color + '10', borderRadius: 14, padding: 14,
                  borderLeftWidth: 4, borderLeftColor: activeTab.color }}>
                  <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: activeTab.color + '20',
                    alignItems: 'center', justifyContent: 'center' }}>
                    <Ionicons name={PHASES[deepSearchTab]?.icon || 'document-text-outline'} size={18} color={activeTab.color} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: '800', color: activeTab.color }}>
                      {activeTab.label.replace(/^[^\w]+/, '')}
                    </Text>
                    <Text style={{ fontSize: 11, color: BRAND.sub, marginTop: 1 }}>
                      Generated by Claude AI · {deepSearchIdea?.industry || 'Engineering'} sector
                    </Text>
                  </View>
                  <TouchableOpacity onPress={() => Share.share({ message: `${activeTab.label}\n\n${resultText}`, title: `${deepSearchIdea?.title} — ${activeTab.label}` })}
                    style={{ padding: 8 }}>
                    <Ionicons name="share-outline" size={20} color={activeTab.color} />
                  </TouchableOpacity>
                </View>

                {/* Result text — render with section highlighting */}
                {resultText.split('\n').map((line, i) => {
                  const isSection = /^[A-Z][A-Z\s&:\/]{4,}:/.test(line.trim()) || /^\d+\.\s+[A-Z]/.test(line.trim());
                  const isBullet  = /^[-•·]/.test(line.trim()) || /^\d+[\.\)]\s/.test(line.trim());
                  const isEmpty   = line.trim() === '';
                  if (isEmpty) return <View key={i} style={{ height: 8 }} />;
                  if (isSection) return (
                    <View key={i} style={{ marginTop: 14, marginBottom: 6, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <View style={{ width: 4, height: 18, borderRadius: 2, backgroundColor: activeTab.color }} />
                      <Text style={{ fontSize: 13, fontWeight: '800', color: activeTab.color, flex: 1 }}>{line.trim()}</Text>
                    </View>
                  );
                  if (isBullet) return (
                    <View key={i} style={{ flexDirection: 'row', gap: 8, marginBottom: 4, paddingLeft: 8 }}>
                      <Text style={{ fontSize: 13, color: activeTab.color, marginTop: 2 }}>•</Text>
                      <Text style={{ fontSize: 13, color: BRAND.text, lineHeight: 21, flex: 1 }}>{line.replace(/^[-•·\d\.\)]\s*/, '').trim()}</Text>
                    </View>
                  );
                  return (
                    <Text key={i} style={{ fontSize: 13, color: BRAND.text, lineHeight: 22, marginBottom: 2 }}>
                      {line}
                    </Text>
                  );
                })}

                {/* Bottom actions */}
                <View style={{ marginTop: 24, gap: 10 }}>
                  <TouchableOpacity onPress={handleDeepSearchExportPDF} disabled={pdfLoading}
                    style={{ borderRadius: 14, overflow: 'hidden' }}>
                    <LinearGradient colors={['#0e4e96', '#19abb5']}
                      style={{ paddingVertical: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                      {pdfLoading
                        ? <ActivityIndicator color="#fff" />
                        : <><Ionicons name="document-attach-outline" size={18} color="#fff" />
                          <Text style={{ color: '#fff', fontWeight: '800', fontSize: 15 }}>
                            Export Full Report (5 sections) as PDF
                          </Text></>}
                    </LinearGradient>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => { setShowDeepSearchModal(false); if (deepSearchIdea) handleGenerateDetailedBusinessPlan(deepSearchIdea); }}
                    style={{ borderRadius: 14, overflow: 'hidden' }}>
                    <LinearGradient colors={['#8b5cf6', '#6d28d9']}
                      style={{ paddingVertical: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                      <Ionicons name="document-text-outline" size={18} color="#fff" />
                      <Text style={{ color: '#fff', fontWeight: '800', fontSize: 15 }}>
                        Generate Full Business Plan
                      </Text>
                    </LinearGradient>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => { if (deepSearchIdea) { setSelectedInnovation(deepSearchIdea); setShowDeepSearchModal(false); setShowDetailModal(true); } }}
                    style={{ paddingVertical: 13, borderRadius: 14, borderWidth: 1, borderColor: BRAND.line,
                             flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Ionicons name="arrow-back-outline" size={16} color={BRAND.sub} />
                    <Text style={{ color: BRAND.sub, fontWeight: '600' }}>Back to Idea</Text>
                  </TouchableOpacity>
                </View>

              </ScrollView>
            )}

          </View>
        </View>
      </Modal>
    );
  }

}


// ────────────────────────────────────────────────────────────────────────────
// INNOVATION IDEA SUBMISSION — CONSTANTS
// ────────────────────────────────────────────────────────────────────────────
const IDEA_TYPES = [
  { id:'technology',  label:'Technology',   icon:'hardware-chip-outline',  color:'#6366f1' },
  { id:'process',     label:'Process',      icon:'git-branch-outline',     color:BRAND.mint },
  { id:'efficiency',  label:'Efficiency',   icon:'speedometer-outline',    color:BRAND.success },
  { id:'automation',  label:'Automation',   icon:'settings-outline',       color:BRAND.warning },
  { id:'wellness',    label:'Wellness',     icon:'heart-outline',          color:BRAND.pink },
  { id:'product',     label:'Product',      icon:'cube-outline',           color:BRAND.blue },
  { id:'service',     label:'Service',      icon:'briefcase-outline',      color:'#0891b2' },
  { id:'other',       label:'Other',        icon:'ellipsis-horizontal',    color:BRAND.sub },
];
const IDEA_IMPACTS = [
  { id:'LOW',      label:'Low',      color:BRAND.sub,     icon:'arrow-down-outline' },
  { id:'MEDIUM',   label:'Medium',   color:BRAND.warning, icon:'remove-outline'     },
  { id:'HIGH',     label:'High',     color:BRAND.success, icon:'arrow-up-outline'   },
  { id:'CRITICAL', label:'Critical', color:BRAND.danger,  icon:'flash-outline'      },
];
const IDEA_EFFORTS = [
  { id:'low',    label:'Low',    color:BRAND.success, desc:'< 1 month'  },
  { id:'medium', label:'Medium', color:BRAND.warning, desc:'1–3 months' },
  { id:'high',   label:'High',   color:BRAND.danger,  desc:'3+ months'  },
];
const IDEA_DEPTS = [
  'Engineering','Operations','IT','R&D','Product',
  'Finance','HR','Sales','Marketing','Other',
];
const IDEA_STATUSES = {
  approved:'#4CAF50', pending:'#FFC107', under_review:'#2196F3',
  rejected:'#F44336', draft:'#9E9E9E',
};
const IDEA_STATUS_LABELS = {
  approved:'Approved', pending:'Pending', under_review:'Under Review',
  rejected:'Rejected', draft:'Draft',
};
const IDEA_SORT_OPTS = [
  { id:'newest', label:'Newest First'   },
  { id:'oldest', label:'Oldest First'   },
  { id:'score',  label:'Highest Score'  },
  { id:'impact', label:'High Impact'    },
  { id:'cost',   label:'Lowest Cost'    },
  { id:'roi',    label:'Best ROI'       },
];
const IDEA_FILTERS = [
  { value:'all',          label:'All',          color:BRAND.blue   },
  { value:'approved',     label:'Approved',     color:'#4CAF50'    },
  { value:'pending',      label:'Pending',      color:'#FFC107'    },
  { value:'under_review', label:'In Review',    color:'#2196F3'    },
  { value:'rejected',     label:'Rejected',     color:'#F44336'    },
];
const IDEA_DRAFT_STORAGE_KEY = 'jobeng_submit_idea_draft_v1';

// ────────────────────────────────────────────────────────────────────────────
// SUBMIT INNOVATION IDEA — FULL 4-STEP MODAL
// ────────────────────────────────────────────────────────────────────────────
function SubmitIdeaModal({ visible, onClose, onSubmitted }) {
  const EMPTY = {
    idea_title:'', idea_description:'', innovation_type:'technology',
    customer_pain_point:'', proposed_solution:'', expected_benefits:'',
    potential_impact:'MEDIUM', effort_required:'medium',
    department:'Engineering', target_users:'', success_metrics:'',
    estimated_cost:'', estimated_roi:'', estimated_timeline:'',
    similar_existing_solutions:'', unique_aspects:'', supporting_data:'', tags:'',
  };

  const [form,        setForm]        = React.useState(EMPTY);
  const [errors,      setErrors]      = React.useState({});
  const [step,        setStep]        = React.useState(1);
  const [loading,     setLoading]     = React.useState(false);
  const [aiField,     setAiField]     = React.useState('');
  const [hasDraft,    setHasDraft]    = React.useState(false);
  const [doneScreen,  setDoneScreen]  = React.useState(false);
  const [doneRef,     setDoneRef]     = React.useState(null);
  const scrollRef = React.useRef(null);

  const STEPS = [
    { n:1, label:'Basics',   icon:'information-circle-outline' },
    { n:2, label:'Solution', icon:'bulb-outline'               },
    { n:3, label:'Details',  icon:'list-outline'               },
    { n:4, label:'Review',   icon:'eye-outline'                },
  ];

  // ── lifecycle ───────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (visible) { checkDraft(); setDoneScreen(false); setErrors({}); }
  }, [visible]);

  const checkDraft = async () => {
    try {
      const raw = await AsyncStorage.getItem(IDEA_DRAFT_STORAGE_KEY);
      if (raw) { const d = JSON.parse(raw); if (d?.idea_title || d?.idea_description) setHasDraft(true); }
    } catch {}
  };
  const restoreDraft = async () => {
    try {
      const raw = await AsyncStorage.getItem(IDEA_DRAFT_STORAGE_KEY);
      if (raw) { setForm(p => ({ ...p, ...JSON.parse(raw) })); }
    } catch {} finally { setHasDraft(false); }
  };
  const saveDraft = async () => {
    try { await AsyncStorage.setItem(IDEA_DRAFT_STORAGE_KEY, JSON.stringify(form)); } catch {}
  };
  const clearDraft = async () => {
    try { await AsyncStorage.removeItem(IDEA_DRAFT_STORAGE_KEY); } catch {}
  };

  // ── form helpers ────────────────────────────────────────────────────────
  const upd = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => { const e={...p}; delete e[k]; return e; }); };

  const pct = React.useMemo(() => {
    const keys = ['idea_title','idea_description','customer_pain_point','proposed_solution','expected_benefits','unique_aspects'];
    return Math.round(keys.filter(k => form[k]?.trim()).length / keys.length * 100);
  }, [form]);

  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.idea_title.trim())               e.idea_title       = 'Title is required';
      if (form.idea_title.length > 120)          e.idea_title       = 'Max 120 characters';
      if (!form.idea_description.trim())         e.idea_description = 'Description is required';
    }
    if (s === 2) {
      if (!form.customer_pain_point.trim())      e.customer_pain_point = 'Describe the pain point';
      if (!form.proposed_solution.trim())        e.proposed_solution   = 'Describe your solution';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validate(step)) return;
    const next = Math.min(step + 1, 4);
    setStep(next);
    scrollRef.current?.scrollTo({ y:0, animated:true });
    if (step >= 2) saveDraft();
  };
  const goBack = () => { setStep(s => Math.max(s-1,1)); scrollRef.current?.scrollTo({ y:0, animated:true }); };

  // ── AI assist ────────────────────────────────────────────────────────────
  const aiAssist = async (fieldKey) => {
    if (!form.idea_title.trim()) { Alert.alert('Title Required','Enter an idea title first so AI can assist.'); return; }
    setAiField(fieldKey);
    try {
      const ctx = `Innovation idea: "${form.idea_title}" (${form.innovation_type})\nDescription: ${form.idea_description||'none yet'}`;
      const prompts = {
        idea_description:      `${ctx}\n\nWrite a compelling 2–3 sentence description of this engineering innovation. Be specific and practical. Return ONLY the description text.`,
        customer_pain_point:   `${ctx}\nWrite a clear 2–3 sentence customer/operational pain point for this idea. Name the affected team or system. Return ONLY the pain point text.`,
        proposed_solution:     `${ctx}\nPain point: ${form.customer_pain_point}\nWrite a clear 2–3 sentence proposed solution explaining the mechanism. Return ONLY the solution text.`,
        expected_benefits:     `${ctx}\nSolution: ${form.proposed_solution}\nList 3–4 quantified expected benefits with specific numbers. Return ONLY the benefits text.`,
        unique_aspects:        `${ctx}\nWrite 2–3 sentences on what makes this uniquely differentiated from alternatives. Return ONLY the text.`,
        success_metrics:       `${ctx}\nWrite 3–4 specific measurable KPIs with target values and timeframes. Return ONLY the metrics text.`,
      };
      if (!prompts[fieldKey]) return;
      const text = await callClaude(prompts[fieldKey], 300);
      if (text?.trim()) upd(fieldKey, text.trim());
    } catch { Alert.alert('AI Assist','Unavailable — please fill in manually.'); }
    finally { setAiField(''); }
  };

  // ── submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate(1) || !validate(2)) { Alert.alert('Incomplete','Complete required fields in Steps 1 & 2.'); setStep(1); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : undefined,
        estimated_roi:  form.estimated_roi  ? Number(form.estimated_roi)  : undefined,
        tags: form.tags ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : [],
      };
      const res = await innovationService.createIdea(payload);
      await clearDraft();
      setDoneRef(res?.data?.idea_code || res?.data?.id || null);
      setDoneScreen(true);
      onSubmitted?.();
    } catch {
      await clearDraft();
      setDoneRef(null);
      setDoneScreen(true);
      onSubmitted?.();
    } finally { setLoading(false); }
  };

  const reset = () => { setForm(EMPTY); setStep(1); setDoneScreen(false); setErrors({}); setHasDraft(false); };
  const handleClose = () => { onClose(); setTimeout(reset, 400); };

  // ── sub-components (defined inside so they close over form/upd) ─────────
  const Field = ({ label, fkey, placeholder, multiline, kbType, required, maxLen, hint, canAI }) => {
    const err = errors[fkey];
    const len = form[fkey]?.length || 0;
    return (
      <View style={{ marginTop:16 }}>
        <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
          <Text style={{ fontSize:13, fontWeight:'700', color:BRAND.dark }}>
            {label}{required && <Text style={{ color:BRAND.danger }}> *</Text>}
          </Text>
          <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
            {maxLen && (
              <Text style={{ fontSize:11, color: len > maxLen*0.85 ? BRAND.danger : BRAND.sub }}>{len}/{maxLen}</Text>
            )}
            {canAI && (
              <TouchableOpacity onPress={() => aiAssist(fkey)} disabled={!!aiField}
                style={{ backgroundColor:BRAND.claude+'18', borderRadius:8, paddingHorizontal:8, paddingVertical:4,
                         flexDirection:'row', alignItems:'center', gap:4 }}>
                {aiField===fkey
                  ? <ActivityIndicator size={10} color={BRAND.claude} />
                  : <MaterialCommunityIcons name="brain" size={12} color={BRAND.claude} />}
                <Text style={{ fontSize:11, color:BRAND.claude, fontWeight:'700' }}>AI Assist</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        {hint && <Text style={{ fontSize:12, color:BRAND.sub, marginBottom:5 }}>{hint}</Text>}
        <TextInput
          style={{ backgroundColor:'#fff', borderRadius:12, padding:13, fontSize:14,
                   borderWidth:1.5, borderColor: err ? BRAND.danger : form[fkey] ? BRAND.blue+'55' : BRAND.line,
                   minHeight: multiline ? 92 : 48, textAlignVertical: multiline ? 'top':'center',
                   color:BRAND.text }}
          value={form[fkey]}
          onChangeText={t => upd(fkey, maxLen ? t.slice(0,maxLen) : t)}
          placeholder={placeholder} placeholderTextColor="#cbd5e1"
          multiline={!!multiline} numberOfLines={multiline?4:1} keyboardType={kbType||'default'}
        />
        {err && <Text style={{ fontSize:12, color:BRAND.danger, marginTop:4 }}>⚠ {err}</Text>}
      </View>
    );
  };

  const TypeRow = ({ label, items, fkey }) => (
    <View style={{ marginTop:16 }}>
      <Text style={{ fontSize:13, fontWeight:'700', color:BRAND.dark, marginBottom:9 }}>{label}</Text>
      <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
        {items.map(item => {
          const id  = typeof item==='string' ? item : item.id;
          const lbl = typeof item==='string' ? item : (item.label||item.name||id);
          const col = typeof item==='object' ? (item.color||BRAND.blue) : BRAND.blue;
          const ico = typeof item==='object' ? item.icon : null;
          const sel = form[fkey] === id;
          return (
            <TouchableOpacity key={id} onPress={() => upd(fkey, id)}
              style={{ paddingHorizontal:14, paddingVertical:9, borderRadius:22,
                       borderWidth:1.5, borderColor: sel ? col : BRAND.line,
                       backgroundColor: sel ? col+'18' : '#fff',
                       flexDirection:'row', alignItems:'center', gap:6 }}>
              {ico && <Ionicons name={ico} size={14} color={sel ? col : BRAND.sub} />}
              <Text style={{ fontSize:13, fontWeight: sel?'700':'500', color: sel ? col : BRAND.sub }}>{lbl}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );

  const ReviewLine = ({ label, value, color }) => !value ? null : (
    <View style={{ paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#f1f5f9' }}>
      <Text style={{ fontSize:10, color:BRAND.sub, fontWeight:'700', textTransform:'uppercase', marginBottom:3 }}>{label}</Text>
      <Text style={{ fontSize:14, color: color||BRAND.text, lineHeight:20 }}>{value}</Text>
    </View>
  );

  // ── success screen ────────────────────────────────────────────────────────
  if (doneScreen) return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={{ flex:1, backgroundColor:'rgba(0,0,0,0.6)', justifyContent:'center', alignItems:'center', padding:28 }}>
        <View style={{ backgroundColor:'#fff', borderRadius:28, padding:36, width:'100%', alignItems:'center' }}>
          <View style={{ width:80, height:80, borderRadius:40, backgroundColor:BRAND.success+'18',
                         alignItems:'center', justifyContent:'center', marginBottom:18 }}>
            <Ionicons name="checkmark-circle" size={52} color={BRAND.success} />
          </View>
          <Text style={{ fontSize:22, fontWeight:'800', color:BRAND.dark, marginBottom:8 }}>Idea Submitted!</Text>
          <Text style={{ fontSize:14, color:BRAND.sub, textAlign:'center', lineHeight:22 }}>
            Your innovation has been submitted for review. You'll be notified when a decision is made.
          </Text>
          {doneRef && (
            <View style={{ backgroundColor:BRAND.blue+'12', borderRadius:10, paddingHorizontal:14,
                           paddingVertical:8, marginTop:14 }}>
              <Text style={{ fontSize:12, color:BRAND.blue, fontWeight:'800' }}>Ref: {doneRef}</Text>
            </View>
          )}
          <View style={{ width:'100%', gap:10, marginTop:22 }}>
            <TouchableOpacity onPress={reset} style={{ borderRadius:14, overflow:'hidden' }}>
              <LinearGradient colors={['#e91e63','#c2185b']}
                style={{ paddingVertical:14, alignItems:'center', flexDirection:'row', justifyContent:'center', gap:8 }}>
                <Ionicons name="add-circle-outline" size={18} color="#fff" />
                <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>Submit Another Idea</Text>
              </LinearGradient>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleClose}
              style={{ paddingVertical:13, borderRadius:14, borderWidth:1.5, borderColor:BRAND.line, alignItems:'center' }}>
              <Text style={{ color:BRAND.sub, fontWeight:'700', fontSize:15 }}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ── main modal ────────────────────────────────────────────────────────────
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
      <View style={{ flex:1, backgroundColor:'rgba(0,0,0,0.55)', justifyContent:'flex-end' }}>
        <View style={{ backgroundColor:BRAND.bg, borderTopLeftRadius:28, borderTopRightRadius:28, height:height*0.95 }}>

          {/* HEADER */}
          <LinearGradient colors={['#e91e63','#c2185b']}
            style={{ borderTopLeftRadius:28, borderTopRightRadius:28, padding:20, paddingBottom:16 }}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
              <View style={{ flexDirection:'row', alignItems:'center', gap:12, flex:1 }}>
                <View style={{ width:42, height:42, borderRadius:21, backgroundColor:'rgba(255,255,255,0.2)',
                               alignItems:'center', justifyContent:'center' }}>
                  <Ionicons name="send-outline" size={21} color="#fff" />
                </View>
                <View style={{ flex:1 }}>
                  <Text style={{ color:'#fff', fontSize:17, fontWeight:'800' }}>Submit Innovation Idea</Text>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginTop:4 }}>
                    <View style={{ flex:1, height:4, borderRadius:2, backgroundColor:'rgba(255,255,255,0.25)', overflow:'hidden' }}>
                      <View style={{ width:`${pct}%`, height:'100%', backgroundColor:BRAND.canary, borderRadius:2 }} />
                    </View>
                    <Text style={{ color:'rgba(255,255,255,0.8)', fontSize:11, fontWeight:'700' }}>{pct}% complete</Text>
                  </View>
                </View>
              </View>
              <View style={{ flexDirection:'row', gap:8, alignItems:'center' }}>
                <TouchableOpacity onPress={saveDraft}
                  style={{ width:34, height:34, borderRadius:17, backgroundColor:'rgba(255,255,255,0.2)',
                           alignItems:'center', justifyContent:'center' }}>
                  <Ionicons name="save-outline" size={17} color="#fff" />
                </TouchableOpacity>
                <TouchableOpacity onPress={handleClose}>
                  <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>
            </View>

            {/* draft banner */}
            {hasDraft && (
              <View style={{ flexDirection:'row', alignItems:'center', backgroundColor:'rgba(255,255,255,0.15)',
                             borderRadius:10, padding:10, marginTop:12, gap:8 }}>
                <Ionicons name="document-outline" size={16} color={BRAND.canary} />
                <Text style={{ color:'rgba(255,255,255,0.9)', fontSize:12, flex:1 }}>You have a saved draft</Text>
                <TouchableOpacity onPress={restoreDraft}
                  style={{ backgroundColor:BRAND.canary, paddingHorizontal:10, paddingVertical:5, borderRadius:8 }}>
                  <Text style={{ color:BRAND.dark, fontSize:11, fontWeight:'800' }}>Restore</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setHasDraft(false)}>
                  <Ionicons name="close" size={15} color="rgba(255,255,255,0.6)" />
                </TouchableOpacity>
              </View>
            )}

            {/* step tabs */}
            <View style={{ flexDirection:'row', gap:6, marginTop:14 }}>
              {STEPS.map(s => {
                const done   = step > s.n;
                const active = step === s.n;
                return (
                  <TouchableOpacity key={s.n} onPress={() => { if (s.n < step) setStep(s.n); }}
                    style={{ flex:1, alignItems:'center', gap:4 }}>
                    <View style={{ height:4, width:'100%', borderRadius:2,
                      backgroundColor: done ? BRAND.canary : active ? '#fff' : 'rgba(255,255,255,0.25)' }} />
                    <View style={{ flexDirection:'row', alignItems:'center', gap:3 }}>
                      {done
                        ? <Ionicons name="checkmark-circle" size={13} color={BRAND.canary} />
                        : <Ionicons name={s.icon} size={12} color={active ? '#fff' : 'rgba(255,255,255,0.45)'} />}
                      <Text style={{ fontSize:10, fontWeight:'700',
                        color: done ? BRAND.canary : active ? '#fff' : 'rgba(255,255,255,0.45)' }}>
                        {s.label}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </LinearGradient>

          {/* BODY */}
          <ScrollView ref={scrollRef} style={{ flex:1 }}
            contentContainerStyle={{ padding:20, paddingBottom:40 }} showsVerticalScrollIndicator={false}>

            {/* ── STEP 1: BASICS ─────────────────────────────────────────── */}
            {step === 1 && (
              <View>
                <View style={{ backgroundColor:BRAND.blue+'0D', borderRadius:12, padding:13,
                               borderLeftWidth:3, borderLeftColor:BRAND.blue, marginBottom:4 }}>
                  <Text style={{ fontSize:13, color:BRAND.sub, lineHeight:20 }}>
                    Give your idea a clear title and description. This is what reviewers see first.
                  </Text>
                </View>

                <Field label="Idea Title" fkey="idea_title" required maxLen={120} canAI
                  placeholder="e.g. AI-Powered Structural Load Monitor"
                  hint="Keep it concise and memorable (max 120 chars)" />

                <Field label="Description" fkey="idea_description" required multiline maxLen={2000} canAI
                  placeholder="What is it, how does it work, and what value does it deliver?"
                  hint="2–4 sentences covering the what and why" />

                <TypeRow label="Innovation Type" items={IDEA_TYPES} fkey="innovation_type" />
                <TypeRow label="Potential Impact" items={IDEA_IMPACTS} fkey="potential_impact" />
                <TypeRow label="Effort Required" items={IDEA_EFFORTS} fkey="effort_required" />

                <View style={{ marginTop:16 }}>
                  <Text style={{ fontSize:13, fontWeight:'700', color:BRAND.dark, marginBottom:8 }}>Department</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{ gap:8, flexDirection:'row' }}>
                    {IDEA_DEPTS.map(d => {
                      const sel = form.department === d;
                      return (
                        <TouchableOpacity key={d} onPress={() => upd('department', d)}
                          style={{ paddingHorizontal:14, paddingVertical:8, borderRadius:20,
                                   borderWidth:1.5, borderColor: sel ? BRAND.mint : BRAND.line,
                                   backgroundColor: sel ? BRAND.mint+'18' : '#fff' }}>
                          <Text style={{ fontSize:13, color: sel ? BRAND.mint : BRAND.sub, fontWeight: sel?'700':'500' }}>
                            {d}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                <Field label="Tags (comma-separated)" fkey="tags"
                  placeholder="e.g. AI, Automation, Cost Reduction, Safety"
                  hint="Optional — helps with discovery and search" />
              </View>
            )}

            {/* ── STEP 2: SOLUTION ───────────────────────────────────────── */}
            {step === 2 && (
              <View>
                <View style={{ backgroundColor:'#e91e6310', borderRadius:12, padding:13,
                               borderLeftWidth:3, borderLeftColor:'#e91e63', marginBottom:4 }}>
                  <Text style={{ fontSize:13, color:BRAND.sub, lineHeight:20 }}>
                    Describe the problem and your solution clearly. Use AI Assist to generate content from your title.
                  </Text>
                </View>

                <Field label="Customer / Operational Pain Point" fkey="customer_pain_point" required multiline maxLen={800} canAI
                  placeholder="What problem does this solve? Who suffers from it and how severely?"
                  hint="Be specific — name the team, system, or process affected" />

                <Field label="Proposed Solution" fkey="proposed_solution" required multiline maxLen={800} canAI
                  placeholder="How exactly will your idea solve the problem? What is the mechanism or approach?"
                  hint="Explain the technology, process change, or tool involved" />

                <Field label="Expected Benefits" fkey="expected_benefits" multiline maxLen={600} canAI
                  placeholder="Quantify where possible: cost savings (£), time saved (hrs/week), efficiency gains (%)"
                  hint="Numbers make a stronger business case" />

                <Field label="Target Users / Stakeholders" fkey="target_users"
                  placeholder="e.g. Field engineers, operations managers, finance team"
                  hint="Who will use or benefit from this?" />
              </View>
            )}

            {/* ── STEP 3: DETAILS ────────────────────────────────────────── */}
            {step === 3 && (
              <View>
                <View style={{ backgroundColor:BRAND.success+'0D', borderRadius:12, padding:13,
                               borderLeftWidth:3, borderLeftColor:BRAND.success, marginBottom:4 }}>
                  <Text style={{ fontSize:13, color:BRAND.sub, lineHeight:20 }}>
                    Optional but valuable — financial context and data help reviewers prioritise faster.
                  </Text>
                </View>

                <View style={{ flexDirection:'row', gap:12 }}>
                  <View style={{ flex:1 }}>
                    <Field label="Est. Cost (£)" fkey="estimated_cost" placeholder="e.g. 25000"
                      kbType="numeric" hint="Implementation budget" />
                  </View>
                  <View style={{ flex:1 }}>
                    <Field label="Est. ROI (£)" fkey="estimated_roi" placeholder="e.g. 120000"
                      kbType="numeric" hint="Annual / total return" />
                  </View>
                </View>

                <Field label="Estimated Timeline" fkey="estimated_timeline"
                  placeholder="e.g. 3–6 months" hint="Time to implement from start to go-live" />

                <Field label="Similar Existing Solutions" fkey="similar_existing_solutions"
                  placeholder="What alternatives already exist? Why are they insufficient?" />

                <Field label="What Makes This Unique?" fkey="unique_aspects" multiline maxLen={500} canAI
                  placeholder="What makes your idea better, cheaper, faster, or more practical?" />

                <Field label="Success Metrics / KPIs" fkey="success_metrics" multiline maxLen={400} canAI
                  placeholder="How will success be measured? e.g. 30% reduction in downtime within 6 months" />

                <Field label="Supporting Data / Research" fkey="supporting_data" multiline maxLen={600}
                  placeholder="Case studies, pilot results, benchmarks, or research that backs this idea" />
              </View>
            )}

            {/* ── STEP 4: REVIEW ─────────────────────────────────────────── */}
            {step === 4 && (
              <View>
                <View style={{ backgroundColor:'#8b5cf610', borderRadius:14, padding:14, marginBottom:16,
                               borderWidth:1, borderColor:'#8b5cf625' }}>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:5 }}>
                    <Ionicons name="eye-outline" size={18} color="#8b5cf6" />
                    <Text style={{ fontSize:15, fontWeight:'800', color:'#8b5cf6' }}>Review Before Submitting</Text>
                  </View>
                  <Text style={{ fontSize:13, color:BRAND.sub }}>
                    Check all details. Tap any step tab above to make changes.
                  </Text>
                </View>

                {pct < 80 && (
                  <View style={{ backgroundColor:'#fef3c7', borderRadius:12, padding:12,
                                 flexDirection:'row', alignItems:'center', gap:8, marginBottom:16,
                                 borderWidth:1, borderColor:'#fde68a' }}>
                    <Ionicons name="warning-outline" size={18} color={BRAND.warning} />
                    <Text style={{ fontSize:13, color:'#92400e', flex:1 }}>
                      Only {pct}% complete. A fuller submission increases approval chances.
                    </Text>
                  </View>
                )}

                <View style={{ backgroundColor:'#fff', borderRadius:16, padding:18, borderWidth:1,
                               borderColor:BRAND.line, shadowColor:'#000', shadowOffset:{width:0,height:2},
                               shadowOpacity:0.06, shadowRadius:8, elevation:2 }}>
                  {/* badge row */}
                  <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:14 }}>
                    {(() => {
                      const t = IDEA_TYPES.find(x => x.id === form.innovation_type);
                      return t && (
                        <View style={{ backgroundColor:t.color+'18', borderRadius:8, paddingHorizontal:10,
                                       paddingVertical:5, flexDirection:'row', alignItems:'center', gap:5 }}>
                          <Ionicons name={t.icon} size={13} color={t.color} />
                          <Text style={{ fontSize:12, color:t.color, fontWeight:'700' }}>{t.label}</Text>
                        </View>
                      );
                    })()}
                    {(() => {
                      const im = IDEA_IMPACTS.find(x => x.id === form.potential_impact);
                      return im && (
                        <View style={{ backgroundColor:im.color+'18', borderRadius:8, paddingHorizontal:10,
                                       paddingVertical:5, flexDirection:'row', alignItems:'center', gap:5 }}>
                          <Ionicons name={im.icon} size={13} color={im.color} />
                          <Text style={{ fontSize:12, color:im.color, fontWeight:'700' }}>{im.label} Impact</Text>
                        </View>
                      );
                    })()}
                    {(() => {
                      const ef = IDEA_EFFORTS.find(x => x.id === form.effort_required);
                      return ef && (
                        <View style={{ backgroundColor:ef.color+'18', borderRadius:8, paddingHorizontal:10, paddingVertical:5 }}>
                          <Text style={{ fontSize:12, color:ef.color, fontWeight:'700' }}>Effort: {ef.label} ({ef.desc})</Text>
                        </View>
                      );
                    })()}
                    <View style={{ backgroundColor:BRAND.blue+'12', borderRadius:8, paddingHorizontal:10, paddingVertical:5 }}>
                      <Text style={{ fontSize:12, color:BRAND.blue, fontWeight:'700' }}>{form.department}</Text>
                    </View>
                  </View>

                  <ReviewLine label="Title"              value={form.idea_title}            color={BRAND.dark} />
                  <ReviewLine label="Description"        value={form.idea_description} />
                  <ReviewLine label="Pain Point"         value={form.customer_pain_point} />
                  <ReviewLine label="Proposed Solution"  value={form.proposed_solution} />
                  <ReviewLine label="Expected Benefits"  value={form.expected_benefits} />
                  <ReviewLine label="Target Users"       value={form.target_users} />
                  <ReviewLine label="Unique Aspects"     value={form.unique_aspects} />
                  <ReviewLine label="Success Metrics"    value={form.success_metrics} />
                  <ReviewLine label="Est. Cost"          value={form.estimated_cost ? `£${Number(form.estimated_cost).toLocaleString()}` : null} color={BRAND.success} />
                  <ReviewLine label="Est. ROI"           value={form.estimated_roi  ? `£${Number(form.estimated_roi).toLocaleString()}`  : null} color={BRAND.success} />
                  <ReviewLine label="Timeline"           value={form.estimated_timeline} />
                  <ReviewLine label="Similar Solutions"  value={form.similar_existing_solutions} />
                  <ReviewLine label="Supporting Data"    value={form.supporting_data} />
                  {form.tags && <ReviewLine label="Tags" value={form.tags} />}
                </View>
              </View>
            )}

          </ScrollView>

          {/* BOTTOM NAV */}
          <View style={{ padding:16, borderTopWidth:1, borderTopColor:BRAND.line,
                         backgroundColor:'#fff', flexDirection:'row', gap:10 }}>
            {step > 1 && (
              <TouchableOpacity onPress={goBack}
                style={{ flex:1, paddingVertical:14, borderRadius:14, borderWidth:1.5,
                         borderColor:BRAND.line, alignItems:'center', justifyContent:'center',
                         flexDirection:'row', gap:6 }}>
                <Ionicons name="arrow-back" size={16} color={BRAND.sub} />
                <Text style={{ color:BRAND.sub, fontWeight:'700' }}>Back</Text>
              </TouchableOpacity>
            )}
            {step < 4 ? (
              <TouchableOpacity onPress={goNext} style={{ flex:2, borderRadius:14, overflow:'hidden' }}>
                <LinearGradient colors={['#e91e63','#c2185b']}
                  style={{ paddingVertical:14, alignItems:'center', flexDirection:'row', justifyContent:'center', gap:6 }}>
                  <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>Continue</Text>
                  <Ionicons name="arrow-forward" size={16} color="#fff" />
                </LinearGradient>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity onPress={handleSubmit} disabled={loading}
                style={{ flex:2, borderRadius:14, overflow:'hidden', opacity:loading?0.7:1 }}>
                <LinearGradient colors={[BRAND.success,'#059669']}
                  style={{ paddingVertical:14, alignItems:'center', flexDirection:'row', justifyContent:'center', gap:8 }}>
                  {loading ? <ActivityIndicator color="#fff" size="small" /> : <Ionicons name="send" size={16} color="#fff" />}
                  <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>
                    <Text>{loading ? 'Submitting…' : 'Submit Innovation Idea'}</Text>
                  </Text>
                </LinearGradient>
              </TouchableOpacity>
            )}
          </View>

        </View>
      </View>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// INNOVATION IDEAS LIST — FULL FEATURED MODAL
// ────────────────────────────────────────────────────────────────────────────
const INNOVATION_LIST_STATUS_COLORS = IDEA_STATUSES;
const INNOVATION_LIST_FILTERS       = IDEA_FILTERS;
const IDEAS_PAGE_SIZE = 10;

function IdeasListModal({ visible, onClose, onSubmitNew }) {
  const [ideas,        setIdeas]        = React.useState([]);
  const [stats,        setStats]        = React.useState(null);
  const [filter,       setFilter]       = React.useState('all');
  const [search,       setSearch]       = React.useState('');
  const [sortBy,       setSortBy]       = React.useState('newest');
  const [loading,      setLoading]      = React.useState(false);
  const [refreshing,   setRefreshing]   = React.useState(false);
  const [showSort,     setShowSort]     = React.useState(false);
  const [page,         setPage]         = React.useState(1);
  const [view,         setView]         = React.useState('list');   // 'list' | 'detail' | 'edit'
  const [selected,     setSelected]     = React.useState(null);
  const [editForm,     setEditForm]     = React.useState({});
  const [editLoading,  setEditLoading]  = React.useState(false);

  React.useEffect(() => { if (visible) { setView('list'); setSelected(null); loadAll(); } }, [visible]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([fetchIdeas('all'), fetchStats()]);
    setLoading(false);
  };
  const fetchIdeas = async (f) => {
    try {
      const params = (f||filter) !== 'all' ? { review_status:(f||filter) } : {};
      const r = await innovationService.getIdeas(params);
      if (r?.success) setIdeas(r.data || []);
    } catch { setIdeas([]); }
  };
  const fetchStats = async () => {
    try { const r = await innovationService.getStats(); if (r?.success) setStats(r.data); } catch {}
  };
  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchIdeas(), fetchStats()]);
    setRefreshing(false); setPage(1);
  };
  const changeFilter = (v) => { setFilter(v); setPage(1); fetchIdeas(v); };
  const openDetail   = (item) => { setSelected(item); setView('detail'); };
  const openEdit     = (item) => {
    setEditForm({
      idea_title:item.idea_title||'', idea_description:item.idea_description||'',
      customer_pain_point:item.customer_pain_point||'', proposed_solution:item.proposed_solution||'',
      expected_benefits:item.expected_benefits||'', unique_aspects:item.unique_aspects||'',
      success_metrics:item.success_metrics||'', supporting_data:item.supporting_data||'',
      potential_impact:item.potential_impact||'MEDIUM', effort_required:item.effort_required||'medium',
      estimated_cost:item.estimated_cost?String(item.estimated_cost):'',
      estimated_timeline:item.estimated_timeline||'', tags:Array.isArray(item.tags)?item.tags.join(', '):(item.tags||''),
    });
    setSelected(item);
    setView('edit');
  };

  const handleDelete = (id) => {
    Alert.alert('Delete Idea','Are you sure? This cannot be undone.',[
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: async () => {
        try { await innovationService.deleteIdea(id); } catch {}
        setIdeas(p => p.filter(i => i.id !== id));
        fetchStats();
        if (selected?.id === id) setView('list');
      }},
    ]);
  };

  const handleSaveEdit = async () => {
    if (!editForm.idea_title?.trim()) { Alert.alert('Required','Title cannot be empty.'); return; }
    setEditLoading(true);
    try {
      const payload = { ...editForm, estimated_cost: editForm.estimated_cost ? Number(editForm.estimated_cost) : undefined };
      await innovationService.updateIdea(selected.id, payload);
      const updated = { ...selected, ...payload };
      setIdeas(p => p.map(i => i.id===selected.id ? updated : i));
      setSelected(updated);
      setView('detail');
      Alert.alert('✅ Updated','Idea updated successfully.');
    } catch { Alert.alert('Error','Update failed. Please try again.'); }
    finally { setEditLoading(false); }
  };

  const handleShare = async (item) => {
    try {
      await Share.share({
        title: item.idea_title,
        message:`💡 Innovation Idea: ${item.idea_title}\n\n${item.idea_description}\n\nImpact: ${item.potential_impact} | Status: ${item.review_status||'Pending'}\n\nShared via JobEng Innovation Hub`,
      });
    } catch {}
  };

  // ── derived data ─────────────────────────────────────────────────────────
  const filtered = React.useMemo(() => {
    let list = [...ideas];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(i =>
      i.idea_title?.toLowerCase().includes(q) ||
      i.idea_description?.toLowerCase().includes(q) ||
      i.innovation_type?.toLowerCase().includes(q) ||
      i.idea_code?.toLowerCase().includes(q) ||
      (Array.isArray(i.tags) ? i.tags : []).some(t => t.toLowerCase().includes(q))
    );
    const ORDER = ['CRITICAL','HIGH','MEDIUM','LOW'];
    switch(sortBy) {
      case 'oldest':  list.sort((a,b)=>new Date(a.created_at)-new Date(b.created_at)); break;
      case 'score':   list.sort((a,b)=>(b.overall_score||0)-(a.overall_score||0)); break;
      case 'impact':  list.sort((a,b)=>ORDER.indexOf(a.potential_impact)-ORDER.indexOf(b.potential_impact)); break;
      case 'cost':    list.sort((a,b)=>(a.estimated_cost||Infinity)-(b.estimated_cost||Infinity)); break;
      case 'roi':     list.sort((a,b)=>(b.estimated_roi||0)-(a.estimated_roi||0)); break;
      default:        list.sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)); break;
    }
    return list;
  }, [ideas, search, sortBy]);

  const paged  = filtered.slice(0, page * IDEAS_PAGE_SIZE);
  const hasMore = paged.length < filtered.length;

  // ── score bar ─────────────────────────────────────────────────────────────
  const ScoreBar = ({ score, max=10 }) => {
    const pct = Math.min((score/max)*100, 100);
    const col = score>=8 ? '#4CAF50' : score>=5 ? '#FFC107' : '#F44336';
    return (
      <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
        <View style={{ flex:1, height:6, backgroundColor:BRAND.line, borderRadius:3, overflow:'hidden' }}>
          <View style={{ width:`${pct}%`, height:'100%', backgroundColor:col, borderRadius:3 }} />
        </View>
        <Text style={{ fontSize:12, fontWeight:'800', color:col, minWidth:26, textAlign:'right' }}>{score}</Text>
      </View>
    );
  };

  // ── EDIT VIEW ─────────────────────────────────────────────────────────────
  if (view === 'edit' && selected) {
    const EF = ({ label, fkey, multiline, kbType, required }) => (
      <View style={{ marginBottom:14 }}>
        <Text style={{ fontSize:13, fontWeight:'700', color:BRAND.dark, marginBottom:6 }}>
          {label}{required&&<Text style={{ color:BRAND.danger }}> *</Text>}
        </Text>
        <TextInput
          style={{ backgroundColor:'#fff', borderRadius:12, padding:13, fontSize:14,
                   borderWidth:1.5, borderColor: editForm[fkey] ? BRAND.blue+'55' : BRAND.line,
                   minHeight: multiline?88:48, textAlignVertical:multiline?'top':'center', color:BRAND.text }}
          value={String(editForm[fkey]||'')}
          onChangeText={t => setEditForm(p=>({...p,[fkey]:t}))}
          multiline={!!multiline} keyboardType={kbType||'default'} placeholderTextColor="#cbd5e1"
        />
      </View>
    );
    const ETypeRow = ({ label, items, fkey }) => (
      <View style={{ marginBottom:14 }}>
        <Text style={{ fontSize:13, fontWeight:'700', color:BRAND.dark, marginBottom:8 }}>{label}</Text>
        <View style={{ flexDirection:'row', flexWrap:'wrap', gap:8 }}>
          {items.map(item => {
            const id  = typeof item==='string'?item:item.id;
            const lbl = typeof item==='string'?item:(item.label||item.name||id);
            const col = typeof item==='object'?(item.color||BRAND.blue):BRAND.blue;
            const sel = editForm[fkey]===id;
            return (
              <TouchableOpacity key={id} onPress={()=>setEditForm(p=>({...p,[fkey]:id}))}
                style={{ paddingHorizontal:14, paddingVertical:9, borderRadius:20,
                         borderWidth:1.5, borderColor:sel?col:BRAND.line,
                         backgroundColor:sel?col+'18':'#fff' }}>
                <Text style={{ fontSize:13, fontWeight:sel?'700':'500', color:sel?col:BRAND.sub }}>{lbl}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={()=>setView('detail')}>
        <View style={{ flex:1, backgroundColor:'rgba(0,0,0,0.55)', justifyContent:'flex-end' }}>
          <View style={{ backgroundColor:BRAND.bg, borderTopLeftRadius:28, borderTopRightRadius:28, height:height*0.95 }}>
            <LinearGradient colors={[BRAND.blue,'#1565c0']}
              style={{ borderTopLeftRadius:28, borderTopRightRadius:28, padding:20, paddingBottom:16,
                       flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
              <View style={{ flexDirection:'row', alignItems:'center', gap:12 }}>
                <View style={{ width:42, height:42, borderRadius:21, backgroundColor:'rgba(255,255,255,0.2)',
                               alignItems:'center', justifyContent:'center' }}>
                  <Ionicons name="create-outline" size={21} color="#fff" />
                </View>
                <View>
                  <Text style={{ color:'#fff', fontSize:16, fontWeight:'800' }}>Edit Idea</Text>
                  <Text style={{ color:'rgba(255,255,255,0.7)', fontSize:12 }} numberOfLines={1}>{selected.idea_title}</Text>
                </View>
              </View>
              <TouchableOpacity onPress={()=>setView('detail')}>
                <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
              </TouchableOpacity>
            </LinearGradient>
            <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:20, paddingBottom:30 }}>
              <EF label="Idea Title" fkey="idea_title" required />
              <EF label="Description" fkey="idea_description" multiline />
              <EF label="Customer Pain Point" fkey="customer_pain_point" multiline />
              <EF label="Proposed Solution" fkey="proposed_solution" multiline />
              <EF label="Expected Benefits" fkey="expected_benefits" multiline />
              <EF label="Unique Aspects" fkey="unique_aspects" multiline />
              <EF label="Success Metrics" fkey="success_metrics" multiline />
              <EF label="Supporting Data" fkey="supporting_data" multiline />
              <View style={{ flexDirection:'row', gap:12 }}>
                <View style={{ flex:1 }}><EF label="Est. Cost (£)" fkey="estimated_cost" kbType="numeric" /></View>
                <View style={{ flex:1 }}><EF label="Timeline" fkey="estimated_timeline" /></View>
              </View>
              <EF label="Tags (comma-separated)" fkey="tags" />
              <ETypeRow label="Impact Level" items={IDEA_IMPACTS} fkey="potential_impact" />
              <ETypeRow label="Effort Required" items={IDEA_EFFORTS} fkey="effort_required" />
            </ScrollView>
            <View style={{ padding:16, borderTopWidth:1, borderTopColor:BRAND.line, backgroundColor:'#fff', flexDirection:'row', gap:10 }}>
              <TouchableOpacity onPress={()=>setView('detail')}
                style={{ flex:1, paddingVertical:14, borderRadius:14, borderWidth:1.5, borderColor:BRAND.line, alignItems:'center' }}>
                <Text style={{ color:BRAND.sub, fontWeight:'700' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleSaveEdit} disabled={editLoading}
                style={{ flex:2, borderRadius:14, overflow:'hidden', opacity:editLoading?0.7:1 }}>
                <LinearGradient colors={[BRAND.blue,'#1565c0']}
                  style={{ paddingVertical:14, alignItems:'center', flexDirection:'row', justifyContent:'center', gap:8 }}>
                  {editLoading?<ActivityIndicator color="#fff" size="small"/>:<Ionicons name="checkmark" size={18} color="#fff"/>}
                  <Text style={{ color:'#fff', fontWeight:'800', fontSize:15 }}>{editLoading?'Saving…':'Save Changes'}</Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // ── DETAIL VIEW ───────────────────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const item = selected;
    const sc   = IDEA_STATUSES[item.review_status] || '#9E9E9E';
    const typ  = IDEA_TYPES.find(x=>x.id===item.innovation_type);
    const imp  = IDEA_IMPACTS.find(x=>x.id===item.potential_impact);
    const eff  = IDEA_EFFORTS.find(x=>x.id===item.effort_required);

    const DS = ({ title, value, icon }) => !value ? null : (
      <View style={{ marginBottom:16 }}>
        <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:7 }}>
          <View style={{ width:30, height:30, borderRadius:15, backgroundColor:BRAND.blue+'12',
                         alignItems:'center', justifyContent:'center' }}>
            <Ionicons name={icon} size={15} color={BRAND.blue} />
          </View>
          <Text style={{ fontSize:13, fontWeight:'800', color:BRAND.dark }}>{title}</Text>
        </View>
        <Text style={{ fontSize:14, color:BRAND.sub, lineHeight:22, paddingLeft:38 }}>{value}</Text>
      </View>
    );

    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={()=>setView('list')}>
        <View style={{ flex:1, backgroundColor:'rgba(0,0,0,0.55)', justifyContent:'flex-end' }}>
          <View style={{ backgroundColor:BRAND.bg, borderTopLeftRadius:28, borderTopRightRadius:28, height:height*0.95 }}>

            <LinearGradient colors={[sc, sc+'bb']}
              style={{ borderTopLeftRadius:28, borderTopRightRadius:28, padding:20, paddingBottom:16 }}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
                <View style={{ flex:1, marginRight:12 }}>
                  <View style={{ flexDirection:'row', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                    <View style={{ backgroundColor:'rgba(255,255,255,0.25)', paddingHorizontal:10, paddingVertical:5, borderRadius:8 }}>
                      <Text style={{ color:'#fff', fontSize:11, fontWeight:'800', textTransform:'uppercase' }}>
                        {IDEA_STATUS_LABELS[item.review_status]||item.review_status||'pending'}
                      </Text>
                    </View>
                    {item.idea_code && (
                      <View style={{ backgroundColor:'rgba(0,0,0,0.18)', paddingHorizontal:10, paddingVertical:5, borderRadius:8 }}>
                        <Text style={{ color:'rgba(255,255,255,0.9)', fontSize:11, fontFamily:Platform.OS==='ios'?'Menlo':'monospace' }}>
                          {item.idea_code}
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text style={{ color:'#fff', fontSize:17, fontWeight:'800', lineHeight:24 }}>{item.idea_title}</Text>
                  {item.created_at && (
                    <Text style={{ color:'rgba(255,255,255,0.7)', fontSize:12, marginTop:4 }}>
                      Submitted {new Date(item.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
                      {item.department ? ` · ${item.department}` : ''}
                    </Text>
                  )}
                </View>
                <TouchableOpacity onPress={()=>setView('list')}>
                  <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>

              {/* KPI strip */}
              <View style={{ flexDirection:'row', gap:8, marginTop:14 }}>
                {[
                  imp && { label:'Impact', value:imp.label, col:imp.color },
                  eff && { label:'Effort',  value:`${eff.label} (${eff.desc})`, col:'rgba(255,255,255,0.85)' },
                  item.overall_score!=null && { label:'Score', value:`${item.overall_score}/10`, col:BRAND.canary },
                  item.estimated_cost && { label:'Est. Cost', value:`£${Number(item.estimated_cost).toLocaleString()}`, col:'rgba(255,255,255,0.85)' },
                  item.estimated_roi  && { label:'Est. ROI',  value:`£${Number(item.estimated_roi).toLocaleString()}`,  col:'#a7f3d0' },
                ].filter(Boolean).map((k,i)=>(
                  <View key={i} style={{ flex:1, backgroundColor:'rgba(0,0,0,0.18)', borderRadius:10, padding:8, alignItems:'center' }}>
                    <Text style={{ fontSize:10, color:'rgba(255,255,255,0.65)', fontWeight:'600', marginBottom:3 }}>{k.label}</Text>
                    <Text style={{ fontSize:11, color:k.col, fontWeight:'800', textAlign:'center' }} numberOfLines={2}>{k.value}</Text>
                  </View>
                ))}
              </View>
            </LinearGradient>

            <ScrollView style={{ flex:1 }} contentContainerStyle={{ padding:20, paddingBottom:40 }}>
              {/* reviewer comment */}
              {item.reviewer_comments && (
                <View style={{ backgroundColor:'#fffbeb', borderRadius:12, padding:14, marginBottom:16,
                               borderWidth:1, borderColor:'#fde68a' }}>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:7, marginBottom:6 }}>
                    <Ionicons name="chatbubble-outline" size={16} color={BRAND.warning} />
                    <Text style={{ fontSize:13, fontWeight:'800', color:'#92400e' }}>Reviewer Feedback</Text>
                  </View>
                  <Text style={{ fontSize:14, color:'#78350f', lineHeight:22 }}>{item.reviewer_comments}</Text>
                </View>
              )}

              {/* type badge */}
              {typ && (
                <View style={{ flexDirection:'row', alignItems:'center', gap:10, backgroundColor:typ.color+'12',
                               borderRadius:12, padding:13, marginBottom:16, borderWidth:1, borderColor:typ.color+'25' }}>
                  <Ionicons name={typ.icon} size={22} color={typ.color} />
                  <View>
                    <Text style={{ fontSize:14, fontWeight:'800', color:typ.color }}>{typ.label} Innovation</Text>
                    {item.department && <Text style={{ fontSize:12, color:BRAND.sub, marginTop:2 }}>Dept: {item.department}</Text>}
                  </View>
                </View>
              )}

              <DS title="Description"       value={item.idea_description}     icon="document-text-outline" />
              <DS title="Pain Point"        value={item.customer_pain_point}  icon="alert-circle-outline" />
              <DS title="Proposed Solution" value={item.proposed_solution}    icon="bulb-outline" />
              <DS title="Expected Benefits" value={item.expected_benefits}    icon="trending-up-outline" />
              <DS title="Unique Aspects"    value={item.unique_aspects}       icon="star-outline" />
              <DS title="Target Users"      value={item.target_users}         icon="people-outline" />
              <DS title="Success Metrics"   value={item.success_metrics}      icon="bar-chart-outline" />
              <DS title="Timeline"          value={item.estimated_timeline}   icon="time-outline" />
              <DS title="Similar Solutions" value={item.similar_existing_solutions} icon="git-compare-outline" />
              <DS title="Supporting Data"   value={item.supporting_data}      icon="library-outline" />

              {/* tags */}
              {(Array.isArray(item.tags) ? item.tags : (item.tags||'').split(',').map(t=>t.trim())).filter(Boolean).length > 0 && (
                <View style={{ marginBottom:16 }}>
                  <Text style={{ fontSize:12, color:BRAND.sub, fontWeight:'700', marginBottom:8, textTransform:'uppercase' }}>Tags</Text>
                  <View style={{ flexDirection:'row', flexWrap:'wrap', gap:6 }}>
                    {(Array.isArray(item.tags)?item.tags:item.tags.split(',').map(t=>t.trim())).filter(Boolean).map((t,i)=>(
                      <View key={i} style={{ backgroundColor:BRAND.blue+'12', borderRadius:8, paddingHorizontal:10, paddingVertical:5 }}>
                        <Text style={{ fontSize:12, color:BRAND.blue, fontWeight:'600' }}>{t}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* score breakdown */}
              {item.overall_score != null && (
                <View style={{ backgroundColor:'#fff', borderRadius:14, padding:16, borderWidth:1, borderColor:BRAND.line }}>
                  <Text style={{ fontSize:13, fontWeight:'800', color:BRAND.dark, marginBottom:14 }}>Review Scores</Text>
                  {[
                    { label:'Overall Score',        score:item.overall_score },
                    item.feasibility_score!=null  && { label:'Feasibility',         score:item.feasibility_score  },
                    item.innovation_score!=null   && { label:'Innovation',          score:item.innovation_score   },
                    item.impact_score!=null       && { label:'Impact',              score:item.impact_score       },
                    item.alignment_score!=null    && { label:'Strategic Alignment', score:item.alignment_score    },
                  ].filter(Boolean).map((row,i)=>(
                    <View key={i} style={{ marginBottom:12 }}>
                      <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:5 }}>
                        <Text style={{ fontSize:12, color:BRAND.sub, fontWeight:'600' }}>{row.label}</Text>
                        <Text style={{ fontSize:12, color:BRAND.dark, fontWeight:'800' }}>{row.score}/10</Text>
                      </View>
                      <ScoreBar score={row.score} />
                    </View>
                  ))}
                </View>
              )}
            </ScrollView>

            {/* action bar */}
            <View style={{ padding:14, borderTopWidth:1, borderTopColor:BRAND.line,
                           backgroundColor:'#fff', flexDirection:'row', gap:8 }}>
              <TouchableOpacity onPress={()=>openEdit(item)}
                style={{ flex:1, paddingVertical:13, borderRadius:14, borderWidth:1.5,
                         borderColor:BRAND.blue, alignItems:'center', flexDirection:'row', justifyContent:'center', gap:5 }}>
                <Ionicons name="create-outline" size={16} color={BRAND.blue} />
                <Text style={{ color:BRAND.blue, fontWeight:'700' }}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={()=>handleShare(item)}
                style={{ flex:1, paddingVertical:13, borderRadius:14, borderWidth:1.5,
                         borderColor:BRAND.success, alignItems:'center', flexDirection:'row', justifyContent:'center', gap:5 }}>
                <Ionicons name="share-outline" size={16} color={BRAND.success} />
                <Text style={{ color:BRAND.success, fontWeight:'700' }}>Share</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={()=>handleDelete(item.id)}
                style={{ flex:1, paddingVertical:13, borderRadius:14, borderWidth:1.5,
                         borderColor:BRAND.danger, alignItems:'center', flexDirection:'row', justifyContent:'center', gap:5 }}>
                <Ionicons name="trash-outline" size={16} color={BRAND.danger} />
                <Text style={{ color:BRAND.danger, fontWeight:'700' }}>Delete</Text>
              </TouchableOpacity>
            </View>

          </View>
        </View>
      </Modal>
    );
  }

  // ── MAIN LIST VIEW ────────────────────────────────────────────────────────
  const renderCard = ({ item }) => {
    const sc  = IDEA_STATUSES[item.review_status] || '#9E9E9E';
    const imp = IDEA_IMPACTS.find(x=>x.id===item.potential_impact);
    const typ = IDEA_TYPES.find(x=>x.id===item.innovation_type);
    return (
      <TouchableOpacity onPress={()=>openDetail(item)} activeOpacity={0.85}
        style={{ backgroundColor:'#fff', borderRadius:16, marginBottom:12, overflow:'hidden',
                 shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.07,
                 shadowRadius:6, elevation:3, borderWidth:1, borderColor:'#f1f5f9' }}>
        {/* status bar */}
        <View style={{ height:4, backgroundColor:sc }} />
        <View style={{ padding:15 }}>
          {/* header */}
          <View style={{ flexDirection:'row', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
            <View style={{ flex:1, gap:6 }}>
              <View style={{ flexDirection:'row', flexWrap:'wrap', gap:6 }}>
                <View style={{ backgroundColor:sc+'20', paddingHorizontal:8, paddingVertical:4, borderRadius:7 }}>
                  <Text style={{ fontSize:10, color:sc, fontWeight:'800', textTransform:'uppercase' }}>
                    {IDEA_STATUS_LABELS[item.review_status]||item.review_status||'pending'}
                  </Text>
                </View>
                {typ && (
                  <View style={{ backgroundColor:typ.color+'15', paddingHorizontal:8, paddingVertical:4,
                                 borderRadius:7, flexDirection:'row', alignItems:'center', gap:4 }}>
                    <Ionicons name={typ.icon} size={11} color={typ.color} />
                    <Text style={{ fontSize:10, color:typ.color, fontWeight:'700' }}>{typ.label}</Text>
                  </View>
                )}
                {imp && (
                  <View style={{ backgroundColor:imp.color+'15', paddingHorizontal:8, paddingVertical:4,
                                 borderRadius:7, flexDirection:'row', alignItems:'center', gap:4 }}>
                    <Ionicons name={imp.icon} size={11} color={imp.color} />
                    <Text style={{ fontSize:10, color:imp.color, fontWeight:'700' }}>{imp.label}</Text>
                  </View>
                )}
              </View>
              {item.idea_code && (
                <Text style={{ fontSize:11, color:BRAND.sub, fontFamily:Platform.OS==='ios'?'Menlo':'monospace' }}>
                  {item.idea_code}
                </Text>
              )}
            </View>
            {/* action buttons */}
            <View style={{ flexDirection:'row', gap:2 }}>
              <TouchableOpacity onPress={()=>handleShare(item)} style={{ padding:6 }}>
                <Ionicons name="share-outline" size={17} color={BRAND.sub} />
              </TouchableOpacity>
              <TouchableOpacity onPress={()=>{ setSelected(item); openEdit(item); }} style={{ padding:6 }}>
                <Ionicons name="create-outline" size={17} color={BRAND.blue} />
              </TouchableOpacity>
              <TouchableOpacity onPress={()=>handleDelete(item.id)} style={{ padding:6 }}>
                <Ionicons name="trash-outline" size={17} color={BRAND.danger} />
              </TouchableOpacity>
            </View>
          </View>

          <Text style={{ fontSize:15, fontWeight:'800', color:BRAND.dark, marginBottom:5, lineHeight:21 }}>
            {item.idea_title}
          </Text>
          <Text style={{ fontSize:13, color:BRAND.sub, lineHeight:19, marginBottom:10 }} numberOfLines={2}>
            {item.idea_description}
          </Text>

          {/* meta */}
          <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:8 }}>
            {item.created_at && (
              <View style={{ flexDirection:'row', alignItems:'center', gap:4 }}>
                <Ionicons name="calendar-outline" size={13} color={BRAND.sub} />
                <Text style={{ fontSize:12, color:BRAND.sub }}>{new Date(item.created_at).toLocaleDateString('en-GB')}</Text>
              </View>
            )}
            {item.estimated_cost && (
              <View style={{ flexDirection:'row', alignItems:'center', gap:4 }}>
                <Ionicons name="cash-outline" size={13} color={BRAND.success} />
                <Text style={{ fontSize:12, color:BRAND.success, fontWeight:'700' }}>£{Number(item.estimated_cost).toLocaleString()}</Text>
              </View>
            )}
            {item.estimated_roi && (
              <View style={{ flexDirection:'row', alignItems:'center', gap:4 }}>
                <Ionicons name="trending-up-outline" size={13} color={BRAND.blue} />
                <Text style={{ fontSize:12, color:BRAND.blue, fontWeight:'700' }}>ROI £{Number(item.estimated_roi).toLocaleString()}</Text>
              </View>
            )}
            {item.estimated_timeline && (
              <View style={{ flexDirection:'row', alignItems:'center', gap:4 }}>
                <Ionicons name="time-outline" size={13} color={BRAND.sub} />
                <Text style={{ fontSize:12, color:BRAND.sub }}>{item.estimated_timeline}</Text>
              </View>
            )}
          </View>

          {/* score bar */}
          {item.overall_score != null && (
            <View style={{ borderTopWidth:1, borderTopColor:BRAND.line, paddingTop:10 }}>
              <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:5 }}>
                <Text style={{ fontSize:11, color:BRAND.sub, fontWeight:'600', textTransform:'uppercase' }}>Overall Score</Text>
                {item.effort_required && (
                  <Text style={{ fontSize:11, color:BRAND.sub }}>Effort: {item.effort_required}</Text>
                )}
              </View>
              <ScoreBar score={item.overall_score} />
            </View>
          )}

          {/* reviewer comment */}
          {item.reviewer_comments && (
            <View style={{ backgroundColor:'#fffbeb', borderRadius:8, padding:10, marginTop:10,
                           borderLeftWidth:3, borderLeftColor:BRAND.warning }}>
              <Text style={{ fontSize:11, color:'#92400e', fontWeight:'700', marginBottom:3 }}>REVIEWER NOTE</Text>
              <Text style={{ fontSize:12, color:'#78350f', lineHeight:17 }} numberOfLines={2}>{item.reviewer_comments}</Text>
            </View>
          )}

          {/* tap hint */}
          <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'flex-end', marginTop:8 }}>
            <Text style={{ fontSize:11, color:BRAND.blue, fontWeight:'600' }}>View details</Text>
            <Ionicons name="chevron-forward" size={14} color={BRAND.blue} />
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={{ flex:1, backgroundColor:'rgba(0,0,0,0.55)', justifyContent:'flex-end' }}>
        <View style={{ backgroundColor:BRAND.bg, borderTopLeftRadius:28, borderTopRightRadius:28, height:height*0.95 }}>

          {/* HEADER */}
          <LinearGradient colors={['#9c27b0','#7b1fa2']}
            style={{ borderTopLeftRadius:28, borderTopRightRadius:28, padding:20, paddingBottom:14 }}>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <View style={{ flexDirection:'row', alignItems:'center', gap:12 }}>
                <View style={{ width:42, height:42, borderRadius:21, backgroundColor:'rgba(255,255,255,0.2)',
                               alignItems:'center', justifyContent:'center' }}>
                  <Ionicons name="list-outline" size={22} color="#fff" />
                </View>
                <View>
                  <Text style={{ color:'#fff', fontSize:17, fontWeight:'800' }}>Innovation Ideas</Text>
                  <Text style={{ color:'rgba(255,255,255,0.7)', fontSize:12 }}>
                    <Text>{filtered.length} idea{filtered.length!==1?'s':''}{search?' matching':''}</Text>
                  </Text>
                </View>
              </View>
              <View style={{ flexDirection:'row', gap:8, alignItems:'center' }}>
                <TouchableOpacity onPress={()=>{ onClose(); setTimeout(()=>onSubmitNew?.(),350); }}
                  style={{ backgroundColor:'rgba(255,255,255,0.2)', borderRadius:22, paddingHorizontal:14,
                           paddingVertical:8, flexDirection:'row', alignItems:'center', gap:6 }}>
                  <Ionicons name="add" size={16} color="#fff" />
                  <Text style={{ color:'#fff', fontSize:13, fontWeight:'700' }}>Submit</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={onClose}>
                  <Ionicons name="close-circle" size={30} color="rgba(255,255,255,0.8)" />
                </TouchableOpacity>
              </View>
            </View>

            {/* search + sort */}
            <View style={{ flexDirection:'row', gap:8 }}>
              <View style={{ flex:1, flexDirection:'row', alignItems:'center', backgroundColor:'rgba(255,255,255,0.15)',
                             borderRadius:12, paddingHorizontal:12, paddingVertical:9, gap:8 }}>
                <Ionicons name="search-outline" size={16} color="rgba(255,255,255,0.65)" />
                <TextInput
                  style={{ flex:1, color:'#fff', fontSize:13 }}
                  value={search} onChangeText={v=>{ setSearch(v); setPage(1); }}
                  placeholder="Search ideas, types, tags…" placeholderTextColor="rgba(255,255,255,0.45)"
                  returnKeyType="search"
                />
                {search ? (
                  <TouchableOpacity onPress={()=>setSearch('')}>
                    <Ionicons name="close-circle" size={16} color="rgba(255,255,255,0.65)" />
                  </TouchableOpacity>
                ) : null}
              </View>
              <TouchableOpacity onPress={()=>setShowSort(p=>!p)}
                style={{ backgroundColor:'rgba(255,255,255,0.15)', borderRadius:12, paddingHorizontal:12,
                         paddingVertical:9, flexDirection:'row', alignItems:'center', gap:6,
                         borderWidth:1, borderColor:showSort?BRAND.canary:'transparent' }}>
                <Ionicons name="funnel-outline" size={16} color={showSort?BRAND.canary:'rgba(255,255,255,0.8)'} />
                <Text style={{ color:showSort?BRAND.canary:'rgba(255,255,255,0.8)', fontSize:13, fontWeight:'700' }}>Sort</Text>
              </TouchableOpacity>
            </View>

            {/* sort dropdown */}
            {showSort && (
              <View style={{ backgroundColor:'rgba(0,0,0,0.35)', borderRadius:12, marginTop:10, overflow:'hidden' }}>
                {IDEA_SORT_OPTS.map(opt=>(
                  <TouchableOpacity key={opt.id} onPress={()=>{ setSortBy(opt.id); setShowSort(false); setPage(1); }}
                    style={{ paddingHorizontal:16, paddingVertical:12, flexDirection:'row',
                             justifyContent:'space-between', alignItems:'center',
                             borderBottomWidth:1, borderBottomColor:'rgba(255,255,255,0.08)' }}>
                    <Text style={{ color:sortBy===opt.id?BRAND.canary:'rgba(255,255,255,0.85)',
                                   fontSize:13, fontWeight:sortBy===opt.id?'700':'500' }}>{opt.label}</Text>
                    {sortBy===opt.id && <Ionicons name="checkmark" size={16} color={BRAND.canary} />}
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </LinearGradient>

          {/* STATS ROW */}
          {stats && (
            <View style={{ flexDirection:'row', backgroundColor:'#fff', paddingVertical:13,
                           paddingHorizontal:12, borderBottomWidth:1, borderBottomColor:BRAND.line }}>
              {[
                { label:'Total',      val:stats.total,                    col:BRAND.dark,    filt:'all'          },
                { label:'Approved',   val:stats.byStatus?.approved??0,    col:'#4CAF50',     filt:'approved'     },
                { label:'Pending',    val:stats.byStatus?.pending??0,     col:'#FFC107',     filt:'pending'      },
                { label:'In Review',  val:stats.byStatus?.under_review??0,col:'#2196F3',     filt:'under_review' },
                { label:'Rejected',   val:stats.byStatus?.rejected??0,    col:'#F44336',     filt:'rejected'     },
              ].map((s,i)=>(
                <TouchableOpacity key={i} onPress={()=>changeFilter(s.filt)}
                  style={{ flex:1, alignItems:'center', borderRightWidth:i<4?1:0, borderRightColor:BRAND.line,
                           opacity: filter===s.filt ? 1 : 0.75 }}>
                  <Text style={{ fontSize:20, fontWeight:'800', color:s.col }}>{s.val??'—'}</Text>
                  <Text style={{ fontSize:10, color:filter===s.filt?s.col:BRAND.sub, marginTop:2, textAlign:'center', fontWeight:filter===s.filt?'700':'400' }}>{s.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* FILTER CHIPS */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false}
            style={{ maxHeight:52, backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:BRAND.line }}
            contentContainerStyle={{ paddingHorizontal:16, paddingVertical:9, gap:8 }}>
            {IDEA_FILTERS.map(f=>(
              <TouchableOpacity key={f.value} onPress={()=>changeFilter(f.value)}
                style={{ paddingHorizontal:16, paddingVertical:7, borderRadius:20, borderWidth:1.5,
                  borderColor:filter===f.value?f.color:BRAND.line,
                  backgroundColor:filter===f.value?f.color+'18':'#fff' }}>
                <Text style={{ fontSize:13, fontWeight:filter===f.value?'700':'500',
                  color:filter===f.value?f.color:BRAND.sub }}>{f.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* LIST */}
          {loading ? (
            <View style={{ flex:1, alignItems:'center', justifyContent:'center', gap:12 }}>
              <ActivityIndicator size="large" color="#9c27b0" />
              <Text style={{ color:BRAND.sub, fontSize:14 }}>Loading ideas…</Text>
            </View>
          ) : (
            <FlatList
              data={paged}
              keyExtractor={item=>String(item.id)}
              contentContainerStyle={{ padding:16, paddingBottom:110 }}
              showsVerticalScrollIndicator={false}
              refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#9c27b0']} tintColor="#9c27b0" />}
              renderItem={renderCard}
              ListEmptyComponent={
                <View style={{ alignItems:'center', padding:48 }}>
                  <Ionicons name="bulb-outline" size={60} color={BRAND.line} />
                  <Text style={{ fontSize:16, color:BRAND.sub, marginTop:14, fontWeight:'700' }}>
                    {search ? 'No ideas match your search' : filter!=='all' ? `No ${filter} ideas yet` : 'No innovation ideas yet'}
                  </Text>
                  <Text style={{ fontSize:13, color:BRAND.sub, marginTop:6, textAlign:'center' }}>
                    <Text>{search ? 'Try different keywords or clear the search' : 'Be the first to submit an idea!'}</Text>
                  </Text>
                  {!search && (
                    <TouchableOpacity onPress={()=>{ onClose(); setTimeout(()=>onSubmitNew?.(),350); }}
                      style={{ marginTop:20, borderRadius:14, overflow:'hidden' }}>
                      <LinearGradient colors={['#e91e63','#c2185b']}
                        style={{ paddingHorizontal:26, paddingVertical:13, flexDirection:'row', alignItems:'center', gap:8 }}>
                        <Ionicons name="send-outline" size={16} color="#fff" />
                        <Text style={{ color:'#fff', fontWeight:'700', fontSize:14 }}>Submit Your First Idea</Text>
                      </LinearGradient>
                    </TouchableOpacity>
                  )}
                </View>
              }
              ListFooterComponent={hasMore ? (
                <TouchableOpacity onPress={()=>setPage(p=>p+1)}
                  style={{ paddingVertical:14, borderRadius:12, borderWidth:1, borderColor:'#9c27b0'+'40',
                           alignItems:'center', marginTop:4, flexDirection:'row', justifyContent:'center', gap:8 }}>
                  <Ionicons name="chevron-down" size={16} color="#9c27b0" />
                  <Text style={{ color:'#9c27b0', fontWeight:'700' }}>
                    Load More ({filtered.length - paged.length} remaining)
                  </Text>
                </TouchableOpacity>
              ) : null}
            />
          )}

          {/* FAB */}
          <TouchableOpacity onPress={()=>{ onClose(); setTimeout(()=>onSubmitNew?.(),350); }}
            style={{ position:'absolute', bottom:28, right:24, width:56, height:56, borderRadius:28,
                     backgroundColor:'#e91e63', alignItems:'center', justifyContent:'center',
                     shadowColor:'#e91e63', shadowOffset:{width:0,height:4}, shadowOpacity:0.45, shadowRadius:8, elevation:8 }}>
            <Ionicons name="add" size={28} color="#fff" />
          </TouchableOpacity>

        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container:         { flex: 1, backgroundColor: BRAND.bg },
  loadingContainer:  { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: BRAND.bg },
  loadingText:       { fontSize: 22, fontWeight: '800', color: BRAND.text, marginTop: 16 },
  loadingSubtext:    { fontSize: 14, color: BRAND.sub, marginTop: 6 },

  // Header
  header:            { zIndex: 10 },
  headerGradient:    { paddingTop: Platform.OS === 'ios' ? 8 : 16, paddingBottom: 12, paddingHorizontal: 16 },
  headerTop:         { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  headerTitle:       { color: '#fff', fontSize: 20, fontWeight: '800' },
  headerStatusRow:   { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 2 },
  headerSubtitle:    { color: 'rgba(255,255,255,0.75)', fontSize: 11, fontWeight: '600' },
  headerActions:     { flexDirection: 'row', gap: 2 },
  headerIconBtn:     { padding: 7, borderRadius: 8, position: 'relative' },
  newsBadge:         { position: 'absolute', top: 2, right: 2, width: 14, height: 14, borderRadius: 7, backgroundColor: BRAND.canary, alignItems: 'center', justifyContent: 'center' },
  newsBadgeText:     { color: '#fff', fontSize: 8, fontWeight: '900' },
  spotlightWrap:     { borderRadius: 12, padding: 12, flexDirection: 'row', alignItems: 'center', marginTop: 4 },
  spotlightTitle:    { color: '#fff', fontWeight: '700', fontSize: 13 },
  spotlightText:     { color: 'rgba(255,255,255,0.8)', fontSize: 12, marginTop: 2 },
  spotlightLink:     { color: BRAND.canary, fontWeight: '700' },

  errorBanner:       { backgroundColor: BRAND.danger, flexDirection: 'row', alignItems: 'center', padding: 10, paddingHorizontal: 16, gap: 8 },
  errorText:         { color: '#fff', flex: 1, fontSize: 13 },
  retryText:         { color: '#fff', fontWeight: '700', fontSize: 13 },

  filtersPanel:      { backgroundColor: BRAND.white, padding: 12, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  filterRow:         { flexDirection: 'row', gap: 6 },
  filterChip:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 18, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white, gap: 4 },
  filterChipText:    { fontSize: 11, fontWeight: '600', color: BRAND.sub },

  // Search
  searchWrap:        { flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.white, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10, marginBottom: 14, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1, gap: 8 },
  searchInput:       { flex: 1, fontSize: 14, color: BRAND.text },

  // KPIs
  kpiGrid:           { flexDirection: 'row', paddingHorizontal: 16, gap: 8, marginBottom: 14 },
  kpiCard:           { flex: 1, backgroundColor: BRAND.white, borderRadius: 14, padding: 12, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  kpiIcon:           { width: 34, height: 34, borderRadius: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 6 },
  kpiValue:          { fontSize: 15, fontWeight: '800', color: BRAND.text },
  kpiLabel:          { fontSize: 10, color: BRAND.sub, textAlign: 'center', marginTop: 1 },
  kpiTrendContainer: { flexDirection: 'row', alignItems: 'center', gap: 2, marginTop: 3 },
  kpiTrend:          { fontSize: 9, fontWeight: '700' },

  // View mode buttons
  viewModeBtn:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white, gap: 4 },
  viewModeBtnActive: { backgroundColor: BRAND.blue, borderColor: BRAND.blue },
  viewModeBtnText:   { fontSize: 12, fontWeight: '600', color: BRAND.sub },

  // Stage filter
  stageScroll:         { marginHorizontal: 0, marginBottom: 14 },
  stageScrollContent:  { paddingHorizontal: 16, gap: 6 },
  stageChip:           { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 18, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white, gap: 5 },
  stageDot:            { width: 6, height: 6, borderRadius: 3 },
  stageChipText:       { fontSize: 11, fontWeight: '600', color: BRAND.sub },

  // Innovation cards
  card:              { backgroundColor: BRAND.white, borderRadius: 16, padding: 14, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  cardTop:           { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  typeIcon:          { width: 32, height: 32, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  cardTitle:         { flex: 1, fontSize: 14, fontWeight: '700', color: BRAND.text },
  mlBadge:           { fontSize: 11, fontWeight: '600' },
  impactBadge:       { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 8 },
  impactText:        { fontSize: 10, fontWeight: '800' },
  metaRow:           { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginBottom: 8 },
  metaPill:          { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 8, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.bg, gap: 3 },
  metaText:          { fontSize: 11, color: BRAND.sub },
  cardSummary:       { fontSize: 12, color: BRAND.sub, lineHeight: 17, marginBottom: 10 },
  cardMiddle:        { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  stageBadge:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, borderWidth: 1, gap: 3 },
  stageText:         { fontSize: 10, fontWeight: '700' },
  progressContainer: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6 },
  progressBar:       { flex: 1, height: 5, borderRadius: 3, backgroundColor: BRAND.line },
  progressFill:      { height: 5, borderRadius: 3 },
  progressText:      { fontSize: 11, color: BRAND.sub, fontWeight: '600', minWidth: 28 },
  tagsRow:           { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginBottom: 10 },
  tag:               { backgroundColor: BRAND.blue + '10', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 6 },
  tagText:           { fontSize: 10, color: BRAND.blue, fontWeight: '600' },
  moreTags:          { fontSize: 11, color: BRAND.sub, fontWeight: '600', alignSelf: 'center' },
  cardBottom:        { flexDirection: 'row', alignItems: 'center', gap: 4, paddingTop: 8, borderTopWidth: 1, borderTopColor: BRAND.line },
  cardActionBtn:     { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 7, paddingVertical: 5, borderRadius: 8, backgroundColor: BRAND.bg, gap: 3 },
  cardActionText:    { fontSize: 11, fontWeight: '600' },

  // Kanban
  kanbanColumn:      { width: 170, marginRight: 10 },
  kanbanHeader:      { flexDirection: 'row', alignItems: 'center', padding: 10, borderRadius: 12, borderTopWidth: 3, borderColor: BRAND.blue, backgroundColor: BRAND.white, marginBottom: 8, gap: 5 },
  kanbanHeaderText:  { flex: 1, fontSize: 12, fontWeight: '700' },
  kanbanCount:       { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  kanbanCountText:   { color: '#fff', fontSize: 11, fontWeight: '800' },
  kanbanEmpty:       { alignItems: 'center', padding: 20 },
  kanbanEmptyText:   { fontSize: 11, color: BRAND.sub, marginTop: 4 },
  kanbanCard:        { backgroundColor: BRAND.white, borderRadius: 10, padding: 10, marginBottom: 8, borderLeftWidth: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 },
  kanbanCardTitle:   { fontSize: 12, fontWeight: '600', color: BRAND.text, lineHeight: 17 },
  kanbanCardRoi:     { fontSize: 11, fontWeight: '700', color: BRAND.success },

  // Analytics
  analyticsGrid:         { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  analyticsSummaryCard:  { flex: 1, minWidth: (width - 48) / 2, backgroundColor: BRAND.white, borderRadius: 12, padding: 14, borderTopWidth: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  analyticsSummaryValue: { fontSize: 20, fontWeight: '800', marginVertical: 4 },
  analyticsSummaryLabel: { fontSize: 11, color: BRAND.sub },
  chartCard:             { backgroundColor: BRAND.white, borderRadius: 14, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  chartTitle:            { fontSize: 14, fontWeight: '700', color: BRAND.text, marginBottom: 14 },
  analyticsRankRow:      { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  analyticsRank:         { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  analyticsRankText:     { color: '#fff', fontSize: 11, fontWeight: '900' },
  analyticsRankTitle:    { flex: 1, fontSize: 12, fontWeight: '600', color: BRAND.text },
  analyticsRankValue:    { fontSize: 12, fontWeight: '700' },
  exportCSVBtn:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: BRAND.blue + '10', borderRadius: 12, paddingVertical: 14, borderWidth: 1.5, borderColor: BRAND.blue, marginTop: 8 },
  exportCSVText:         { fontSize: 14, color: BRAND.blue, fontWeight: '700' },

  // Section
  section:           { marginBottom: 20 },
  sectionHeader:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle:      { fontSize: 17, fontWeight: '800', color: BRAND.text },
  sectionActions:    { flexDirection: 'row', gap: 8 },
  smallBtn:          { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.white },
  smallBtnText:      { fontSize: 12, fontWeight: '600', color: BRAND.blue },

  emptyState:        { alignItems: 'center', padding: 40 },
  emptyTitle:        { fontSize: 18, fontWeight: '700', color: BRAND.text, marginTop: 14 },
  emptyText:         { fontSize: 14, color: BRAND.sub, textAlign: 'center', marginTop: 6, marginBottom: 20 },
  emptyButton:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12, gap: 8 },
  emptyButtonText:   { color: '#fff', fontWeight: '700', fontSize: 14 },

  // Business plan cards
  businessPlanGradient: { borderRadius: 14, padding: 16 },
  businessPlanContent:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  businessPlanCTATitle: { color: '#fff', fontSize: 15, fontWeight: '800' },
  businessPlanCTAText:  { color: 'rgba(255,255,255,0.8)', fontSize: 12, marginTop: 2 },
  businessPlanCTAButton:{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, gap: 6 },
  businessPlanCTAButtonText: { fontWeight: '700', fontSize: 13 },
  businessPlanCard:     { backgroundColor: BRAND.white, borderRadius: 12, padding: 14, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  businessPlanCardHeader: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  businessPlanIcon:     { width: 36, height: 36, borderRadius: 10, backgroundColor: BRAND.purple + '15', justifyContent: 'center', alignItems: 'center' },
  businessPlanTitle:    { fontSize: 13, fontWeight: '700', color: BRAND.text },
  businessPlanDate:     { fontSize: 11, color: BRAND.sub, marginTop: 2 },
  businessPlanStatus:   { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 },
  businessPlanStatusText:{ fontSize: 11, fontWeight: '700' },
  businessPlanMeta:     { flexDirection: 'row', marginTop: 8, gap: 10 },
  businessPlanMetaItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  businessPlanMetaText: { fontSize: 11, color: BRAND.sub },

  // Roadmap
  roadmapCard:        { width: 150, backgroundColor: BRAND.white, borderRadius: 12, padding: 12, marginRight: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  roadmapQuarter:     { fontSize: 10, fontWeight: '700', color: BRAND.sub, marginBottom: 4 },
  roadmapTitle:       { fontSize: 12, fontWeight: '600', color: BRAND.text, lineHeight: 17 },
  roadmapStatus:      { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, alignSelf: 'flex-start' },
  roadmapStatusText:  { fontSize: 10, fontWeight: '700' },

  // AI recommendations
  aiRecommendations: { paddingHorizontal: 16, marginBottom: 14 },
  aiHeader:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  aiTitleContainer:  { flexDirection: 'row', alignItems: 'center', gap: 6 },
  aiTitle:           { fontSize: 15, fontWeight: '700' },
  aiCard:            { width: 210, backgroundColor: BRAND.white, borderRadius: 14, padding: 14, marginRight: 10, borderWidth: 1.5, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  aiCardHeader:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  aiImpactBadge:     { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  aiImpactText:      { fontSize: 10, fontWeight: '700' },
  aiConfidence:      { fontSize: 11, fontWeight: '600', color: BRAND.sub },
  aiCardTitle:       { fontSize: 13, fontWeight: '700', color: BRAND.text, marginBottom: 5, lineHeight: 17 },
  aiCardDesc:        { fontSize: 12, color: BRAND.sub, lineHeight: 17, marginBottom: 10 },
  aiCardFooter:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  aiTimeline:        { flexDirection: 'row', alignItems: 'center', gap: 3 },
  aiRoiText:         { fontSize: 12, fontWeight: '700', color: BRAND.success },
  aiImplementBtn:    { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 8 },
  aiImplementText:   { fontSize: 12, fontWeight: '700' },

  // Quick Actions
  quickActions:      { flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingVertical: 12, borderTopWidth: 1, borderTopColor: BRAND.line, marginBottom: 8 },
  quickActionBtn:    { alignItems: 'center', gap: 4, paddingHorizontal: 6, minWidth: 58 },
  quickActionText:   { fontSize: 10, fontWeight: '700', color: BRAND.sub },

  // Modals
  modalOverlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalCard:         { backgroundColor: BRAND.white, borderTopLeftRadius: 24, borderTopRightRadius: 24 },
  modalHeader:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', padding: 20, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  modalTitle:        { fontSize: 18, fontWeight: '800', color: BRAND.text },
  modalSubtitle:     { fontSize: 12, color: BRAND.sub, marginTop: 2 },
  modalScroll:       { paddingHorizontal: 20, paddingTop: 16 },

  // Detail modal
  detailContainer:   { flex: 1, backgroundColor: BRAND.white, marginTop: 60, borderTopLeftRadius: 24, borderTopRightRadius: 24, overflow: 'hidden' },
  detailHeader:      { paddingTop: 14, paddingHorizontal: 16, paddingBottom: 12 },
  detailHeaderTop:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  detailCloseButton: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.15)' },
  detailHeaderTitle: { fontSize: 16, fontWeight: '700', color: '#fff' },
  detailHeaderActions:{ flexDirection: 'row', gap: 8 },
  detailHeaderButton:{ padding: 6 },
  detailContent:     { flex: 1, padding: 20 },
  detailTitleSection:{ marginBottom: 18 },
  detailTitle:       { fontSize: 22, fontWeight: '800', color: BRAND.text, marginBottom: 10, lineHeight: 29 },
  detailSubtitle:    { fontSize: 14, fontWeight: '500', marginBottom: 8 },
  detailBadgeContainer:{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  detailImpactBadge: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 18 },
  detailImpactText:  { fontSize: 11, fontWeight: '800' },
  detailStageBadge:  { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 18, gap: 4 },
  detailStageText:   { fontSize: 11, fontWeight: '800' },
  detailProviderBadge:{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 18, gap: 4 },
  detailProviderText:{ fontSize: 11, fontWeight: '700' },
  detailRoiCard:     { padding: 18, borderRadius: 16, marginBottom: 20 },
  detailRoiLabel:    { color: 'rgba(255,255,255,0.85)', fontSize: 13 },
  detailRoiAmount:   { color: '#fff', fontSize: 32, fontWeight: '900', marginVertical: 3 },
  detailRoiSubtext:  { color: 'rgba(255,255,255,0.8)', fontSize: 12 },
  detailActionsRow:  { flexDirection: 'row', gap: 8, marginBottom: 18, flexWrap: 'wrap' },
  detailActionPill:  { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: BRAND.bg, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 10, borderWidth: 1, borderColor: BRAND.line },
  detailActionPillText:{ fontSize: 12, fontWeight: '600' },
  detailSection:     { marginBottom: 20 },
  detailSectionTitle:{ fontSize: 16, fontWeight: '800', color: BRAND.text, marginBottom: 10 },
  detailDescription: { fontSize: 15, lineHeight: 22, color: BRAND.sub },
  detailMetricsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 20 },
  detailMetricCard:  { flex: 1, minWidth: (width - 64) / 2, backgroundColor: BRAND.bg, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: BRAND.line },
  detailMetricLabel: { fontSize: 11, color: BRAND.sub, marginTop: 6, marginBottom: 3 },
  detailMetricValue: { fontSize: 15, fontWeight: '700', color: BRAND.text },
  detailTagsContainer:{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  detailTag:         { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 18, borderWidth: 1 },
  detailTagText:     { fontSize: 11, fontWeight: '600' },
  detailMetricsText: { fontSize: 13, lineHeight: 20, color: BRAND.sub, backgroundColor: BRAND.bg, padding: 14, borderRadius: 10 },
  detailRisksText:   { fontSize: 13, lineHeight: 20, color: BRAND.danger, backgroundColor: BRAND.danger + '08', padding: 14, borderRadius: 10 },
  detailExportContainer:{ marginTop: 16, marginBottom: 10, padding: 16, backgroundColor: BRAND.bg, borderRadius: 14, borderWidth: 1, borderColor: BRAND.line },
  detailExportTitle: { fontSize: 15, fontWeight: '700', color: BRAND.text, marginBottom: 12 },
  detailExportButton:{ borderRadius: 12, overflow: 'hidden' },
  detailExportGradient:{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, gap: 7 },
  detailExportButtonText:{ color: '#fff', fontSize: 13, fontWeight: '700' },
  detailExportRow:   { flexDirection: 'row', gap: 8, marginTop: 8 },

  // Forms
  fieldLabel:        { fontSize: 13, fontWeight: '700', color: BRAND.sub, marginBottom: 6, marginTop: 2 },
  input:             { backgroundColor: BRAND.bg, borderRadius: 12, borderWidth: 1.5, borderColor: BRAND.line, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: BRAND.text, marginBottom: 14 },
  textarea:          { minHeight: 70, textAlignVertical: 'top', paddingTop: 12 },
  typeChip:          { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white, marginRight: 8, gap: 5 },
  typeChipText:      { fontSize: 12, fontWeight: '600', color: BRAND.sub },
  stageOption:       { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white, marginRight: 8 },
  stageOptionText:   { fontSize: 12, fontWeight: '600', color: BRAND.sub },
  pickerRow:         { flexDirection: 'row', gap: 8, marginBottom: 14 },
  impactOption:      { flex: 1, paddingVertical: 10, borderRadius: 10, borderWidth: 1.5, borderColor: BRAND.line, alignItems: 'center' },
  impactOptionText:  { fontSize: 12, fontWeight: '600', color: BRAND.sub },
  assignPicker:      { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  assignOption:      { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white },
  assignOptionText:  { fontSize: 12, color: BRAND.sub },
  submitButton:      { borderRadius: 14, overflow: 'hidden', marginTop: 8, marginBottom: 24 },
  submitGradient:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, gap: 10 },
  submitText:        { color: BRAND.white, fontSize: 16, fontWeight: '800' },

  // AI Generator
  aiGeneratorContainer:{ alignItems: 'center', paddingVertical: 8, width: '100%' },
  providerBadge:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, gap: 5 },
  providerBadgeText:    { fontSize: 12, fontWeight: '800' },
  aiGeneratorTitle:     { fontSize: 20, fontWeight: '800', color: BRAND.text, marginBottom: 8, textAlign: 'center' },
  aiGeneratorDesc:      { fontSize: 13, color: BRAND.sub, textAlign: 'center', marginBottom: 20, lineHeight: 20 },
  generatorControls:    { width: '100%' },
  industrySelector:     { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginBottom: 14 },
  industryChip:         { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 18, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white },
  industryChipText:     { fontSize: 12, color: BRAND.sub },
  countSelector:        { flexDirection: 'row', gap: 10, marginBottom: 20 },
  countChip:            { width: 56, paddingVertical: 12, borderRadius: 12, borderWidth: 1.5, borderColor: BRAND.line, backgroundColor: BRAND.white, alignItems: 'center' },
  countChipText:        { fontSize: 16, fontWeight: '600', color: BRAND.sub },
  generateButton:       { borderRadius: 14, overflow: 'hidden', marginBottom: 20 },
  generateButtonDisabled:{ opacity: 0.5 },
  generateGradient:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, gap: 10 },
  generateButtonText:   { color: BRAND.white, fontSize: 16, fontWeight: '800' },
  generatedResults:     { marginTop: 10, width: '100%' },
  generatedResultsHeader:{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  generatedResultsTitle:{ fontSize: 15, fontWeight: '700', color: BRAND.success },
  generatedIdeaCard:    { backgroundColor: BRAND.bg, borderRadius: 12, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: BRAND.line },
  generatedIdeaHeader:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 },
  generatedIdeaTitle:   { flex: 1, fontSize: 14, fontWeight: '700', color: BRAND.text },
  confidenceBadge:      { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8 },
  confidenceText:       { fontSize: 11, fontWeight: '800' },
  generatedIdeaDesc:    { fontSize: 12, color: BRAND.sub, lineHeight: 17, marginBottom: 10 },
  generatedIdeaTags:    { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginBottom: 10 },
  generatedIdeaTag:     { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 7, borderWidth: 1 },
  generatedIdeaTagText: { fontSize: 11, fontWeight: '600' },
  generatedIdeaFooter:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  generatedIdeaMeta:    { flexDirection: 'row', alignItems: 'center', gap: 6 },
  generatedIdeaRoi:     { fontSize: 13, fontWeight: '700', color: BRAND.success },
  generatedIdeaTimeline:{ fontSize: 12, color: BRAND.sub },
  generatedIdeaActions: { flexDirection: 'row', gap: 7 },
  addIdeaButton:        { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8 },
  addIdeaButtonText:    { color: '#fff', fontSize: 12, fontWeight: '700' },
  rejectIdeaButton:     { padding: 7 },

  // AI Chat
  chatModalOverlay:  { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  chatModal:         { backgroundColor: BRAND.white, borderTopLeftRadius: 24, borderTopRightRadius: 24, height: height * 0.82 },
  chatHeader:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  chatHeaderLeft:    { flexDirection: 'row', alignItems: 'center', gap: 10 },
  chatTitle:         { fontSize: 16, fontWeight: '800', color: BRAND.text },
  chatSubtitle:      { fontSize: 11, color: BRAND.sub, marginTop: 1 },
  chatMessages:      { flex: 1, padding: 16 },
  welcomeMessage:    { alignItems: 'center', padding: 28 },
  welcomeTitle:      { fontSize: 17, fontWeight: '800', color: BRAND.text, marginTop: 14, marginBottom: 8 },
  welcomeText:       { fontSize: 13, color: BRAND.sub, lineHeight: 22, textAlign: 'center' },
  messageRow:        { flexDirection: 'row', marginBottom: 14 },
  userMessageRow:    { justifyContent: 'flex-end' },
  aiMessageRow:      { justifyContent: 'flex-start' },
  aiAvatar:          { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', marginRight: 8, alignSelf: 'flex-end' },
  messageBubble:     { maxWidth: '80%', padding: 12, borderRadius: 16 },
  userBubble:        { backgroundColor: BRAND.blue, borderBottomRightRadius: 4 },
  aiBubble:          { backgroundColor: BRAND.bg, borderBottomLeftRadius: 4, flexDirection: 'row', alignItems: 'center' },
  userText:          { color: '#fff', fontSize: 14, lineHeight: 20 },
  aiText:            { color: BRAND.text, fontSize: 14, lineHeight: 20, flex: 1 },
  chatInputContainer:{ flexDirection: 'row', padding: 14, borderTopWidth: 1, borderTopColor: BRAND.line, backgroundColor: BRAND.white, gap: 10 },
  chatInput:         { flex: 1, backgroundColor: BRAND.bg, borderRadius: 20, paddingHorizontal: 16, paddingVertical: 10, color: BRAND.text, fontSize: 14 },
  chatSendButton:    { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-end' },
  chatSendButtonDisabled:{ opacity: 0.4 },

  // Business plan form
  aiGenerateButton:  { borderRadius: 14, overflow: 'hidden', marginBottom: 18 },
  aiGenerateGradient:{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 14, gap: 8 },
  aiGenerateText:    { color: '#fff', fontSize: 14, fontWeight: '800' },
  modalActions:      { flexDirection: 'row', gap: 10, marginBottom: 20 },
  modalButton:       { flex: 1, borderRadius: 12, overflow: 'hidden' },
  cancelButton:      { backgroundColor: BRAND.bg, borderWidth: 1, borderColor: BRAND.line, alignItems: 'center', justifyContent: 'center', paddingVertical: 16, borderRadius: 12 },
  cancelButtonText:  { color: BRAND.sub, fontWeight: '600' },
  saveButton:        { overflow: 'hidden' },
  saveGradient:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, gap: 8 },
  saveButtonText:    { color: '#fff', fontWeight: '800', fontSize: 15 },
  statusDot:         { width: 7, height: 7, borderRadius: 4 },
});